  import React, { useState, useEffect } from "react"
  import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    Card,
    Typography,
    IconButton,
    styled,
    Tooltip,
    CircularProgress,
    useTheme,
    Collapse,
    Grid,
    Chip,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    alpha,
    Checkbox,
    Alert,
    AlertTitle,
    Menu,
    ListItemIcon,
    ListItemText,
    ListSubheader,
    FormControlLabel,
    Stack,
    Radio,
    RadioGroup,
    Skeleton,
  } from "@mui/material"
  import { Pagination } from "@mui/material"
  import {
    Add as AddIcon,
    KeyboardArrowDown as KeyboardArrowDownIcon,
    KeyboardArrowRight as KeyboardArrowRightIcon,
    Edit,
    Visibility,
    LocalShipping,
    Payment,
    Person,
    LocationOn,
    ShoppingCart,
    Update as UpdateIcon,
    CheckCircle,
    Schedule,
    LocalShippingOutlined,
    Cancel,
    Refresh,
    FileDownload,
    SelectAll,
    Receipt as ReceiptIcon,
    Download as DownloadIcon,
    Close as CloseIcon,
    PictureAsPdf as PdfIcon,
    LocalShipping as DeliveryIcon,
    Assignment as ChallanIcon,
    Email as EmailIcon,
    Send as SendIcon,
    CameraAlt as CameraAltIcon,
    UploadFile as UploadFileIcon,
  } from "@mui/icons-material"
  import { useNavigate } from "react-router-dom"
  import { markAppOrderExpectReturn } from "./appOrderListCache"

  import apiService from "Network/apiService"
  import { API_ENDPOINTS } from "Network/apiEndpoints"
  import {
    submitNewPaidPaymentStatus,
    buildNewPaidPaymentFormData,
    extractPaymentApiError,
    getOrderPaymentDetailsFromOrder,
    validateNewPaidPaymentRequest,
    validatePaidPaymentFormDetails,
    isZeroRemainingPayment,
    PAYMENT_UPDATE_LOCKED_ORDER_STATUSES,
    getOrderPaymentStatusValue,
    normalizeStatusKey,
    getOrderStatusKey,
    isPaymentUpdateDisabledForOrder,
    getPaymentUpdateDisabledReason,
    DEFAULT_PAYMENT_UPDATE_MODAL,
    getPaidPaymentStepState,
    getAdvancePaymentEntries,
  } from "Network/paymentStatusService"
  import {
    fetchAdvancePaymentMethods,
    buildPaymentMethodsById,
  } from "Network/advancePaymentMethodService"
  import { useToast } from "OtherReusableFunctionality/ToastContext"

  const getOrderChallan = (order) =>
    order?.delivery_challan || order?.deliveryChallan || order?.challan;

  const isOrderByTruck = (order) => {
    if (!order) return false;
    const challan = getOrderChallan(order);
    const orderBy = String(order?.order_by || challan?.order_by || '')
      .trim()
      .toLowerCase();
    return orderBy === 'truck' || orderBy === 'by truck';
  };

  /**
   * Title-case an address: capitalize first letter and first letter after every space.
   * If input is falsy, returns it as-is.
   */
  const titleCaseAddress = (value) => {
    if (value === null || value === undefined) return value;
    const str = String(value).trim();
    if (!str) return str;
    return str
      .split(" ")
      .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : ""))
      .join(" ");
  };

  // API always requires vehicle_type. For Truck use the selected type;
  // for Post/Courier/International (field hidden) fall back to order_by.
  const resolveVehicleTypeForPayload = (orderBy, vehicleType) => {
    const selectedType = String(vehicleType || '').trim();
    if (selectedType) return selectedType;
    return String(orderBy || '').trim();
  };

  const isTruthyFlag = (value) => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    const normalized = String(value ?? '').trim().toLowerCase();
    return ['true', '1', 'yes', 'on'].includes(normalized);
  };

  const getApiErrorMessage = (error, fallback = 'Something went wrong') => {
    if (!error) return fallback;
    if (typeof error === 'string') return error;
    if (Array.isArray(error?.errors) && error.errors.length) {
      return error.errors.join(' ');
    }
    if (error?.errors && typeof error.errors === 'object') {
      const values = Object.values(error.errors).flat().filter(Boolean);
      if (values.length) return values.join(' ');
    }
    return error?.message || error?.detail || error?.error || fallback;
  };

  const DEFAULT_DISPATCH_LOGISTICS = {
    meter_not_working: false,
    google_maps_km: '',
    diesel_receipt_number: '',
    diesel_amount: '',
    crates_dispatched: '0',
    extra_packages: '0',
    sample_packages_quantity: '0',
  };

  const DEFAULT_DELIVERED_LOGISTICS = {
    final_km: '',
    km_excess_reason: '',
    crates_returned: '',
    crates_short_reason: '',
    crates_returned_person: '',
    extra_plants: '0',
    plants_returned: '0',
    plants_returned_reason: '',
    packages_returned: '0',
    packages_returned_reason: '',
    package_returned: '0',
  };

  const getOrderCompanyName = (order) =>
    String(
      order?.company_name ||
        order?.company?.name ||
        order?.company_details?.name ||
        order?.items?.[0]?.company_name ||
        order?.items?.[0]?.company?.name ||
        ''
    ).trim();

  /** Branch logistics UI/payload by company_name (Seeds / Fertilizers / other). */
  const getOrderCompanyKind = (orderOrName) => {
    const name = String(
      typeof orderOrName === 'string' ? orderOrName : getOrderCompanyName(orderOrName)
    )
      .trim()
      .toLowerCase();
    if (!name) return 'other';
    if (name.includes('fertilizer')) return 'fertilizers';
    if (name.includes('seeds') || name.includes('ishved crop science')) return 'seeds';
    return 'other';
  };

  const parseNonNegInt = (raw, fallback = 0) => {
    const text = String(raw ?? '').trim();
    if (text === '') return fallback;
    const num = Number(text);
    return Number.isFinite(num) && Number.isInteger(num) && num >= 0 ? num : NaN;
  };

  const getChallanLogisticsDefaults = (challan = {}) => ({
    meter_not_working: isTruthyFlag(challan?.meter_not_working),
    google_maps_km:
      challan?.google_maps_km !== null && challan?.google_maps_km !== undefined
        ? String(challan.google_maps_km)
        : '',
    diesel_receipt_number: challan?.diesel_receipt_number || '',
    diesel_amount:
      challan?.diesel_amount !== null && challan?.diesel_amount !== undefined
        ? String(challan.diesel_amount)
        : '',
    crates_dispatched:
      challan?.crates_dispatched !== null && challan?.crates_dispatched !== undefined
        ? String(challan.crates_dispatched)
        : '0',
    final_km:
      challan?.final_km !== null && challan?.final_km !== undefined
        ? String(challan.final_km)
        : '',
    km_excess_reason: challan?.km_excess_reason || '',
    crates_returned:
      challan?.crates_returned !== null && challan?.crates_returned !== undefined
        ? String(challan.crates_returned)
        : '',
    crates_short_reason: challan?.crates_short_reason || '',
    crates_returned_person: challan?.crates_returned_person || '',
    extra_plants:
      challan?.extra_plants !== null && challan?.extra_plants !== undefined
        ? String(challan.extra_plants)
        : challan?.extra_plant !== null && challan?.extra_plant !== undefined
          ? String(challan.extra_plant)
          : '0',
    plants_returned:
      challan?.plants_returned !== null && challan?.plants_returned !== undefined
        ? String(challan.plants_returned)
        : '0',
    plants_returned_reason: challan?.plants_returned_reason || '',
    extra_packages:
      challan?.extra_packages !== null && challan?.extra_packages !== undefined
        ? String(challan.extra_packages)
        : '0',
    sample_packages_quantity:
      challan?.sample_packages_quantity !== null &&
      challan?.sample_packages_quantity !== undefined
        ? String(challan.sample_packages_quantity)
        : '0',
    packages_returned:
      challan?.packages_returned !== null && challan?.packages_returned !== undefined
        ? String(challan.packages_returned)
        : '0',
    packages_returned_reason: challan?.packages_returned_reason || '',
    package_returned:
      challan?.package_returned !== null && challan?.package_returned !== undefined
        ? String(challan.package_returned)
        : '0',
  });

  const buildDispatchLogisticsPayload = (form = {}, order = form.order) => {
    const meterNotWorking = isTruthyFlag(form.meter_not_working);
    const kind = getOrderCompanyKind(order);
    const payload = {
      meter_not_working: meterNotWorking,
      diesel_receipt_number: String(form.diesel_receipt_number || '').trim(),
      diesel_amount: Number(form.diesel_amount),
      ...(meterNotWorking ? { google_maps_km: Number(form.google_maps_km) } : {}),
    };

    if (kind === 'seeds') {
      const extraPackages = parseNonNegInt(form.extra_packages, 0);
      payload.extra_packages = Number.isNaN(extraPackages) ? 0 : extraPackages;
      return payload;
    }
    if (kind === 'fertilizers') {
      const sampleQty = parseNonNegInt(form.sample_packages_quantity, 0);
      payload.sample_packages_quantity = Number.isNaN(sampleQty) ? 0 : sampleQty;
      return payload;
    }

    const cratesRaw = String(form.crates_dispatched ?? '').trim();
    const crates = cratesRaw === '' ? 0 : Number(cratesRaw);
    const extraPlantsRaw = String(form.extra_plants ?? form.extra_plant ?? '').trim();
    const extraPlants = extraPlantsRaw === '' ? 0 : Number(extraPlantsRaw);
    payload.crates_dispatched = Number.isFinite(crates) && crates >= 0 ? crates : 0;
    payload.extra_plants =
      Number.isFinite(extraPlants) && extraPlants >= 0 ? extraPlants : 0;
    return payload;
  };

  const validateDispatchLogistics = (form = {}, order = form.order) => {
    const isTruckOrderBy = String(form.orderBy || '').trim().toLowerCase() === 'truck';
    if (isTruckOrderBy) {
      if (!String(form.diesel_receipt_number || '').trim()) {
        return 'Diesel receipt number is required';
      }
      if (!(form.diesel_receipt_upload instanceof File)) {
        return 'Receipt upload is required';
      }
      const dieselAmount = Number(form.diesel_amount);
      if (
        String(form.diesel_amount ?? '').trim() === '' ||
        Number.isNaN(dieselAmount) ||
        dieselAmount < 0
      ) {
        return 'Diesel amount is required and must be ≥ 0';
      }
      if (isTruthyFlag(form.meter_not_working)) {
        const mapsKm = Number(form.google_maps_km);
        if (
          String(form.google_maps_km ?? '').trim() === '' ||
          Number.isNaN(mapsKm) ||
          mapsKm <= 0
        ) {
          return 'Google Maps KM is required and must be greater than 0 when meter is not working';
        }
      }
    }

    const kind = getOrderCompanyKind(order);
    if (kind === 'seeds') {
      const extraPackages = parseNonNegInt(form.extra_packages, 0);
      if (Number.isNaN(extraPackages)) {
        return 'Extra packages must be an integer ≥ 0';
      }
      return '';
    }
    if (kind === 'fertilizers') {
      const sampleQty = parseNonNegInt(form.sample_packages_quantity, 0);
      if (Number.isNaN(sampleQty)) {
        return 'Sample packages quantity must be an integer ≥ 0';
      }
      return '';
    }

    const cratesRaw = String(form.crates_dispatched ?? '').trim();
    if (cratesRaw !== '') {
      const crates = Number(cratesRaw);
      if (!Number.isInteger(crates) || crates < 0) {
        return 'Crates dispatched must be an integer ≥ 0';
      }
    }
    return '';
  };

  const getDeliveredLogisticsContext = (form = {}, challan = {}) => {
    const meterNotWorking = isTruthyFlag(
      form.meter_not_working !== undefined && form.meter_not_working !== ''
        ? form.meter_not_working
        : challan?.meter_not_working
    );
    const googleMapsKm = Number(
      form.google_maps_km !== undefined && String(form.google_maps_km).trim() !== ''
        ? form.google_maps_km
        : challan?.google_maps_km
    );
    const cratesDispatched = Number(
      form.crates_dispatched !== undefined && String(form.crates_dispatched).trim() !== ''
        ? form.crates_dispatched
        : (challan?.crates_dispatched ?? 0)
    );
    return {
      meterNotWorking,
      googleMapsKm,
      cratesDispatched: Number.isFinite(cratesDispatched) ? cratesDispatched : 0,
    };
  };

  const validateDeliveredLogistics = (form = {}, challan = {}, order = form.order) => {
    const { meterNotWorking, googleMapsKm, cratesDispatched } =
      getDeliveredLogisticsContext(form, challan);
    const kind = getOrderCompanyKind(order);

    if (kind === 'seeds') {
      const packagesReturned = parseNonNegInt(form.packages_returned, 0);
      if (Number.isNaN(packagesReturned)) {
        return 'Packages returned must be an integer ≥ 0';
      }
      if (packagesReturned > 0 && !String(form.packages_returned_reason || '').trim()) {
        return 'Packages returned reason is required when packages returned is greater than 0';
      }
    } else if (kind === 'fertilizers') {
      const packageReturned = parseNonNegInt(form.package_returned, 0);
      if (Number.isNaN(packageReturned)) {
        return 'Package returned must be an integer ≥ 0';
      }
    } else {
      if (String(form.crates_returned ?? '').trim() === '') {
        return 'Crates returned is required';
      }
      const cratesReturned = Number(form.crates_returned);
      if (
        Number.isNaN(cratesReturned) ||
        cratesReturned < 0 ||
        !Number.isInteger(cratesReturned)
      ) {
        return 'Crates returned must be an integer ≥ 0';
      }
      if (!String(form.crates_returned_person || '').trim()) {
        return 'Crates return person is required';
      }
      if (
        cratesReturned !== cratesDispatched &&
        !String(form.crates_short_reason || '').trim()
      ) {
        return 'Crate shortage reason is required when returned crates differ from dispatched';
      }

      const plantsReturnedRaw = String(form.plants_returned ?? '').trim();
      const plantsReturned = plantsReturnedRaw === '' ? 0 : Number(plantsReturnedRaw);
      if (
        Number.isNaN(plantsReturned) ||
        plantsReturned < 0 ||
        !Number.isInteger(plantsReturned)
      ) {
        return 'Plants returned must be an integer ≥ 0';
      }
      if (plantsReturned > 0 && !String(form.plants_returned_reason || '').trim()) {
        return 'Plants returned reason is required when plants returned is greater than 0';
      }
    }

    if (meterNotWorking) {
      if (String(form.final_km ?? '').trim() === '') {
        return 'Final KM is required when meter was not working at dispatch';
      }
      const finalKm = Number(form.final_km);
      if (Number.isNaN(finalKm) || finalKm < 0) {
        return 'Final KM must be a valid number ≥ 0';
      }
      if (
        !Number.isNaN(googleMapsKm) &&
        finalKm !== googleMapsKm &&
        !String(form.km_excess_reason || '').trim()
      ) {
        return 'KM excess reason is required when final KM differs from Google Maps KM';
      }
    }
    return '';
  };

  const buildDeliveredLogisticsPayload = (form = {}, challan = {}, order = form.order) => {
    const { meterNotWorking, googleMapsKm, cratesDispatched } =
      getDeliveredLogisticsContext(form, challan);
    const kind = getOrderCompanyKind(order);
    const finalKm = Number(form.final_km);
    const payload = {};

    if (kind === 'seeds') {
      const packagesReturned = parseNonNegInt(form.packages_returned, 0);
      payload.packages_returned = Number.isNaN(packagesReturned) ? 0 : packagesReturned;
      if (payload.packages_returned > 0) {
        payload.packages_returned_reason = String(form.packages_returned_reason || '').trim();
      }
    } else if (kind === 'fertilizers') {
      const packageReturned = parseNonNegInt(form.package_returned, 0);
      payload.package_returned = Number.isNaN(packageReturned) ? 0 : packageReturned;
    } else {
      const cratesReturned = Number(form.crates_returned);
      const plantsReturnedRaw = String(form.plants_returned ?? '').trim();
      const plantsReturned = plantsReturnedRaw === '' ? 0 : Number(plantsReturnedRaw);
      payload.crates_returned = cratesReturned;
      payload.crates_returned_person = String(form.crates_returned_person || '').trim();
      payload.plants_returned =
        Number.isFinite(plantsReturned) && plantsReturned >= 0 ? plantsReturned : 0;
      if (cratesReturned !== cratesDispatched) {
        payload.crates_short_reason = String(form.crates_short_reason || '').trim();
      }
      if (plantsReturned > 0) {
        payload.plants_returned_reason = String(form.plants_returned_reason || '').trim();
      }
    }

    if (meterNotWorking) {
      payload.final_km = finalKm;
      if (!Number.isNaN(googleMapsKm) && finalKm !== googleMapsKm) {
        payload.km_excess_reason = String(form.km_excess_reason || '').trim();
      }
    }
    return payload;
  };

  const ORDER_STATUS_TO_TAB = {
    placed: 'Placed',
    confirmed: 'Confirmed',
    processing: 'Processing',
    dispatch: 'Dispatch',
    dispatched: 'Dispatch',
    shipped: 'Dispatch',
    delivered: 'Delivered',
    cancelled: 'Cancelled',
    returned: 'Returned',
  };

  const APP_ORDER_STATUS_TAB_STORAGE_KEY = 'app_order_status_tab';
  const APP_ORDER_LAST_OPENED_STORAGE_KEY = 'app_order_last_opened_id';
  const APP_ORDER_LIST_STATE_STORAGE_KEY = 'app_order_list_state';

  const consumeSavedAppOrderStatusTab = () => {
    const savedTab = sessionStorage.getItem(APP_ORDER_STATUS_TAB_STORAGE_KEY);
    if (!savedTab) return null;
    sessionStorage.removeItem(APP_ORDER_STATUS_TAB_STORAGE_KEY);
    return savedTab;
  };

  const saveAppOrderListState = (state = {}) => {
    try {
      sessionStorage.setItem(APP_ORDER_LIST_STATE_STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      console.error('Failed to save App Order list state:', error);
    }
  };

  const consumeSavedAppOrderListState = () => {
    try {
      const raw = sessionStorage.getItem(APP_ORDER_LIST_STATE_STORAGE_KEY);
      if (!raw) return null;
      sessionStorage.removeItem(APP_ORDER_LIST_STATE_STORAGE_KEY);
      return JSON.parse(raw);
    } catch (error) {
      console.error('Failed to restore App Order list state:', error);
      sessionStorage.removeItem(APP_ORDER_LIST_STATE_STORAGE_KEY);
      return null;
    }
  };

  /** Local YYYY-MM-DD (avoids UTC day-shift from toISOString). */
  const toStoredAppOrderDate = (date) => {
    if (date == null || date === '') return '';
    if (typeof date === 'string') {
      const trimmed = date.trim();
      if (!trimmed) return '';
      return trimmed.includes('T') ? trimmed.split('T')[0] : trimmed;
    }
    if (date instanceof Date && !Number.isNaN(date.getTime())) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
    return '';
  };

  const rememberLastOpenedAppOrder = (orderOrId) => {
    const orderId =
      typeof orderOrId === 'object' && orderOrId != null
        ? orderOrId.order_id || orderOrId.id
        : orderOrId;
    if (orderId == null || orderId === '') return;
    sessionStorage.setItem(APP_ORDER_LAST_OPENED_STORAGE_KEY, String(orderId));
  };

  const getLastOpenedAppOrderId = () =>
    sessionStorage.getItem(APP_ORDER_LAST_OPENED_STORAGE_KEY);

  const PAYMENT_ADVANCE_TAB_MAP = {
    placed: 'Confirmed',
    confirmed: 'Processing',
    processing: 'Dispatch',
    dispatch: 'Delivered',
    delivered: 'Returned',
  };

  const isPaymentSatisfiedForOrderAdvance = (paymentStatus = '') => {
    const status = normalizeStatusKey(paymentStatus);
    return status === 'paid' || status === 'outstanding' || status === 'credit';
  };

  const isPaymentSatisfiedForDispatch = (paymentStatus = '') => {
    const status = normalizeStatusKey(paymentStatus);
    return status === 'paid' || status === 'credit';
  };

  const shouldAdvanceTabAfterPaymentUpdate = (paymentStatus = '') =>
    isPaymentSatisfiedForOrderAdvance(paymentStatus);

  const getStatusOrderFromOptions = (statusOptions = []) => {
    const orderMap = {};
    statusOptions.forEach((option) => {
      const category = normalizeStatusKey(option.category);
      const sequence = option.sequence || 0;
      if (category) orderMap[category] = sequence;
    });
    return orderMap;
  };

  const getNextSequentialStatusFromOptions = (statusOptions, currentStatusName, currentStatusCategory) => {
    if (!statusOptions?.length) return null;
    const statusOrder = getStatusOrderFromOptions(statusOptions);
    let currentStatusLower = normalizeStatusKey(currentStatusCategory);
    let currentOrder = statusOrder[currentStatusLower] || 0;

    if (currentOrder === 0 && currentStatusName) {
      const currentStatusOption = statusOptions.find((opt) => opt.name === currentStatusName);
      if (currentStatusOption) {
        currentStatusLower = normalizeStatusKey(currentStatusOption.category);
        currentOrder = statusOrder[currentStatusLower] || 0;
      }
    }
    if (currentOrder === 0) return null;

    const isDelivered =
      normalizeStatusKey(currentStatusName) === 'delivered' || currentStatusLower === 'delivered';
    if (isDelivered) {
      return statusOptions.find((option) => {
        const optionCategory = normalizeStatusKey(option.category);
        const optionName = normalizeStatusKey(option.name);
        return optionCategory === 'returned' || optionName === 'returned';
      }) || null;
    }

    const nextOrder = currentOrder + 1;
    return statusOptions.find((option) => {
      const optionCategory = normalizeStatusKey(option.category);
      return statusOrder[optionCategory] === nextOrder;
    }) || null;
  };

  const isSequentialNextStatusTransition = (statusOptions, fromName, fromCategory, toName) => {
    const nextStatus = getNextSequentialStatusFromOptions(statusOptions, fromName, fromCategory);
    if (!nextStatus || !toName) return false;
    return normalizeStatusKey(nextStatus.name) === normalizeStatusKey(toName);
  };

  const statusUpdateRequiresDeliveryPayload = (statusName = '') => {
    const key = normalizeStatusKey(statusName);
    return key === 'dispatch' || key === 'shipped' || key === 'delivered';
  };

  const resolveTabFromOrderStatus = (status) => {
    if (!status) return null;
    const normalized = normalizeStatusKey(status);
    return ORDER_STATUS_TO_TAB[normalized] || status;
  };

  const getAdvancedStatusFromResponse = (response) => {
    const status =
      response?.data?.status?.name ||
      response?.data?.order_status ||
      response?.data?.status;
    return status ? String(status) : null;
  };

  const submitOrderStatusAdvanceRequest = async (order, statusOptions, targetStatusName = null) => {
    const userId = localStorage.getItem('user_id');
    const orderId = order?.order_id;
    if (!orderId) {
      return { success: false, message: 'Order ID is required' };
    }

    let statusName = targetStatusName;
    if (!statusName) {
      const nextStatus = getNextSequentialStatusFromOptions(
        statusOptions,
        order?.status?.name || order?.status_name,
        order?.status?.category
      );
      statusName = nextStatus?.name;
    }

    if (!statusName) {
      return { success: false, message: 'Next order status not found' };
    }

    return apiService.put(
      API_ENDPOINTS.UPDATE_product_status,
      {
        user_id: userId,
        status: statusName,
        order_id: orderId,
      },
      'application/json'
    );
  };

  const resolveNextTabAfterPaymentUpdate = (order, currentTabFilter, response = null) => {
    const responseStatus = getAdvancedStatusFromResponse(response);
    if (responseStatus) {
      return resolveTabFromOrderStatus(responseStatus);
    }

    const currentKey =
      normalizeStatusKey(currentTabFilter) || getOrderStatusKey(order);
    return PAYMENT_ADVANCE_TAB_MAP[currentKey] || null;
  };

  // *Enhanced styled components with better dark theme support
  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    padding: "16px 20px",
    color: theme.palette.text.primary,
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    whiteSpace: "nowrap",
    "&.header": {
        fontWeight: 600,
        fontSize: "0.875rem",
        backgroundColor: alpha(theme.palette.primary.main, 0.04),
        color: theme.palette.text.primary,
        position: 'sticky',
        top: 0,
        zIndex: 1,
    },
  }));

  const StyledTableRow = styled(TableRow, {
    shouldForwardProp: (prop) => prop !== 'highlighted',
  })(({ theme, highlighted }) => ({
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    ...(highlighted && {
      backgroundColor: alpha(theme.palette.grey[500], 0.14),
    }),
    "&:hover": {
        backgroundColor: highlighted
          ? alpha(theme.palette.grey[500], 0.2)
          : alpha(theme.palette.primary.main, 0.04),
        transform: 'translateY(-1px)',
        boxShadow: `0 4px 8px ${alpha(theme.palette.common.black, 0.1)}`,
    },
    "& > .MuiTableCell-root": {
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    },
  }));

  const ExpandableRow = styled(TableRow)(({ theme }) => ({
    backgroundColor: alpha(theme.palette.background.paper, 0.8),
    '& > .MuiTableCell-root': {
      borderBottom: 'none',
    }
  }));

  const DetailCard = styled(Card)(({ theme }) => ({
    margin: '8px 0',
    padding: '20px',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    borderRadius: theme.shape.borderRadius * 2,
    transition: 'all 0.3s ease-in-out',
  }));

  const StatusChip = styled(Chip)(({ theme, status }) => {
    const getStatusConfig = (status) => {
      const configs = {
        pending: { 
          bg: '#FFB74D',
          text: '#FFFFFF',
          icon: <Schedule sx={{ fontSize: '16px !important' }} />
        },
        confirmed: { 
          bg: '#BA68C8',
          text: '#FFFFFF',
          icon: <CheckCircle sx={{ fontSize: '16px !important' }} />
        },
        processing: { 
          bg: '#BA68C8',
          text: '#FFFFFF',
          icon: <Schedule sx={{ fontSize: '16px !important' }} />
        },
        shipped: { 
          bg: '#66BB6A',
          text: '#FFFFFF',
          icon: <LocalShippingOutlined sx={{ fontSize: '16px !important' }} />
        },
        dispatch: {
          bg: '#66BB6A',
          text: '#FFFFFF',
          icon: <LocalShippingOutlined sx={{ fontSize: '16px !important' }} />
        },
        delivered: { 
          bg: '#66BB6A',
          text: '#FFFFFF',
          icon: <CheckCircle sx={{ fontSize: '16px !important' }} />
        },
        cancelled: { 
          bg: '#EF5350',
          text: '#FFFFFF',
          icon: <Cancel sx={{ fontSize: '16px !important' }} />
        },
        returned: { 
          bg: '#BA68C8',
          text: '#FFFFFF',
          icon: <Refresh sx={{ fontSize: '16px !important' }} />
        },
        orders: {
          bg: '#FFB74D',
          text: '#FFFFFF',
          icon: <Schedule sx={{ fontSize: '16px !important' }} />
        }
      }; 
      return configs[status?.toLowerCase()] || { 
        bg: theme.palette.grey[400], 
        text: theme.palette.common.white,
        icon: null
      };
    };

    const config = getStatusConfig(status);
    return {
      backgroundColor: config.bg,
      color: config.text,
      fontWeight: 600,
      textTransform: 'capitalize',
      borderRadius: theme.shape.borderRadius,
      height: 'auto',
      padding: '4px 10px',
      minWidth: 'auto',
      '& .MuiChip-icon': {
        color: config.text,
        marginLeft: '4px',
        marginRight: '4px'
      },
      '& .MuiChip-label': {
        padding: '0 4px',
        fontSize: '0.875rem',
        color: config.text,
        fontWeight: 600
      }
    };
  });

  const PaymentStatusChip = styled(Chip)(({ theme, status }) => {
    const getPaymentStatusConfig = (status) => {
      const configs = {
        pending: { 
          bg: '#fcc874',
          text: '#FFFFFF',
          icon: <Schedule sx={{ fontSize: '16px !important' }} />
        },
        outstanding: { 
          bg: '#888cff',
          text: '#FFFFFF',
          icon: <Schedule sx={{ fontSize: '16px !important' }} />
        },
        paid: { 
          bg: '#22d28a',
          text: '#FFFFFF',
          icon: <CheckCircle sx={{ fontSize: '16px !important' }} />
        },
        failed: { 
          bg: '#f4346e',
          text: '#FFFFFF',
          icon: <Cancel sx={{ fontSize: '16px !important' }} />
        },
        refunded: { 
          bg: '#888cff',
          text: '#FFFFFF',
          icon: <Refresh sx={{ fontSize: '16px !important' }} />
        },
      }; 
      return configs[status?.toLowerCase()] || { 
        bg: theme.palette.grey[400], 
        text: '#FFFFFF',
        icon: null
      };
    };

    const config = getPaymentStatusConfig(status);
    return {
      backgroundColor: config.bg,
      color: config.text,
      fontWeight: 600,
      textTransform: 'capitalize',
      borderRadius: theme.shape.borderRadius,
      height: 'auto',
      padding: '4px 10px',
      minWidth: 'auto',
      '& .MuiChip-icon': {
        color: config.text,
        marginLeft: '4px',
        marginRight: '4px'
      },
      '& .MuiChip-label': {
        padding: '0 4px',
        fontSize: '0.875rem',
        color: config.text,
        fontWeight: 600
      }
    };
  });

  const EmptyStateBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 300,
    padding: theme.spacing(4),
    textAlign: 'center',
    '& .empty-icon': {
      fontSize: 64,
      color: theme.palette.text.disabled,
      marginBottom: theme.spacing(2),
    }
  }));

  const InvoiceModal = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
      maxWidth: '90vw',
      maxHeight: '90vh',
      width: '1200px',
      height: '800px',
      margin: theme.spacing(2),
    },
  }));

  const AppOrderPopup = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
      borderRadius: 8,
      overflow: 'hidden',
      boxShadow: `0 12px 32px ${alpha(theme.palette.common.black, 0.18)}`,
    },
    '& .MuiDialogTitle-root': {
      padding: theme.spacing(2.5, 3, 1.5),
    },
    '& .MuiDialogContent-root': {
      padding: theme.spacing(0, 3, 2.25),
    },
    '& .MuiDialogActions-root': {
      padding: theme.spacing(0, 3, 2.5),
    },
  }));

  const InvoiceFrame = styled('iframe')(({ theme }) => ({
    width: '100%',
    height: '100%',
    border: 'none',
    borderRadius: theme.shape.borderRadius,
  }));

  const VEHICLE_REFERENCE_NO_REGEX = /^[A-Z]{2}\s\d{2}\s[A-Z]{2}\s\d{4}$/;
  const VEHICLE_REFERENCE_NO_ERROR = 'Enter valid format: MH 12 AB 1234';
  const formatVehicleReferenceNo = (value = '') => {
    const cleanedValue = String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
    const part1 = cleanedValue.slice(0, 2).replace(/[^A-Z]/g, '');
    const part2 = cleanedValue.slice(2, 4).replace(/[^0-9]/g, '');
    const part3 = cleanedValue.slice(4, 6).replace(/[^A-Z]/g, '');
    const part4 = cleanedValue.slice(6, 10).replace(/[^0-9]/g, '');
    return [part1, part2, part3, part4].filter(Boolean).join(' ');
  };
  const isVehicleReferenceNoInvalid = (value = '') => {
    const normalizedValue = String(value || '').trim();
    return normalizedValue !== '' && !VEHICLE_REFERENCE_NO_REGEX.test(normalizedValue);
  };

  const PAYMENT_ACCOUNT_DETAILS = {
    accountHolder: 'NEXGENAI AGRITECH',
    accountNumber: '039905009909',
    ifscCode: 'ICIC0000399',
    email: 'CEO@NEXGENAGRITECH.AI',
  };

  const formatPaymentCurrency = (amount) => {
    const value = parseFloat(amount);
    return `₹${(Number.isNaN(value) ? 0 : value).toLocaleString('en-IN')}`;
  };

  const formatOrderDateTime = (value) => {
    if (!value) return '—';
    try {
      return new Date(value).toLocaleString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return String(value);
    }
  };

  const ADVANCE_PAYMENT_FIELD_LABELS = {
    transaction_id: 'Transaction ID',
    transaction_reference_no: 'Transaction Reference No.',
    upi_id_used: 'UPI ID',
    payment_screenshot: 'Payment Screenshot',
    sender_bank_name: 'Bank Name',
    payment_confirmation_file: 'Payment Confirmation File',
    cheque_number: 'Cheque Number',
    bank_name_branch: 'Bank Name',
    cheque_date: 'Cheque Date',
    cheque_amount: 'Cheque Amount',
    cheque_image: 'Cheque Image',
    cash_receipt_number: 'Cash Receipt No.',
    receiver_name: 'Receiver Name (Staff)',
  };

  const getEntryBankName = (entry = {}) =>
    entry?.sender_bank_name ||
    entry?.fields?.sender_bank_name ||
    entry?.fields?.bank_name_branch ||
    '';

  const getEntryNote = (entry = {}, order = null) => {
    if (entry?.comment != null && String(entry.comment).trim() !== '') {
      return String(entry.comment).trim();
    }
    const notes = entry?.order?.notes ?? order?.notes;
    if (notes != null && String(notes).trim() !== '') {
      return String(notes).trim();
    }
    return '';
  };

  const getAdvancePaymentFieldRows = (entry = {}) => {
    const fields = entry?.fields;
    if (!fields || typeof fields !== 'object') return [];
    const skipKeys = new Set([
      'id',
      'created_at',
      'updated_at',
      'is_deleted',
      'sender_bank_name',
      'bank_name_branch',
    ]);
    return Object.entries(fields)
      .filter(([key, val]) => !skipKeys.has(key) && val != null && String(val).trim() !== '')
      .map(([key, val]) => ({
        key,
        label:
          ADVANCE_PAYMENT_FIELD_LABELS[key] ||
          key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
        value: String(val),
      }));
  };

  function AdvancePaymentEntryCard({ entry, index, theme, order = null }) {
    const fieldRows = getAdvancePaymentFieldRows(entry);
    const recordedBy =
      entry.user?.full_name ||
      [entry.user?.first_name, entry.user?.last_name].filter(Boolean).join(' ') ||
      '—';
    const [previewUrl, setPreviewUrl] = useState(null);
    const entries = getAdvancePaymentEntries(order) || [];

    return (
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: index > 0 ? 0 : 0,
          borderRadius: 2,
          bgcolor: alpha(theme.palette.warning.main, 0.04),
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5, gap: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            Entry #{entries.length - index}
            {entry.is_full_payment
              ? entries.length === 1
                ? ' · Full Payment'
                : ' · Pending Payment'
              : ' · Advanced Payment'}
          </Typography>
          <Chip
            size="small"
            label={entry.method?.name || '—'}
            color="warning"
            variant="outlined"
          />
        </Box>

        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2">
              <strong>{entries.length === 1 && (entry?.remaining_amount === 0 || entry?.remaining_amount === 0.0)
                ? 'Paid Amount'
                : entry?.remaining_amount === 0 || entry?.remaining_amount === 0.0
                  ? 'Pending Paid Amount'
                  : 'Advance Paid Amount'}:</strong> {formatPaymentCurrency(entry.advance_amount)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2">
              <strong>Pending Amount:</strong> {formatPaymentCurrency(entry.remaining_amount)}
            </Typography>
          </Grid>
          {entry.transaction_id ? (
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">
                <strong>Transaction ID:</strong> {entry.transaction_id}
              </Typography>
            </Grid>
          ) : null}
          
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">
                <strong>Bank Name:</strong> {getEntryBankName(entry) || 'N/A'}
              </Typography>
            </Grid>
          
          <Grid item xs={12} sm={6}>
            <Typography variant="body2">
              <strong>Recorded By:</strong> {recordedBy}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2">
              <strong>Payment Date:</strong> {formatOrderDateTime(entry.created_at)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2">
              <strong>Updated At:</strong> {formatOrderDateTime(entry.updated_at)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2">
              <strong>Note:</strong> {getEntryNote(entry, order) || 'N/A'}
            </Typography>
          </Grid>
        </Grid>

        {fieldRows.length > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Payment method details
            </Typography>
            <Grid container spacing={1}>
              {fieldRows.map((row) => (
                <Grid item xs={12} sm={6} key={`${entry.id}-${row.key}`}>
                  {row.key === 'payment_screenshot' ||
                  row.key === 'payment_confirmation_file' ||
                  row.key === 'cheque_image' ? (
                    <Typography
                      variant="body2"
                      component="div"
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
                    >
                      <strong>{row.label}:</strong>
                      {row.value ? (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setPreviewUrl(row.value)}
                          sx={{
                            textTransform: 'none',
                            border: '1px solid #1e293b',
                            borderRadius: '8px',
                            px: '18px',
                            py: '6px',
                            fontSize: '13px',
                            fontWeight: 500,
                            color: '#1e293b',
                            bgcolor: '#fff',
                            '&:hover': {
                              border: '1px solid #1e293b',
                              bgcolor: '#f1f5f9',
                            },
                          }}
                        >
                          View
                        </Button>
                      ) : null}
                    </Typography>
                  ) : (
                    <Typography variant="body2">
                      <strong>{row.label}:</strong> {row.value}
                    </Typography>
                  )}
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {entry.image && (
          <Typography variant="body2" sx={{ mt: 1.5 }}>
            <strong>Attachment:</strong>{' '}
            <Box
              component="a"
              href={entry.image}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ wordBreak: 'break-all' }}
            >
              View file
            </Box>
          </Typography>
        )}

        <Dialog
          open={Boolean(previewUrl)}
          onClose={() => setPreviewUrl(null)}
          maxWidth={false}
          PaperProps={{
            sx: {
              borderRadius: '12px',
              p: 2,
              maxWidth: '90vw',
              maxHeight: '90vh',
            },
          }}
          BackdropProps={{
            sx: {
              backgroundColor: 'rgba(0,0,0,0.6)',
            },
          }}
        >
          <Box sx={{ position: 'relative' }}>
            <IconButton
              onClick={() => setPreviewUrl(null)}
              size="small"
              sx={{
                position: 'absolute',
                top: 6,
                right: 6,
                zIndex: 1,
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <Box
              component="img"
              src={previewUrl || ''}
              alt="Payment Screenshot"
              sx={{
                maxWidth: '100%',
                maxHeight: '80vh',
                objectFit: 'contain',
                borderRadius: '10px',
              }}
            />
          </Box>
        </Dialog>
      </Paper>
    );
  }

  function AdvancePaymentDetailsSection({ order, theme }) {
    const entries = getAdvancePaymentEntries(order);
    if (entries.length === 0) return null;

    return (
      <Grid item xs={12}>
        <DetailCard
          elevation={2}
          sx={{
            backgroundColor: 'background.paper',
            border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            borderRadius: 3,
            p: 2.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Payment sx={{ mr: 1, color: theme.palette.warning.main }} />
            <Typography variant="h6" fontWeight={600}>
              Payment Details ({entries.length})
            </Typography>
          </Box>
          {entries.map((entry, index) => (
            <Box key={entry.id ?? `advance-${index}`} sx={{ mb: index < entries.length - 1 ? 2 : 0 }}>
              <AdvancePaymentEntryCard entry={entry} index={index} theme={theme} order={order} />
            </Box>
          ))}
        </DetailCard>
      </Grid>
    );
  }

  const formatOrderDetailCurrency = (amount) =>
    `₹${parseFloat(amount).toLocaleString('en-IN')}`;

  const getOrderDetailStatusIcon = (status) => {
    const category = typeof status === 'object' && status !== null && 'category' in status
      ? status.category
      : status;
    const key = (typeof category === 'string' ? category : (category?.name ?? '')).toLowerCase();
    switch (key) {
      case 'pending': return <Schedule />;
      case 'confirmed': return <CheckCircle />;
      case 'processing': return <Schedule />;
      case 'shipped': return <LocalShippingOutlined />;
      case 'delivered': return <CheckCircle />;
      case 'cancelled': return <Cancel />;
      case 'returned': return <Refresh />;
      default: return null;
    }
  };

  function AppOrderDetailsView({ order, onCheckPaymentStatus, checkingPaymentOrderId = null }) {
    const theme = useTheme();
    const [receiptPreviewUrl, setReceiptPreviewUrl] = useState(null);
    const dieselReceiptPhotoUrl = order?.delivery_challan?.diesel_receipt_photo
      ? apiService.resolveAuthenticatedFileUrl(order.delivery_challan.diesel_receipt_photo)
      : '';
    const isDieselReceiptPdf = /\.pdf($|\?)/i.test(String(order?.delivery_challan?.diesel_receipt_photo || ''));

    return (
<Box sx={{ width: '100%', padding: 2 }}>
              <Grid container spacing={2.5} alignItems="stretch">
                <Grid item xs={12} md={4} sx={{ minWidth: 0 }}>
                  <DetailCard elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'background.paper', border: `1px solid ${alpha(theme.palette.divider, 0.5)}`, borderRadius: 3, p: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Person sx={{ mr: 1, color: theme.palette.primary.main }} />
                      <Typography variant="h6" fontWeight={600}>Customer Information</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Name:</strong> {order.user?.full_name || order.user_name}
                    </Typography>
                    {(order.dealer_name || order?.user?.dealer_name) && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Dealer:</strong> {order.dealer_name || order?.user?.dealer_name}
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Order ID:</strong> {order.order_id || "N/A"}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Email:</strong> {order.user?.email || order.user_email}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Order Date:</strong> {order.order_date_formatted || "N/A"}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Mobile:</strong> {order.user?.mobile_number || order.mobile_number}
                    </Typography>
                    
                    {(order.farmer_name || getAdvancePaymentEntries(order)[0]?.order?.farmer_name) && (
                      <Box sx={{ mt: 1, mb: 1, p: 1.5, backgroundColor: alpha(theme.palette.info.main, 0.1), borderRadius: 1, border: `1px solid ${alpha(theme.palette.info.main, 0.3)}` }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "grey.600", mb: 0.5 }}>
                          Order on Behalf of : {order.farmer_name || getAdvancePaymentEntries(order)[0]?.order?.farmer_name}
                        </Typography>
                      </Box>
                    )}
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 1 }}>
                      <LocationOn sx={{ mr: 1, color: theme.palette.secondary.main }} />
                      <Typography variant="subtitle2" fontWeight={600}>Delivery Address</Typography>
                    </Box>
                    <Tooltip title={titleCaseAddress(order.delivery_address || (order.address_details ? `${order.address_details.full_address}, ${order.address_details.village}, ${order.address_details.tehsil}, ${order.address_details.pincode}` : 'Address not available'))} arrow placement="top">
                      <Typography variant="body2" color="text.secondary" sx={{ backgroundColor: alpha(theme.palette.background.default, 0.5), padding: 1.5, borderRadius: 1, border: `1px solid ${alpha(theme.palette.divider, 0.1)}`, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'help' }}>
                        {titleCaseAddress(order.delivery_address || (order.address_details ? `${order.address_details.full_address}, ${order.address_details.village}, ${order.address_details.tehsil}, ${order.address_details.pincode}` : 'Address not available'))}
                      </Typography>
                    </Tooltip>
                    
                    {order.notes && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }} fontWeight={600}>Special Instructions:</Typography>
                        <Tooltip title={order.notes} arrow placement="top">
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', p: 1.5, backgroundColor: alpha(theme.palette.info.main, 0.1), borderRadius: 1, border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'help' }}>
                            {order.notes}
                          </Typography>
                        </Tooltip>
                      </Box>
                    )}
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 1 }}>
                      <LocationOn sx={{ mr: 1, color: theme.palette.warning.main }} />
                      <Typography variant="subtitle2" fontWeight={600}>Billing Address</Typography>
                    </Box>
                    <Tooltip title={titleCaseAddress(order.billing_address || (order.bill_address_details ? `${order.bill_address_details.full_address}, ${order.bill_address_details.village}, ${order.bill_address_details.tehsil}, ${order.bill_address_details.pincode}` : 'Billing address not available'))} arrow placement="top">
                      <Typography variant="body2" color="text.secondary" sx={{ backgroundColor: alpha(theme.palette.background.default, 0.5), padding: 1.5, borderRadius: 1, border: `1px solid ${alpha(theme.palette.divider, 0.1)}`, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'help' }}>
                        {titleCaseAddress(order.billing_address || (order.bill_address_details ? `${order.bill_address_details.full_address}, ${order.bill_address_details.village}, ${order.bill_address_details.tehsil}, ${order.bill_address_details.pincode}` : 'Billing address not available'))}
                      </Typography>
                    </Tooltip>
                  </DetailCard>
                </Grid>

                <Grid item xs={12} md={4} sx={{ minWidth: 0 }}>
                  <DetailCard elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 2.5, backgroundColor: 'background.paper', border: `1px solid ${alpha(theme.palette.divider, 0.5)}`, borderRadius: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
                      <LocalShipping sx={{ mr: 1.25, fontSize: '1.25rem', color: theme.palette.success.main }} />
                      <Typography variant="h6" fontWeight={600}>Order Status</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          <strong>Order Status:</strong> <StatusChip label={order.status?.name || order.order_status_display} status={order.status?.category?.toLowerCase() || order.status?.name?.toLowerCase()} size="small" icon={getOrderDetailStatusIcon(order.status)} />
                        </Typography>
                      </Box>
                    </Box>
                    
                    {order.expected_delivery_date && (
                      <Box sx={{ mb: 1.5 }}>
                        <Typography variant="body2"><strong>Expected Delivery Date (Farmer):</strong></Typography>
                        <Typography variant="body2" color="primary" fontWeight={600} sx={{ mt: 0.5 }}>
                          {new Date(order.expected_delivery_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                        </Typography>
                      </Box>
                    )}
                    
                    {order.delivery_date && ['delivered', 'returned'].includes(
                      (order.status?.category || order.status?.name || '').toString().toLowerCase()
                    ) && (
                      <Box sx={{ mt: 2.5, mb: 1.5 }}>
                        <Typography variant="body2" sx={{ mb: 1.25 }}>
                          <strong>Actual Delivery Date:</strong>{' '}
                          <Typography component="span" variant="body2" color="success.main" fontWeight={600}>
                            {(() => {
                              try {
                                const date = new Date(order.delivery_date);
                                return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
                              } catch (e) {
                                return order.delivery_date_formatted || order.delivery_date;
                              }
                            })()}
                          </Typography>
                        </Typography>
                      </Box>
                    )}

                    {(order.status?.category || order.status?.name || '').toString().toLowerCase() === 'returned' && (
                      <Box sx={{ mt: 2.5, mb: 1.5 }}>
                        <Typography variant="body2" sx={{ mb: 1.25 }}>
                          <strong>Returned At:</strong>{' '}
                          {(() => {
                            const dt = order.returned_at ?? order.status?.returned_at;
                            if (!dt) return 'N/A';
                            try {
                              const date = new Date(dt);
                              return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
                            } catch (e) {
                              return String(dt);
                            }
                          })()}
                        </Typography>
                      </Box>
                    )}

                    {(order.status?.category || order.status?.name || '').toString().toLowerCase() === 'cancelled' && (
                      <Box sx={{ mt: 2.5, mb: 1.5 }}>
                        <Typography variant="body2" sx={{ mb: 1.25 }}>
                          <strong>Cancelled At:</strong>{' '}
                          {(() => {
                            const dt = order.cancelled_at ?? order.status?.cancelled_at;
                            if (!dt) return 'N/A';
                            try {
                              const date = new Date(dt); 
                              return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
                            } catch (e) {
                              return String(dt);
                            }
                          })()}
                        </Typography>
                      </Box>
                    )}
                    
                    {order.reason && (
                      <Box sx={{ mb: 2.5 }}>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>Reason:</strong> {order.reason}
                        </Typography>
                      </Box>
                    )}

                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Payment sx={{ mr: 1.25, fontSize: '1.25rem', color: theme.palette.info.main }} />
                      <Typography variant="subtitle1" fontWeight={600}>Payment Information</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mb: 1.25 }}>
                      <strong>Payment Status:</strong> 
                      <PaymentStatusChip label={order.payment_status?.name || order.payment_status_display || 'Pending'} status={(order.payment_status?.name || order.payment_status || 'pending')?.toLowerCase()} size="small" sx={{ ml: 1.25 }} />
                    </Typography>
                    
                    {/* {((order.reason ?? order.status?.reason) != null && String(order.reason ?? order.status?.reason ?? '').trim() !== '') && (
                      <Typography variant="body2" sx={{ mb: 1.25, mt: 1 }}>
                        <strong>Reason:</strong> {order.reason ?? order.status?.reason ?? ''}
                      </Typography>
                    )} */}
                    
                    {order?.latest_refund && (
                      <>
                        <Typography variant="body2" sx={{ mb: 1.25 }}>
                          <strong>Refunded Amount:</strong> ₹{order?.latest_refund?.amount ?? "N/A"}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1.25 }}>
                          <strong>Refunded At:</strong> {order?.latest_refund?.refunded_at ? new Date(order.latest_refund.refunded_at).toLocaleString() : "N/A"}
                        </Typography>
                      </>
                    )}
                    
                    {(order.payment_status?.name || order.payment_status || '').toString().toLowerCase() === 'refunded' && (
                      <>
                        <Typography variant="body2" sx={{ mb: 1.25 }}>
                          <strong>Refunded Amount:</strong> {order.refunded_amount ?? order.payment_status?.refunded_amount ?? 'N/A'}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1.25 }}>
                          <strong>Refunded At:</strong> {(() => {
                            const dt = order.refunded_at ?? order.payment_status?.refunded_at;
                            if (!dt) return 'N/A';
                            try {
                              const date = new Date(dt);
                              return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
                            } catch (e) {
                              return String(dt);
                            }
                          })()}
                        </Typography>
                      </>
                    )}

                    {order?.latest_refund && !order?.latest_refund?.is_success && (
                      <Button size="small" variant="outlined" color="primary" onClick={(e) => { e.stopPropagation(); onCheckPaymentStatus?.(order); }} disabled={checkingPaymentOrderId === (order?.order_id ?? order?.id)} sx={{ mt: 1.5, textTransform: 'none', fontWeight: 600 }}>
                        {checkingPaymentOrderId === (order?.order_id ?? order?.id) ? (<CircularProgress size={18} sx={{ mr: 0.5 }} />) : null}
                        Check refund status
                      </Button>
                    )}
                  </DetailCard>
                </Grid>

                {order.delivery_challan && (
                  <Grid item xs={12} md={4} sx={{ minWidth: 0 }}>
                    <DetailCard elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'background.paper', border: `1px solid ${alpha(theme.palette.divider, 0.5)}`, borderRadius: 3, p: 2.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <ChallanIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                        <Typography variant="h6" fontWeight={600}>Delivery Challan Details</Typography>
                      </Box>
                      {order.delivery_challan.vehicle_number && <Typography variant="body2" sx={{ mb: 1 }}><strong>Vehicle Number:</strong> {order.delivery_challan.vehicle_number}</Typography>}
                      {order.delivery_challan.vehicle_name && <Typography variant="body2" sx={{ mb: 1 }}><strong>Vehicle Name:</strong> {order.delivery_challan.vehicle_name}</Typography>}
                      {order.delivery_challan.driver_name && <Typography variant="body2" sx={{ mb: 1 }}><strong>Driver Name:</strong> {order.delivery_challan.driver_name}</Typography>}
                      {isOrderByTruck(order) && order.delivery_challan.vehicle_type && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Vehicle Type:</strong> {order.delivery_challan.vehicle_type}
                        </Typography>
                      )}
                      {order.delivery_challan.dispatch_person_name && <Typography variant="body2" sx={{ mb: 1 }}><strong>Dispatch Person:</strong> {order.delivery_challan.dispatch_person_name}</Typography>}
                      {order.order_by && <Typography variant="body2" sx={{ mb: 1 }}><strong>Order By:</strong> {order.order_by}</Typography>}
                      {order.order_by_entity && <Typography variant="body2" sx={{ mb: 1 }}><strong>Delivery Partner Name:</strong> {order.order_by_entity}</Typography>}
                      {(order.dealer_name || order?.user?.dealer_name) && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Dealer:</strong> {order.dealer_name || order?.user?.dealer_name}
                        </Typography>
                      )}
                      {isOrderByTruck(order) && (
                        <>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="subtitle2" fontWeight={600} gutterBottom>Kilometer Information</Typography>
                          {!isTruthyFlag(order.delivery_challan.meter_not_working) && (
                            <>
                              {order.delivery_challan.start_km !== null && order.delivery_challan.start_km !== undefined && <Typography variant="body2" sx={{ mb: 1 }}><strong>Start KM:</strong> {order.delivery_challan.start_km}</Typography>}
                              {order.delivery_challan.end_km !== null && order.delivery_challan.end_km !== undefined && <Typography variant="body2" sx={{ mb: 1 }}><strong>End KM:</strong> {order.delivery_challan.end_km}</Typography>}
                              {order.delivery_challan.total_km !== null && order.delivery_challan.total_km !== undefined && <Typography variant="body2" sx={{ mb: 1 }}><strong>Total KM:</strong> {order.delivery_challan.total_km}</Typography>}
                            </>
                          )}
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Meter not Working:</strong> {isTruthyFlag(order.delivery_challan.meter_not_working) ? 'Yes' : 'No'}
                          </Typography>
                          {order.delivery_challan.google_maps_km !== null && order.delivery_challan.google_maps_km !== undefined && (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Google Maps KM:</strong> {order.delivery_challan.google_maps_km}
                            </Typography>
                          )}
                          {order.delivery_challan.final_km !== null && order.delivery_challan.final_km !== undefined && (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Final KM:</strong> {order.delivery_challan.final_km}
                            </Typography>
                          )}
                          {order.delivery_challan.km_excess_reason && (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>KM Excess Reason:</strong> {order.delivery_challan.km_excess_reason}
                            </Typography>
                          )}
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="subtitle2" fontWeight={600} gutterBottom>Diesel Information</Typography>
                          {order.delivery_challan.diesel_receipt_number && (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Diesel Receipt No:</strong> {order.delivery_challan.diesel_receipt_number}
                            </Typography>
                          )}
                          {order.delivery_challan.diesel_amount !== null && order.delivery_challan.diesel_amount !== undefined && (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Diesel Amount:</strong> {order.delivery_challan.diesel_amount}
                            </Typography>
                          )}
                          {order.delivery_challan.diesel_receipt_photo && (
                            <Typography
                              variant="body2"
                              component="div"
                              sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
                            >
                              <strong>Receipt Upload:</strong>
                              {isDieselReceiptPdf ? (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  href={dieselReceiptPhotoUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{
                                    textTransform: 'none',
                                    border: '1px solid #1e293b',
                                    borderRadius: '8px',
                                    px: '18px',
                                    py: '6px',
                                    fontSize: '13px',
                                    fontWeight: 500,
                                    color: '#1e293b',
                                    bgcolor: '#fff',
                                    '&:hover': {
                                      border: '1px solid #1e293b',
                                      bgcolor: '#f1f5f9',
                                    },
                                  }}
                                >
                                  View
                                </Button>
                              ) : (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => setReceiptPreviewUrl(dieselReceiptPhotoUrl)}
                                  sx={{
                                    textTransform: 'none',
                                    border: '1px solid #1e293b',
                                    borderRadius: '8px',
                                    px: '18px',
                                    py: '6px',
                                    fontSize: '13px',
                                    fontWeight: 500,
                                    color: '#1e293b',
                                    bgcolor: '#fff',
                                    '&:hover': {
                                      border: '1px solid #1e293b',
                                      bgcolor: '#f1f5f9',
                                    },
                                  }}
                                >
                                  View
                                </Button>
                              )}
                            </Typography>
                          )}
                        </>
                      )}
                      {(() => {
                        const challan = order.delivery_challan || {};
                        const companyKind = getOrderCompanyKind(order);
                        const hasValue = (value) => {
                          if (value === null || value === undefined) return false;
                          if (typeof value === 'string') {
                            const text = String(value).trim();
                            if (!text) return false;
                            if (/^0+(\.0+)?$/.test(text)) return false;
                            return true;
                          }
                          if (typeof value === 'number' && value === 0) return false;
                          return true;
                        };

                        const rows = [];
                        if (companyKind === 'seeds') {
                          if (hasValue(challan.extra_packages)) {
                            rows.push(['Extra Packages', challan.extra_packages]);
                          }
                          if (hasValue(challan.packages_returned)) {
                            rows.push(['Packages Returned', challan.packages_returned]);
                          }
                          if (hasValue(challan.packages_returned_reason)) {
                            rows.push(['Packages Returned Reason', challan.packages_returned_reason]);
                          }
                        } else if (companyKind === 'fertilizers') {
                          if (hasValue(challan.sample_packages_quantity)) {
                            rows.push(['Sample Packages Quantity', challan.sample_packages_quantity]);
                          }
                          if (hasValue(challan.package_returned)) {
                            rows.push(['Package Returned', challan.package_returned]);
                          }
                        } else {
                          if (hasValue(challan.crates_dispatched)) {
                            rows.push(['Crates Dispatched', challan.crates_dispatched]);
                          }
                          if (hasValue(challan.crates_returned)) {
                            rows.push(['Crates Returned', challan.crates_returned]);
                          }
                          if (hasValue(challan.crates_returned_person || challan.crates_return_person)) {
                            rows.push([
                              'Crates Return Person',
                              challan.crates_returned_person || challan.crates_return_person,
                            ]);
                          }
                          if (hasValue(challan.crates_short_reason)) {
                            rows.push(['Crates Short Reason', challan.crates_short_reason]);
                          }
                          if (hasValue(challan.extra_plants) || hasValue(challan.extra_plant)) {
                            rows.push([
                              'Extra Plants',
                              hasValue(challan.extra_plants) ? challan.extra_plants : challan.extra_plant,
                            ]);
                          }
                          if (hasValue(challan.plants_returned)) {
                            rows.push(['Plants Returned', challan.plants_returned]);
                          }
                          if (hasValue(challan.plants_returned_reason)) {
                            rows.push(['Plants Returned Reason', challan.plants_returned_reason]);
                          }
                        }

                        if (!rows.length) return null;

                        return (
                          <>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                              {companyKind === 'seeds' || companyKind === 'fertilizers'
                                ? 'Packages'
                                : 'Crates / Plants'}
                            </Typography>
                            {rows.map(([label, value]) => (
                              <Typography key={label} variant="body2" sx={{ mb: 1 }}>
                                <strong>{label}:</strong> {value}
                              </Typography>
                            ))}
                          </>
                        );
                      })()}
                    </DetailCard>
                  </Grid>
                )}

                <AdvancePaymentDetailsSection order={order} theme={theme} />

                <Grid item xs={12}>
                  <DetailCard elevation={2} sx={{ backgroundColor: 'background.paper', border: `1px solid ${alpha(theme.palette.divider, 0.5)}`, borderRadius: 3, p: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <ShoppingCart sx={{ mr: 1, color: theme.palette.primary.main }} />
                      <Typography variant="h5" fontWeight={600}>Order Items ({order.total_items})</Typography>
                    </Box>
                    
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell align="center" sx={{ fontWeight: 600, width: '80px' }}>Sr. No.</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 600 }}>Product</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 600 }}>Category</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 600 }}>SKU</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 600 }}>Size</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 600 }}>Unit Price</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 600 }}>Quantity</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 600 }}>GST Rate</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 600 }}>Total</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {order.items.map((item, index) => (
                            <TableRow key={index} sx={{ '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.04) } }}>
                              <TableCell align="center">{index + 1}</TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ maxWidth: 220, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontWeight: 500 }} title={item.product_name}>
                                  {item.product_name}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }} title={item.category_name}>
                                  {item.category_name || 'N/A'}
                                </Typography>
                              </TableCell>
                              <TableCell align="center"><Typography variant="body2" color="text.secondary">{item.product_sku}</Typography></TableCell>
                              <TableCell align="center">
                                <Typography variant="body2" color="text.secondary">
                                  {item.size
                                    ? `${item.size}${item.product_size?.unit_of_measure ? ` ${item.product_size.unit_of_measure}` : ''}`
                                    : 'N/A'}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">{formatOrderDetailCurrency(item.unit_price)}</TableCell>
                              <TableCell align="center">{parseFloat(item.quantity)}</TableCell>
                              <TableCell align="center">{parseFloat(item.gst_rate)}%</TableCell>
                              <TableCell align="center"><Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>{formatOrderDetailCurrency(item.item_total_with_gst)}</Typography></TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Total Items:</strong> {order.total_items}</Typography>
                        <Typography variant="body2"><strong>Total Quantity:</strong> {parseFloat(order.total_quantity)}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Subtotal:</strong> {formatOrderDetailCurrency(order.subtotal)}</Typography>
                          <Typography variant="body2" sx={{ mb: 0.5 }}><strong>GST Amount:</strong> {formatOrderDetailCurrency(order.gst_amount)}</Typography>
                          <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Delivery Charge:</strong> {formatOrderDetailCurrency(order.delivery_charge)}</Typography>
                          {parseFloat(order.discount_amount) > 0 && <Typography variant="body1" color="success.main" sx={{ mb: 0.5 }}><strong>Discount:</strong> -{formatOrderDetailCurrency(order.discount_amount)}</Typography>}
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="h6" color="primary.main" fontWeight={700}>Total: {formatOrderDetailCurrency(order.total_amount)}</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </DetailCard>
                </Grid>
              </Grid>

              <Dialog
                open={Boolean(receiptPreviewUrl)}
                onClose={() => setReceiptPreviewUrl(null)}
                maxWidth={false}
                PaperProps={{
                  sx: {
                    borderRadius: '12px',
                    p: 2,
                    maxWidth: '90vw',
                    maxHeight: '90vh',
                  },
                }}
                BackdropProps={{
                  sx: {
                    backgroundColor: 'rgba(0,0,0,0.6)',
                  },
                }}
              >
                <Box sx={{ position: 'relative' }}>
                  <IconButton
                    onClick={() => setReceiptPreviewUrl(null)}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 6,
                      right: 6,
                      zIndex: 1,
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                  <Box
                    component="img"
                    src={receiptPreviewUrl || ''}
                    alt="Diesel Receipt"
                    sx={{
                      maxWidth: '100%',
                      maxHeight: '80vh',
                      objectFit: 'contain',
                      borderRadius: '10px',
                    }}
                  />
                </Box>
              </Dialog>
            </Box>
    
    );
  }


  const sanitizePaymentFieldValue = (value = '', maxLength) => {
    const cleaned = String(value).replace(/[^a-zA-Z0-9\s.\-/@]/g, '');
    return maxLength ? cleaned.slice(0, maxLength) : cleaned;
  };

  const PAYMENT_FIELD_SANITIZERS = {
    transaction_reference_no: (value, maxLength = 22) =>
      String(value).toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, maxLength),
    sender_bank_name: (value, maxLength = 50) =>
      String(value).replace(/[^a-zA-Z\s]/g, '').slice(0, maxLength),
    cheque_number: (value, maxLength = 6) =>
      String(value).replace(/\D/g, '').slice(0, maxLength),
    bank_name_branch: (value, maxLength = 50) =>
      String(value).replace(/[^a-zA-Z\s]/g, '').slice(0, maxLength),
    cheque_amount: (value) => {
      const cleaned = String(value).replace(/[^\d.]/g, '');
      const [whole, fraction = ''] = cleaned.split('.');
      if (!cleaned.includes('.')) return whole.slice(0, 12);
      return `${whole.slice(0, 12)}.${fraction.slice(0, 2)}`;
    },
    receiver_name: (value, maxLength = 50) =>
      String(value).replace(/[^a-zA-Z\s]/g, '').slice(0, maxLength),
  };

  const sanitizePaymentFieldByKey = (key, value, maxLength) => {
    const sanitizer = PAYMENT_FIELD_SANITIZERS[key];
    if (sanitizer) return sanitizer(value, maxLength);
    return sanitizePaymentFieldValue(value, maxLength);
  };

  const getChequeDateBounds = () => {
    const today = new Date();
    const min = new Date(today);
    min.setDate(min.getDate() - 90);
    const max = new Date(today);
    max.setDate(max.getDate() + 365);
    return {
      min: min.toISOString().slice(0, 10),
      max: max.toISOString().slice(0, 10),
    };
  };

  const PAYMENT_DETAILS_DIALOG_CONTENT_SX = {
    flex: '1 1 auto',
    overflowY: 'auto',
    py: 1,
    px: 2,
  };

  const PAYMENT_DETAILS_DIALOG_ACTIONS_SX = {
    flexShrink: 0,
    px: 2,
    py: 1,
    borderTop: 1,
    borderColor: 'divider',
    bgcolor: 'background.paper',
  };

  const PAYMENT_DETAILS_DIALOG_PAPER_PROPS = {
    sx: {
      display: 'flex',
      flexDirection: 'column',
      maxHeight: '90vh',
      maxWidth: { xs: 'calc(100% - 16px)', sm: 520 },
      width: '100%',
      m: { xs: 1, sm: 2 },
    },
  };

  const PAYMENT_BANK_NAME_FIELD_KEYS = new Set([
    'sender_bank_name',
    'bank_name_branch',
    'bank_name',
  ]);

  const isPaymentBankNameField = (field = {}) => {
    const key = String(field.key || '').toLowerCase();
    const label = String(field.label || '').toLowerCase();
    if (PAYMENT_BANK_NAME_FIELD_KEYS.has(key)) return true;
    return label.includes('sender bank') || label.includes('bank name');
  };

  const enforcePaymentMethodRequiredFields = (formConfig) => {
    if (!formConfig?.fields) return formConfig;
    const enforcedConfig = {
      ...formConfig,
      fields: formConfig.fields.map((field) =>
        isPaymentBankNameField(field)
          ? { ...field, required: false }
          : { ...field, required: true }
      ),
    };
    if (enforcedConfig.fileField) {
      enforcedConfig.fileField = { ...enforcedConfig.fileField, required: true };
    }
    return enforcedConfig;
  };

  const getCreditChequePaymentFields = (paymentFields, installmentAmount, paymentMethodConfig) => {
    if (paymentMethodConfig?.type !== 'cheque') return paymentFields;
    return {
      ...paymentFields,
      cheque_amount: installmentAmount ?? '',
    };
  };

  const validatePaymentMethodMandatoryFields = (modalState) => {
    if (isZeroRemainingPayment(modalState)) return '';
    if (!modalState?.paymentMethod || !modalState?.paymentMethodConfig) return '';

    const fields = modalState.paymentFields || {};
    const paymentFiles = modalState.paymentFiles || {};
    const formConfig = enforcePaymentMethodRequiredFields(modalState.paymentMethodConfig);
    const type = formConfig.type;

    const missingField = formConfig.fields?.find((field) => {
      if (!field.required) return false;
      return !String(fields[field.key] || '').trim();
    });
    if (missingField) {
      return `Please enter ${missingField.label}`;
    }

    if (type === 'bank_transfer') {
      const ref = String(fields.transaction_reference_no || fields.transaction_id || '').trim();
      if (!/^[A-Z0-9]+$/.test(ref)) {
        return 'Transaction Reference No. must contain only English letters and numbers (A–Z, 0–9)';
      }
    }
    if (type === 'cash') {
      const receiver = String(fields.receiver_name || '').trim();
      if (!/^[a-zA-Z\s]+$/.test(receiver)) {
        return 'Receiver Name must contain only English letters';
      }
    }
    if (type === 'cheque') {
      const chequeNo = String(fields.cheque_number || '').trim();
      if (!/^\d{1,6}$/.test(chequeNo)) {
        return 'Cheque Number must be 1 to 6 digits';
      }
    }
    if (
      formConfig.fileField?.required &&
      !(paymentFiles[formConfig.fileField.key] instanceof File)
    ) {
      return `Please upload ${formConfig.fileField.label}`;
    }
    return '';
  };

  const buildNewCreditPaymentFormData = (modalState, orderId, installmentAmount, creditNote = '') => {
    const paidFormState = {
      ...modalState,
      advanceAmount: String(installmentAmount),
      orderPaymentDetails: modalState?.orderPaymentDetails
        ? { ...modalState.orderPaymentDetails, remainingAmount: installmentAmount }
        : { remainingAmount: installmentAmount, totalAmount: installmentAmount, paidAmount: 0 },
    };
    const paidFormData = buildNewPaidPaymentFormData(paidFormState, orderId);
    const formData = new FormData();
    for (const [key, value] of paidFormData.entries()) {
      if (key === 'payment_status') {
        formData.append(key, 'Credit');
      } else if (key === 'paid_amount') {
        formData.append(key, String(installmentAmount));
      } else {
        formData.append(key, value);
      }
    }
    if (creditNote.trim()) {
      formData.append('note', creditNote.trim());
    }
    return formData;
  };

  const getInlinePaidPaymentStepState = (orderPaymentDetails) => ({
    ...getPaidPaymentStepState(orderPaymentDetails),
    step: 'status',
  });

  const getCreditPaymentStepState = (orderPaymentDetails = null) => {
    const remainingAmount = Number(orderPaymentDetails?.remainingAmount ?? 0);
    return {
    step: 'status',
    paymentOption: 'credit',
    installmentAmount: remainingAmount === 0 ? '0' : '',
    creditNote: '',
    paymentMethod: '',
    paymentMethodConfig: null,
    paymentFields: {},
    paymentFiles: {},
    paymentError: '',
  };
  };

  function PaymentAmountSummary({ orderPaymentDetails }) {
    if (!orderPaymentDetails) return null;
    const rows = [
      ['Total Amount', Number(orderPaymentDetails.totalAmount ?? 0)],
      ['Paid Amount', Number(orderPaymentDetails.paidAmount ?? 0)],
      ['Remaining Amount', Number(orderPaymentDetails.remainingAmount ?? 0)],
    ];

    return (
      <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
          Payment Summary
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            gap: 1.25,
          }}
        >
          {rows.map(([label, value]) => (
            <Box key={label}>
              <Typography variant="caption" color="text.secondary" display="block">
                {label}
              </Typography>
              <Typography variant="body1" fontWeight={700}>
                {formatPaymentCurrency(value)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Paper>
    );
  }

  function PaymentDetailsStepContent({
    idPrefix,
    order,
    orderPaymentDetails = null,
    summaryPaymentDetails = null,
    paymentOption,
    advanceAmount,
    onAdvanceAmountChange,
    installmentAmount = '',
    onInstallmentAmountChange,
    creditNote = '',
    onCreditNoteChange,
    paymentMethod,
    paymentMethodConfig = null,
    onPaymentMethodChange,
    advancePaymentMethods = [],
    loadingAdvancePaymentMethods = false,
    paymentFields,
    paymentFiles,
    onFieldChange,
    onFileChange,
    summaryBanner = null,
    itemsLimit = null,
    totalLabel = 'Total',
  }) {
    const isCreditFlow = paymentOption === 'credit';
    const theme = useTheme();
    const items = Array.isArray(order?.items) ? order.items : [];
    const displayItems = itemsLimit != null ? items.slice(0, itemsLimit) : items;
    const totalAmount = Number(
      orderPaymentDetails?.totalAmount ?? order?.total_amount ?? order?.total ?? 0
    );
    const remainingAmount = Number(orderPaymentDetails?.remainingAmount ?? 0);
    const useApiPaymentSummary = !!orderPaymentDetails;
    const zeroRemaining = remainingAmount === 0;

    return (
      <Box>
        {summaryBanner}

        {useApiPaymentSummary ? (
          <PaymentAmountSummary
            orderPaymentDetails={summaryPaymentDetails || orderPaymentDetails}
          />
        ) : (
          <>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.75 }}>
              Order Summary
            </Typography>
            <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, borderRadius: 2 }}>
              {displayItems.length > 0 ? (
                displayItems.map((item, index) => (
                  <Box
                    key={`${item.product_sku || item.product_name}-${index}`}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      py: 0.5,
                      borderBottom:
                        index < displayItems.length - 1
                          ? `1px solid ${alpha(theme.palette.divider, 0.6)}`
                          : 'none',
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {item.product_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {item.product_sku || '—'}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ flexShrink: 0 }}>
                      {parseFloat(item.quantity || 0)} x {formatPaymentCurrency(item.unit_price || 0)}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {order?.order_id ? `Reference order: ${order.order_id}` : 'No line items available'}
                </Typography>
              )}
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Total Items: {order?.total_items ?? items.length} items
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  {totalLabel}: {formatPaymentCurrency(totalAmount)}
                </Typography>
              </Box>
            </Paper>
          </>
        )}

        {useApiPaymentSummary && isCreditFlow && (() => {
          const installmentValue = Number(installmentAmount);
          const exceedsRemaining =
            installmentAmount !== '' &&
            !Number.isNaN(installmentValue) &&
            installmentValue > remainingAmount;

          return (
          <>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Installment Amount"
              value={zeroRemaining ? '0' : installmentAmount}
              onChange={onInstallmentAmountChange}
              placeholder="Enter installment amount"
              disabled={zeroRemaining}
              sx={{ mb: 1.5 }}
              error={!zeroRemaining && exceedsRemaining}
              helperText={
                !zeroRemaining && exceedsRemaining
                  ? 'Installment amount cannot exceed the remaining amount'
                  : ''
              }
              inputProps={{ min: 0, max: remainingAmount, step: 0.01 }}
            />
            {!zeroRemaining && (
              <>
                <FormControl
                  fullWidth
                  size="small"
                  sx={{ mb: 1 }}
                  required={String(installmentAmount ?? '').trim() !== ''}
                >
                  <InputLabel id={`${idPrefix}-payment-method-select-label`}>Select Payment Method</InputLabel>
                  <Select
                    labelId={`${idPrefix}-payment-method-select-label`}
                    label="Select Payment Method"
                    value={paymentMethod}
                    onChange={onPaymentMethodChange}
                    disabled={loadingAdvancePaymentMethods}
                  >
                    <MenuItem value="">
                      <em>Select payment method</em>
                    </MenuItem>
                    {loadingAdvancePaymentMethods && (
                      <MenuItem disabled>
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                        Loading payment methods...
                      </MenuItem>
                    )}
                    {!loadingAdvancePaymentMethods && advancePaymentMethods.length === 0 && (
                      <MenuItem disabled>No payment methods available</MenuItem>
                    )}
                    {advancePaymentMethods.map((method) => (
                      <MenuItem key={`${idPrefix}-credit-${method.value}`} value={method.value}>
                        {method.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <AdminPaymentMethodDetailsSection
                  idPrefix={idPrefix}
                  formConfig={paymentMethodConfig}
                  orderPaymentDetails={orderPaymentDetails}
                  paymentFields={paymentFields}
                  paymentFiles={paymentFiles}
                  onFieldChange={onFieldChange}
                  onFileChange={onFileChange}
                />
              </>
            )}
            <Box
              sx={{
                ...(paymentMethodConfig?.fileField?.key &&
                paymentFiles[paymentMethodConfig.fileField.key]
                  ? { mt: 2 }
                  : {}),
              }}
            >
              <TextField
                fullWidth
                size="small"
                label="Note"
                value={creditNote}
                onChange={onCreditNoteChange}
                placeholder="Enter note"
                multiline
                minRows={3}
                required
                sx={{ mb: 1.5 }}
              />
            </Box>
          </>
          );
        })()}

        {useApiPaymentSummary && !isCreditFlow && (
          <TextField
            fullWidth
            size="small"
            label="Payment Amount"
            value={formatPaymentCurrency(remainingAmount)}
            disabled
            sx={{ mb: 1.5 }}
            helperText={
              zeroRemaining
                ? 'No payment required — paid amount will be 0'
                : 'Payment amount must equal the remaining amount'
            }
          />
        )}

        {!isCreditFlow && zeroRemaining && (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            Remaining amount is 0. Submit to mark the order as Paid without payment details.
          </Alert>
        )}

        {!isCreditFlow && !zeroRemaining && (
          <>
        {/* <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.75 }}>
          Account Details
        </Typography>
        <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, borderRadius: 2 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1.25,
            }}
          >
            {[
              ['Account Holder', PAYMENT_ACCOUNT_DETAILS.accountHolder],
              ['Account Number', PAYMENT_ACCOUNT_DETAILS.accountNumber],
              ['IFSC Code', PAYMENT_ACCOUNT_DETAILS.ifscCode],
              ['Email', PAYMENT_ACCOUNT_DETAILS.email],
            ].map(([label, value]) => (
              <Box key={`${idPrefix}-${label}`}>
                <Typography variant="caption" color="text.secondary" display="block">
                  {label}
                </Typography>
                <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper> */}
          </>
        )}

        {!useApiPaymentSummary && paymentOption === 'advance' && (
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Advance Amount"
            value={advanceAmount}
            onChange={onAdvanceAmountChange}
            sx={{ mb: 1.5 }}
            inputProps={{ min: 0, max: totalAmount, step: 0.01 }}
          />
        )}

        {!isCreditFlow && !zeroRemaining && (
          <FormControl fullWidth size="small" sx={{ mb: 1 }}>
            <InputLabel id={`${idPrefix}-payment-method-select-label`}>Select Payment Method</InputLabel>
            <Select
              labelId={`${idPrefix}-payment-method-select-label`}
              label="Select Payment Method"
              value={paymentMethod}
              onChange={onPaymentMethodChange}
              disabled={loadingAdvancePaymentMethods}
            >
              <MenuItem value="">
                <em>Select payment method</em>
              </MenuItem>
              {loadingAdvancePaymentMethods && (
                <MenuItem disabled>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  Loading payment methods...
                </MenuItem>
              )}
              {!loadingAdvancePaymentMethods && advancePaymentMethods.length === 0 && (
                <MenuItem disabled>No payment methods available</MenuItem>
              )}
              {advancePaymentMethods.map((method) => (
                <MenuItem key={`${idPrefix}-${method.value}`} value={method.value}>
                  {method.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {!isCreditFlow && !zeroRemaining && (
          <AdminPaymentMethodDetailsSection
            idPrefix={idPrefix}
            formConfig={enforcePaymentMethodRequiredFields(paymentMethodConfig)}
            orderPaymentDetails={orderPaymentDetails}
            paymentFields={paymentFields}
            paymentFiles={paymentFiles}
            onFieldChange={onFieldChange}
            onFileChange={onFileChange}
          />
        )}

        {useApiPaymentSummary && !isCreditFlow && (
          <TextField
            fullWidth
            size="small"
            label="Note"
            value={creditNote}
            onChange={onCreditNoteChange}
            placeholder="Enter note"
            multiline
            minRows={3}
            required
            sx={{
              mb: 1.5,
              ...(paymentMethodConfig?.fileField?.key &&
              paymentFiles[paymentMethodConfig.fileField.key]
                ? { mt: 1.5 }
                : {}),
            }}
          />
        )}
      </Box>
    );
  }

  const MAX_PAYMENT_CONFIRMATION_FILE_SIZE_BYTES = 2 * 1024 * 1024;

  function AdminPaymentMethodDetailsSection({
    idPrefix,
    formConfig = null,
    orderPaymentDetails = null,
    paymentFields = {},
    paymentFiles = {},
    onFieldChange,
    onFileChange,
  }) {
    const [paymentFileError, setPaymentFileError] = useState('');

    if (!formConfig) return null;

    const fileInputId = `${idPrefix}-${formConfig.fileField?.key || 'file'}-upload`;
    const chequeDateBounds = getChequeDateBounds();
    const remainingAmount = Number(orderPaymentDetails?.remainingAmount ?? 0);

    const renderPaymentField = (field) => {
      if (field.key === 'cheque_amount' && field.readOnlyWhenRemaining && orderPaymentDetails) {
        return (
          <TextField
            key={`${idPrefix}-${field.key}`}
            fullWidth
            size="small"
            sx={{ mb: 1 }}
            label={`${field.label} *`}
            value={
              field.key === 'cheque_amount' && Object.prototype.hasOwnProperty.call(paymentFields, 'cheque_amount')
                ? paymentFields.cheque_amount
                : paymentFields[field.key] ||
                  (remainingAmount > 0 ? String(remainingAmount) : '')
            }
            disabled
            helperText="Must equal the remaining order amount"
          />
        );
      }

      const isDateField = field.type === 'date';
      const isDecimalField = field.type === 'decimal';

      return (
        <TextField
          key={`${idPrefix}-${field.key}`}
          fullWidth
          size="small"
          sx={{ mb: 1 }}
          label={`${field.label}${field.required ? ' ' : ''}`}
          type={isDateField ? 'date' : isDecimalField ? 'text' : 'text'}
          value={paymentFields[field.key] || ''}
          onChange={(e) =>
            onFieldChange(
              field.key,
              sanitizePaymentFieldByKey(field.key, e.target.value, field.maxLength)
            )
          }
          inputProps={{
            ...(field.maxLength ? { maxLength: field.maxLength } : {}),
            ...(field.key === 'transaction_reference_no'
              ? { style: { textTransform: 'uppercase' } }
              : {}),
            ...(isDateField
              ? { min: chequeDateBounds.min, max: chequeDateBounds.max }
              : {}),
            ...(isDecimalField ? { inputMode: 'decimal' } : {}),
            ...(field.key === 'cheque_number' ? { inputMode: 'numeric' } : {}),
          }}
          helperText={
            field.maxLength
              ? `${String(paymentFields[field.key] || '').length}/${field.maxLength}`
              : field.key === 'transaction_reference_no'
                ? 'Alphanumeric (A–Z, 0–9) only'
                : undefined
          }
          FormHelperTextProps={{ sx: { textAlign: 'right', mr: 0 } }}
          InputLabelProps={isDateField ? { shrink: true } : undefined}
          required={field.required}
        />
      );
    };

    return (
      <Paper variant="outlined" sx={{ p: 1.5, mt: 1, borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
          {formConfig.title}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.4 }}>
          (*) marked fields are mandatory
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mb: 1.25, lineHeight: 1.4, fontStyle: 'italic' }}
        >
          (*) Only English numbers and alphabets are allowed, please fill the fields accordingly
        </Typography>

        {formConfig.fields.map((field) => renderPaymentField(field))}

        {formConfig.fileField && (
          <Box sx={{ mt: 0.5 }}>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 0.75 }}>
              {formConfig.fileField.label}
              {formConfig.fileField.required ? ' *' : ''}
            </Typography>
            <input
              type="file"
              accept={formConfig.fileField.accept || 'image/*,.pdf,application/pdf'}
              style={{ display: 'none' }}
              id={fileInputId}
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                e.target.value = '';

                if (!file) {
                  setPaymentFileError('');
                  onFileChange(formConfig.fileField.key, null);
                  return;
                }

                const isImageOrPdf =
                  file.type.startsWith('image/') ||
                  file.type === 'application/pdf' ||
                  /\.(pdf|jpe?g|png|gif|webp|bmp)$/i.test(file.name || '');

                if (!isImageOrPdf) {
                  setPaymentFileError('Only image or PDF files are allowed.');
                  onFileChange(formConfig.fileField.key, null);
                  return;
                }

                if (file.size > MAX_PAYMENT_CONFIRMATION_FILE_SIZE_BYTES) {
                  setPaymentFileError('File size must not exceed 2 MB. Please choose a smaller image or PDF.');
                  onFileChange(formConfig.fileField.key, null);
                  return;
                }

                setPaymentFileError('');
                onFileChange(formConfig.fileField.key, file);
              }}
            />
            <Button
              variant="contained"
              component="label"
              htmlFor={fileInputId}
              sx={{ textTransform: 'none' }}
            >
              Choose file from gallery / device
            </Button>
            {paymentFileError && (
              <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                {paymentFileError}
              </Typography>
            )}
            {paymentFiles[formConfig.fileField.key] && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Selected: {paymentFiles[formConfig.fileField.key].name}
              </Typography>
            )}
          </Box>
        )}
      </Paper>
    );
  }

  const closePaymentUpdateModalState = () => ({ ...DEFAULT_PAYMENT_UPDATE_MODAL });

  const openPaymentUpdateModalState = (order) => {
    const paymentStatus = getOrderPaymentStatusValue(order);
    const orderDetails = getOrderPaymentDetailsFromOrder(order);
    const base = {
      ...DEFAULT_PAYMENT_UPDATE_MODAL,
      open: true,
      order,
      initialPaymentStatus: paymentStatus,
      refundAmount: order?.refunded_amount ?? order?.refund_amount ?? order?.total_amount ?? '',
    };

    if (paymentStatus === 'credit') {
      return {
        ...base,
        orderPaymentDetails: orderDetails,
        ...getCreditPaymentStepState(orderDetails),
      };
    }

    return base;
  };

  const DEFAULT_BULK_PAYMENT_STATUS_MODAL = {
    open: false,
    selectedPaymentStatus: '',
    initialPaymentStatus: '',
    orderPaymentDetails: null,
    error: '',
    isUpdating: false,
    step: 'status',
    paymentOption: '',
    advanceAmount: '',
    paymentMethod: '',
    paymentMethodConfig: null,
    paymentFields: {},
    paymentFiles: {},
    paymentError: '',
    summaryOrder: null,
    installmentAmount: '',
    creditNote: '',
    refundAmount: '',
  };

  const ProductList = ({ 
    data: products = [], 
    searchQuery = '', 
    statusFilter = 'all', 
    isLoading = false, 
    onRefresh,
    selectedProducts = [],
    onProductSelect,
    onSelectAll,
    isAllSelected = false,
    onExport,
    onZipDownload,
    showCancelRequestActions = false,
    onApproveCancelRequest,
    onRejectCancelRequest,
    updatingCancelRequestOrderId = null,
    showReturnRequestActions = false,
    onApproveReturnRequest,
    onRejectReturnRequest,
    updatingReturnRequestOrderId = null,
    pagination = null,
    currentPage = 1,
    pageSize = 10,
    onPageChange = null,
    onPageSizeChange = null,
    shouldEnableButton,
    listFilters = null,
  }) => {
    const toast = useToast()

    const refreshOrdersAfterPaymentUpdate = async () => {
      await onRefresh?.();
    }

    const [statusUpdateModal, setStatusUpdateModal] = useState({ 
      open: false, 
      order: null,
      entityName: '',
      orderBy: '',
      driverName: '',
      vehicleNo: '',
      vehicleType: '',
      delivery_date: new Date().toISOString().slice(0,10),
      shippedDate: new Date().toISOString().slice(0, 10),
      generateInvoice: false,
      isCreatingChallan: false,
      challanError: '',
      dispatchPersonName: '',
      customDispatchPerson: '',
      useCustomDispatchPerson: false,
      start_km: '',
      end_km: '',
      diesel_receipt_upload: null,
      diesel_receipt_upload_error: '',
      ...DEFAULT_DISPATCH_LOGISTICS,
      ...DEFAULT_DELIVERED_LOGISTICS,
      cancellationReason: '',
      returnReason: '',
      /** When true (Placed tab only), unpaid orders may submit Cancelled without paying first. */
      allowUnpaidCancelFromPlaced: false,
    })
    const [entityOptions, setEntityOptions] = useState([])
    const [loadingEntities, setLoadingEntities] = useState(false)
    const [orderByChoices, setOrderByChoices] = useState([])
    const [paymentUpdateModal, setPaymentUpdateModal] = useState(() => ({ ...DEFAULT_PAYMENT_UPDATE_MODAL }))
    const [invoiceModal, setInvoiceModal] = useState({ open: false, invoice: null, order: null })
    const [deliveryChallanModal, setDeliveryChallanModal] = useState({ open: false, challan: null, order: null })
    const [isDownloading, setIsDownloading] = useState(false);
    const [bulkChallanModal, setBulkChallanModal] = useState({
      open: false,
      orderBy: '',
      entityName: '',
      driverName: '',
      vehicleNo: '',
      vehicleType: '',
      deliveryDate: new Date().toISOString().slice(0,10),
      generateInvoice: false,
      isCreating: false,
      error: '',
      selectedStatus: '',
      selectedStatusCategory: '',
      emails: [],
      emailInput: '',
      dispatchPersonName: '',
      customDispatchPerson: '',
      useCustomDispatchPerson: false,
      start_km: '',
      end_km: '',
      ...DEFAULT_DISPATCH_LOGISTICS,
      ...DEFAULT_DELIVERED_LOGISTICS,
      bulkPaymentStatus: '',
      cancellationReason: '',
      returnReason: '',
      /** Placed tab only: unpaid selected orders may bulk-update to Cancelled without paying first. */
      allowUnpaidBulkCancelFromPlaced: false,
    })
    const [bulkPaymentStatusModal, setBulkPaymentStatusModal] = useState(() => ({
      ...DEFAULT_BULK_PAYMENT_STATUS_MODAL,
    }))
    const [bulkEntityOptions, setBulkEntityOptions] = useState([])
    const [loadingBulkEntities, setLoadingBulkEntities] = useState(false)
    const [updatingStatus, setUpdatingStatus] = useState(false)
    const [updatingPayment, setUpdatingPayment] = useState(false)
    const [isUpdatingBulkPayment, setIsUpdatingBulkPayment] = useState(false)
    const [downloadingInvoice, setDownloadingInvoice] = useState(false)
    const [downloadingChallan, setDownloadingChallan] = useState(false)
    const [checkingPaymentOrderId, setCheckingPaymentOrderId] = useState(null)
    const navigate = useNavigate()
    const theme = useTheme()

    const [statusOptions, setStatusOptions] = useState([])
    const [paymentStatusChoices, setPaymentStatusChoices] = useState([])
    const [loadingPaymentStatusChoices, setLoadingPaymentStatusChoices] = useState(false)
    const [advancePaymentMethods, setAdvancePaymentMethods] = useState([])
    const [loadingAdvancePaymentMethods, setLoadingAdvancePaymentMethods] = useState(false)
    const paymentMethodsById = React.useMemo(
      () => buildPaymentMethodsById(advancePaymentMethods),
      [advancePaymentMethods]
    )
    const [dispatchPersons, setDispatchPersons] = useState([])
    const [vehicleTypes, setVehicleTypes] = useState([])
    const [actionsAnchorEl, setActionsAnchorEl] = useState(null)
    const isActionsMenuOpen = Boolean(actionsAnchorEl)
    const openActionsMenu = (e) => setActionsAnchorEl(e.currentTarget)
    const closeActionsMenu = () => setActionsAnchorEl(null)
    const [emailDialog, setEmailDialog] = useState({
      open: false,
      emailInput: '',
      emails: [],
      sendInvoice: true,
      sendDelivery: true,
      isSending: false,
      error: '',
      success: ''
    })
    const [singleEmailDialog, setSingleEmailDialog] = useState({
      open: false,
      order: null,
      emailInput: '',
      emails: [],
      sendInvoice: true,
      sendDelivery: true,
      isSending: false,
      error: '',
      success: ''
    })

    const extractInvoice = (order) => {
      if (!order) return null;
      const inv = order.invoice;
      if (inv && typeof inv === 'object') {
        const path = inv.path || inv.url || inv.file || inv.link;
        if (path) return { ...inv, path };
      }
      const path = order.invoice_path || order.invoice_url || (typeof inv === 'string' ? inv : null);
      if (path) return { id: inv?.id || order.id, path };
      return null;
    };

    const extractDeliveryChallan = (order) => {
      if (!order) return null;
      const dc = order.delivery_challan || order.deliveryChallan || order.challan;
      if (dc && typeof dc === 'object') {
        const path = dc.path || dc.url || dc.file || dc.link;
        if (path) return { ...dc, path };
      }
      const path = order.delivery_challan_path || order.delivery_challan_url || order.challan_url || (typeof dc === 'string' ? dc : null);
      if (path) return { id: dc?.id || order.id, path };
      return null;
    };

    const getIconForCategory = (category) => {
      const key = (typeof category === 'string' ? category : (category?.name ?? '')).toLowerCase()
      switch (key) {
        case 'pending': return <Schedule />
        case 'confirmed': return <CheckCircle />
        case 'processing': return <Schedule />
        case 'shipped': return <LocalShippingOutlined />
        case 'delivered': return <CheckCircle />
        case 'cancelled': return <Cancel />
        case 'returned': return <Refresh />
        default: return null
      }
    }

    useEffect(() => {
      const fetchOrderStatuses = async () => {
        try {
          const res = await apiService.get(API_ENDPOINTS.GET_order_status_choices)
          const list = Array.isArray(res?.data) ? res.data : []
          const options = list
            .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
            .map(s => ({
              id: s.id,
              value: s.name || '',
              name: s.name || '',
              label: s.name || '',
              category: s.category || s.name || '',
              sequence: s.sequence || 0
            }))
          setStatusOptions(options)
        } catch (e) {
          console.error('Error fetching order status choices:', e)
        }
      }
      fetchOrderStatuses()
    }, [])

    useEffect(() => {
      const fetchDispatchPersons = async () => {
        try {
          const res = await apiService.get('e-commerce/admin-order/get-dispatch-persons/')
          const list = Array.isArray(res?.data) ? res.data : []
          const mappedList = list
            .map((person, index) => ({
              id: person.value || person.id || index,
              name: person.label || person.value || person.name || '',
            }))
            .filter((person) => person.name)
          setDispatchPersons(mappedList)
        } catch (e) {
          console.error('Error fetching dispatch persons:', e)
        }
      }
      fetchDispatchPersons()
    }, [])

    useEffect(() => {
      const fetchVehicleTypes = async () => {
        try {
          const res = await apiService.get('e-commerce/admin-order/get-vehicle-types/')
          const list = Array.isArray(res?.data) ? res.data : []
          const mappedList = list
            .map((item, index) => ({
              id: item.value || item.id || index,
              value: item.value || '',
              label: item.label || item.value || '',
            }))
            .filter((item) => item.value)
          setVehicleTypes(mappedList)
        } catch (e) {
          console.error('Error fetching vehicle types:', e)
        }
      }
      fetchVehicleTypes()
    }, [])

    useEffect(() => {
      const fetchOrderByChoices = async () => {
        try {
          const res = await apiService.get(API_ENDPOINTS.GET_order_by_choices)
          if (res?.success && Array.isArray(res?.data)) {
            setOrderByChoices(res.data)
          } else {
            console.error('Invalid response format from order by choices API')
            setOrderByChoices([])
          }
        } catch (e) {
          console.error('Error fetching order by choices:', e)
          setOrderByChoices([])
        }
      }
      fetchOrderByChoices()
    }, [])

    const applyOrderPaymentDetails = (orderId, orderDetails) => {
      const currentStatus = orderDetails?.current_payment_status
        ? String(orderDetails.current_payment_status).toLowerCase()
        : null
      const remaining = Number(orderDetails?.remainingAmount ?? 0)
      const paidStepPatch = getPaidPaymentStepState(orderDetails)

      setPaymentUpdateModal((prev) => {
        if (!prev.open || prev.order?.order_id !== orderId) return prev
        return {
          ...prev,
          orderPaymentDetails: orderDetails,
          ...(currentStatus
            ? {
                order: {
                  ...prev.order,
                  payment_status: {
                    ...(typeof prev.order?.payment_status === 'object'
                      ? prev.order.payment_status
                      : {}),
                    name: currentStatus,
                  },
                },
              }
            : {}),
          ...(currentStatus === 'credit' || prev.paymentOption === 'credit'
            ? { ...getCreditPaymentStepState(orderDetails), step: 'status' }
            : prev.paymentOption === 'remaining'
              ? { ...paidStepPatch, step: 'status' }
              : {}),
        }
      })

      setBulkPaymentStatusModal((prev) => {
        if (!prev.open || prev.summaryOrder?.order_id !== orderId) return prev
        return {
          ...prev,
          orderPaymentDetails: orderDetails,
          ...(currentStatus === 'credit' ||
          prev.paymentOption === 'credit' ||
          prev.selectedPaymentStatus === 'credit'
            ? { ...getCreditPaymentStepState(orderDetails), step: 'status' }
            : prev.paymentOption === 'remaining' || prev.selectedPaymentStatus === 'paid'
              ? { ...paidStepPatch, step: 'status' }
              : prev.step === 'payment_details'
                ? {
                    ...paidStepPatch,
                    selectedPaymentStatus: prev.selectedPaymentStatus || 'paid',
                  }
                : {}),
        }
      })
    }

    const fetchPaymentStatusChoicesForOrder = async (orderId) => {
      if (!orderId) {
        setPaymentStatusChoices([])
        return
      }
      setLoadingPaymentStatusChoices(true)
      try {
        const res = await apiService.get(
          `${API_ENDPOINTS.GET_payment_status_choices}?order_id=${encodeURIComponent(orderId)}`
        )
        if (res?.success && Array.isArray(res?.data)) {
          setPaymentStatusChoices(
            [...res.data].sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
          )
          applyOrderPaymentDetails(orderId, res?.order_details || null)
        } else {
          console.error('Invalid response format from payment status choices API')
          setPaymentStatusChoices([])
          applyOrderPaymentDetails(orderId, null)
        }
      } catch (e) {
        console.error('Error fetching payment status choices:', e)
        setPaymentStatusChoices([])
        applyOrderPaymentDetails(orderId, null)
      } finally {
        setLoadingPaymentStatusChoices(false)
      }
    }

    const isPaymentUpdateDisabled = (order) => {
      const paymentStatus = getOrderPaymentStatusValue(order);
      if (paymentStatus === 'paid') return true;
      if (paymentStatus === 'credit') return false;
      return isPaymentUpdateDisabledForOrder(order, statusFilter);
    };

    const getPaymentUpdateTooltip = (order) => {
      if (!shouldEnableButton?.('update_payment')) {
        return "You don't have permission to update payment";
      }
      const paymentStatus = getOrderPaymentStatusValue(order);
      if (paymentStatus === 'paid') {
        return 'Payment status cannot be updated when the order is already paid';
      }
      if (paymentStatus === 'credit') {
        return 'Update Payment';
      }
      const reason = getPaymentUpdateDisabledReason(order, statusFilter);
      if (reason) return reason;
      return 'Update Payment';
    };

    const isBulkPaymentUpdateDisabled = () => {
      if (PAYMENT_UPDATE_LOCKED_ORDER_STATUSES.has(normalizeStatusKey(statusFilter))) {
        return true;
      }
      const selected = filteredData.filter((order) => selectedProducts.includes(order.id));
      if (selected.length === 0) return true;
      return selected.every((order) => isPaymentUpdateDisabled(order));
    };

    const loadAdvancePaymentMethods = async () => {
      setLoadingAdvancePaymentMethods(true);
      try {
        const methods = await fetchAdvancePaymentMethods();
        setAdvancePaymentMethods(methods);
      } catch (error) {
        console.error('Error fetching advance payment methods:', error);
        setAdvancePaymentMethods([]);
      } finally {
        setLoadingAdvancePaymentMethods(false);
      }
    };

    const buildPaymentMethodChangePayload = (methodId, orderPaymentDetails) => {
      const config = paymentMethodsById[methodId] || null;
      const remaining = Number(orderPaymentDetails?.remainingAmount ?? 0);
      return {
        paymentMethod: methodId,
        paymentMethodConfig: config,
        paymentFields:
          config?.type === 'cheque' && remaining > 0
            ? { cheque_amount: String(remaining) }
            : {},
        paymentFiles: {},
        paymentError: '',
      };
    };

    const openPaymentModalForOrder = (order) => {
      if (!order || isPaymentUpdateDisabled(order)) return;
      setPaymentUpdateModal(openPaymentUpdateModalState(order));
      fetchPaymentStatusChoicesForOrder(order.order_id);
      loadAdvancePaymentMethods();
    };

    const getPaymentChoiceValue = (choice) =>
      String(choice.value || choice.key || choice.name || '').toLowerCase()

    const getPaymentChoiceLabel = (choice) =>
      choice.label || choice.name || choice.value || choice.key || ''

    const renderPaymentStatusMenuItems = ({ hidePaid = false } = {}) => {
      const shouldHidePaid = (value) =>
        hidePaid && String(value || '').toLowerCase() === 'paid'

      if (loadingPaymentStatusChoices) {
        return (
          <MenuItem disabled>
            <CircularProgress size={18} sx={{ mr: 1 }} />
            Loading payment statuses...
          </MenuItem>
        )
      }
      if (paymentStatusChoices.length > 0) {
        return [...paymentStatusChoices]
          .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
          .filter((choice) => !shouldHidePaid(getPaymentChoiceValue(choice)))
          .map((choice) => {
          const value = getPaymentChoiceValue(choice)
          const label = getPaymentChoiceLabel(choice)
          return (
            <MenuItem key={value || label} value={value}>
              <PaymentStatusChip
                label={label}
                status={value}
                size="small"
                sx={{ mr: 1 }}
              />
              {label}
            </MenuItem>
          )
        })
      }
      return paymentStatusOptions
        .filter((option) => !shouldHidePaid(option.value))
        .map((option) => (
        <MenuItem key={option.value} value={option.value}>
          <PaymentStatusChip
            label={option.label}
            status={option.value}
            size="small"
            sx={{ mr: 1 }}
          />
          {option.label}
        </MenuItem>
      ))
    }

    const paymentStatusOptions = [
      { value: 'pending', label: 'Pending', icon: <Schedule /> },
      { value: 'outstanding', label: 'Outstanding', icon: <Schedule /> },
      { value: 'paid', label: 'Paid', icon: <CheckCircle /> },
      { value: 'failed', label: 'Failed', icon: <Cancel /> },
      { value: 'refunded', label: 'Refunded', icon: <Refresh /> },
    ]

    const getAvailableCounts = () => {
      const selected = filteredData.filter(order => selectedProducts.includes(order.id))
      const tab = (statusFilter || '').toLowerCase()
      const useSelectedOrdersAsCounts = ['dispatch', 'shipped', 'delivered', 'returned', 'cancelled'].includes(tab)
      const invoices = useSelectedOrdersAsCounts
        ? selectedProducts.length
        : selected.filter(order => !!extractInvoice(order)?.path).length
      const challans = useSelectedOrdersAsCounts
        ? selectedProducts.length
        : selected.filter(order => !!extractDeliveryChallan(order)?.path).length
      return { invoices, challans }
    }

    const resolveSelectedOrderIdsForEmail = async () => {
      const selectedKeySet = new Set(selectedProducts.map((id) => String(id)))
      const fromCurrentPage = filteredData
        .filter((order) => {
          const idKey = order?.id != null ? String(order.id) : null
          const orderKey = order?.order_id != null ? String(order.order_id) : null
          return (idKey && selectedKeySet.has(idKey)) || (orderKey && selectedKeySet.has(orderKey))
        })
        .map((order) => order.order_id)
        .filter(Boolean)

      if (fromCurrentPage.length >= selectedProducts.length) {
        return [...new Set(fromCurrentPage)]
      }

      const userId = localStorage.getItem('user_id')
      const statusParam =
        !statusFilter || statusFilter === 'all'
          ? ''
          : `&status=${encodeURIComponent(statusFilter)}`
      let url = `${API_ENDPOINTS.GET_products}?user_id=${userId}${statusParam}&type=normal`
      const fromDate = toStoredAppOrderDate(listFilters?.startDate)
      const toDate = toStoredAppOrderDate(listFilters?.endDate)
      if (fromDate) url += `&from_date=${fromDate}`
      if (toDate) url += `&to_date=${toDate}`
      if (searchQuery && String(searchQuery).trim()) {
        url += `&search=${encodeURIComponent(String(searchQuery).trim())}`
      }
      const companyToUse = listFilters?.selectedCompany
      if (companyToUse && companyToUse !== 'all') {
        url += `&company=${companyToUse}`
      }
      const totalItems = Number(pagination?.total_items) || selectedProducts.length || filteredData.length || 1
      url += `&page=1&page_size=${Math.max(totalItems, 1)}`

      const response = await apiService.get(url)
      const allRows = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.results)
          ? response.results
          : []

      return [
        ...new Set(
          allRows
            .filter((product) => {
              const idKey = product?.id != null ? String(product.id) : null
              const orderKey = product?.order_id != null ? String(product.order_id) : null
              return (idKey && selectedKeySet.has(idKey)) || (orderKey && selectedKeySet.has(orderKey))
            })
            .map((product) => product.order_id)
            .filter(Boolean)
        ),
      ]
    }

    const shouldDisableDocumentsAndEmail = () => {
      const selected = filteredData.filter(order => selectedProducts.includes(order.id))
      const restrictedStatuses = ['Placed', 'Confirmed', 'Processing']
      return selected.some(order => {
        const statusName = order.status?.name || order.status_name || ''
        return restrictedStatuses.includes(statusName)
      })
    }

    const handleOpenEmailDialog = () => {
      setEmailDialog({
        open: true,
        emailInput: '',
        emails: [],
        sendInvoice: true,
        sendDelivery: true,
        isSending: false,
        error: '',
        success: ''
      })
      closeActionsMenu()
    }

    const handleCloseEmailDialog = () => {
      setEmailDialog({
        open: false,
        emailInput: '',
        emails: [],
        sendInvoice: true,
        sendDelivery: true,
        isSending: false,
        error: '',
        success: ''
      })
    }

    const handleEmailDialogChange = (field, value) => {
      setEmailDialog(prev => ({ ...prev, [field]: value, error: '', success: '' }))
    }

    const isValidBulkEmailInput = (value) => {
      const input = String(value || '').trim()
      if (!input) return false
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input)
    }

    const addEmailToBulkDialog = () => {
      const input = (emailDialog.emailInput || '').trim()
      if (!input) return
      if (!isValidBulkEmailInput(input)) {
        setEmailDialog(prev => ({ ...prev, error: 'Enter a valid email address' }))
        return
      }
      if (emailDialog.emails.includes(input)) {
        setEmailDialog(prev => ({ ...prev, error: 'Email already added' }))
        return
      }
      setEmailDialog(prev => ({
        ...prev,
        emails: [...prev.emails, input],
        emailInput: '',
        error: ''
      }))
    }

    const removeEmailFromBulkDialog = (email) => {
      setEmailDialog(prev => ({
        ...prev,
        emails: prev.emails.filter((item) => item !== email),
        error: ''
      }))
    }

    const handleEmailSendSubmit = async () => {
      const { emails, emailInput, sendInvoice, sendDelivery } = emailDialog
      const userId = localStorage.getItem('user_id')

      const emailsToSend = [...emails]
      const typedEmail = String(emailInput || '').trim()
      if (typedEmail) {
        if (!isValidBulkEmailInput(typedEmail)) {
          setEmailDialog(prev => ({ ...prev, error: 'Enter a valid email address' }))
          return
        }
        if (!emailsToSend.includes(typedEmail)) {
          emailsToSend.push(typedEmail)
        }
      }
      if (emailsToSend.length === 0) {
        setEmailDialog(prev => ({ ...prev, error: 'Please add at least one email address' }))
        return
      }
      if (!sendInvoice && !sendDelivery) {
        setEmailDialog(prev => ({ ...prev, error: 'Please select at least one document type to send' }))
        return
      }
      const { invoices, challans } = getAvailableCounts()
      if ((sendInvoice && invoices === 0) && (sendDelivery && challans === 0)) {
        setEmailDialog(prev => ({ ...prev, error: 'No available documents to send for selected orders' }))
        return
      }

      setEmailDialog(prev => ({ ...prev, isSending: true, error: '', success: '' }))
      try {
        const selectedOrderIds = await resolveSelectedOrderIdsForEmail()
        if (!selectedOrderIds.length) {
          throw new Error('Selected orders do not have valid order IDs')
        }

        const payload = {
          emails: emailsToSend.join(','),
          order_id: selectedOrderIds.join(','),
          user_id: userId,
          invoice: !!sendInvoice,
          delivery: !!sendDelivery
        }

        const res = await apiService.post(
          API_ENDPOINTS.POST_send_challan_on_email,
          JSON.stringify(payload),
          'application/json'
        )

        if (res?.success) {
          setEmailDialog(prev => ({ ...prev, success: 'Email sent successfully', isSending: false }))
          setTimeout(() => handleCloseEmailDialog(), 1200)
        } else {
          throw new Error(res?.message || 'Failed to send email')
        }
      } catch (err) {
        setEmailDialog(prev => ({ ...prev, error: err?.message || 'Failed to send email', isSending: false }))
      }
    }

    const openSingleOrderEmail = (order) => {
      const hasInvoice = !!extractInvoice(order)?.path
      const hasChallan = !!extractDeliveryChallan(order)?.path
      const isDispatchTab = (statusFilter || '').toLowerCase() === 'dispatch'
      setSingleEmailDialog({
        open: true,
        order: order,
        emailInput: '',
        emails: [],
        sendInvoice: isDispatchTab ? false : hasInvoice,
        sendDelivery: hasChallan,
        isSending: false,
        error: '',
        success: ''
      })
    }

    const closeSingleOrderEmail = () => {
      setSingleEmailDialog({
        open: false,
        order: null,
        emailInput: '',
        emails: [],
        sendInvoice: true,
        sendDelivery: true,
        isSending: false,
        error: '',
        success: ''
      })
    }

    const addEmailToSingle = () => {
      const input = (singleEmailDialog.emailInput || '').trim()
      if (!input) return
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(input)) {
        setSingleEmailDialog(prev => ({ ...prev, error: 'Enter a valid email address' }))
        return
      }
      if (singleEmailDialog.emails.includes(input)) {
        setSingleEmailDialog(prev => ({ ...prev, error: 'Email already added' }))
        return
      }
      setSingleEmailDialog(prev => ({ ...prev, emails: [...prev.emails, input], emailInput: '', error: '' }))
    }

    const removeEmailFromSingle = (email) => {
      setSingleEmailDialog(prev => ({ ...prev, emails: prev.emails.filter(e => e !== email) }))
    }

    const isValidSingleEmailInput = (value) => {
      const input = String(value || '').trim()
      if (!input) return false
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input)
    }

    const submitSingleOrderEmail = async () => {
      const { order, emails, emailInput, sendInvoice, sendDelivery } = singleEmailDialog
      if (!order) return
      const isDispatchTab = (statusFilter || '').toLowerCase() === 'dispatch'
      const effectiveSendInvoice = isDispatchTab ? false : sendInvoice
      const emailsToSend = [...emails]
      const typedEmail = String(emailInput || '').trim()
      if (typedEmail) {
        if (!isValidSingleEmailInput(typedEmail)) {
          setSingleEmailDialog(prev => ({ ...prev, error: 'Enter a valid email address' }))
          return
        }
        if (!emailsToSend.includes(typedEmail)) {
          emailsToSend.push(typedEmail)
        }
      }
      if (emailsToSend.length === 0) {
        setSingleEmailDialog(prev => ({ ...prev, error: 'Please add at least one email' }))
        return
      }
      if (!effectiveSendInvoice && !sendDelivery) {
        setSingleEmailDialog(prev => ({ ...prev, error: 'Please select at least one document type' }))
        return
      }
      const hasInvoice = !!extractInvoice(order)?.path
      const hasChallan = !!extractDeliveryChallan(order)?.path
      if ((effectiveSendInvoice && !hasInvoice) && (sendDelivery && !hasChallan)) {
        setSingleEmailDialog(prev => ({ ...prev, error: 'Selected documents are not available for this order' }))
        return
      }

      setSingleEmailDialog(prev => ({ ...prev, isSending: true, error: '', success: '' }))
      try {
        const userId = localStorage.getItem('user_id')
        const payload = {
          emails: emailsToSend.join(','),
          order_id: order.order_id,
          user_id: userId,
          invoice: !!effectiveSendInvoice,
          delivery: !!sendDelivery
        }
        const res = await apiService.post(
          API_ENDPOINTS.POST_send_challan_on_email,
          JSON.stringify(payload),
          'application/json'
        )
        if (res?.success) {
          setSingleEmailDialog(prev => ({ ...prev, success: 'Email sent successfully', isSending: false }))
          setTimeout(() => closeSingleOrderEmail(), 1200)
        } else {
          throw new Error(res?.message || 'Failed to send email')
        }
      } catch (err) {
        setSingleEmailDialog(prev => ({ ...prev, error: err?.message || 'Failed to send email', isSending: false }))
      }
    }

    const handleViewInvoice = (order) => {
      const inv = extractInvoice(order)
      if (inv?.path) {
        window.open(inv.path, '_blank')
      }
    }

    const handleDownloadInvoice = async (invoice, orderName) => {
      if (!invoice || !invoice.path) {
        alert('Invoice not available for download')
        return
      }

      setDownloadingInvoice(true)
      try {
        const link = document.createElement('a')
        link.href = invoice.path
        link.download = `Invoice_${invoice.id}_${orderName.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`
        link.target = '_blank'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (error) {
        console.error('Error downloading invoice:', error)
        alert('Error downloading invoice. Please try again.')
      } finally {
        setDownloadingInvoice(false)
      }
    }

    const handleCloseInvoiceModal = () => {
      setInvoiceModal({ open: false, invoice: null, order: null })
    }

    const handleViewDeliveryChallan = (order) => {
      const dc = extractDeliveryChallan(order)
      if (dc?.path) {
        window.open(dc.path, '_blank')
      }
    }

    const handleCheckPaymentStatus = async (order) => {
      const orderId = order?.order_id ?? order?.id
      if (!orderId) return
      const userId = localStorage.getItem('user_id') || ''
      setCheckingPaymentOrderId(orderId)
      try {
        const url = `${API_ENDPOINTS.Refund_button_click}?user_id=${encodeURIComponent(userId)}&order_id=${encodeURIComponent(orderId)}`
        const response = await apiService.get(url)
        if (response?.success && onRefresh) {
          await onRefresh()
        } else if (!response?.success) {
          alert(response?.message || 'Check payment status failed.')
        }
      } catch (error) {
        console.error('Check payment status error:', error)
        alert(error?.message || 'Check payment status failed.')
      } finally {
        setCheckingPaymentOrderId(null)
      }
    }

    const handleDownloadDeliveryChallan = async (challan, orderName) => {
      if (!challan || !challan.path) {
        alert('Delivery challan not available for download')
        return
      }

      setDownloadingChallan(true)
      try {
        const link = document.createElement('a')
        link.href = challan.path
        link.download = `Delivery_Challan_${challan.id}_${orderName.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`
        link.target = '_blank'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (error) {
        console.error('Error downloading delivery challan:', error)
        alert('Error downloading delivery challan. Please try again.')
      } finally {
        setDownloadingChallan(false)
      }
    }

    const handleCloseDeliveryChallanModal = () => {
      setDeliveryChallanModal({ open: false, challan: null, order: null })
    }

    const handleBulkDeliveryChallan = () => {
      const selectedOrders = filteredData.filter(order => selectedProducts.includes(order.id));
      const statusOrder = getStatusOrder();
      
      let minStatusOrder = 999;
      let minStatusCategory = '';
      
      selectedOrders.forEach(order => {
        const orderStatusCategory = (order.status?.category || '').toLowerCase();
        const orderStatusName = order.status?.name || '';
        
        let orderCategory = orderStatusCategory;
        if (!orderCategory && orderStatusName) {
          const orderStatusOption = statusOptions.find(opt => opt.name === orderStatusName);
          orderCategory = orderStatusOption?.category?.toLowerCase() || '';
        }
        
        const orderSequence = statusOrder[orderCategory] || 0;
        if (orderSequence > 0 && orderSequence < minStatusOrder) {
          minStatusOrder = orderSequence;
          minStatusCategory = orderCategory;
        }
      });
      
      let defaultStatus = null;
      
      if (statusFilter === 'Dispatch') {
        defaultStatus = statusOptions.find(status => status.name === 'Delivered') || 
                        statusOptions.find(status => status.name === 'Returned') ||
                        statusOptions.find(status => status.name === 'Cancelled') ||
                        (statusOptions.length > 0 ? statusOptions[0] : null);
      } else if (statusFilter === 'Delivered') {
        // Keep bulk flow aligned with single update: Delivered -> Returned only.
        defaultStatus = statusOptions.find(status => status.name === 'Returned') || null;
      } else if (minStatusOrder !== 999 && minStatusOrder > 0) {
        const nextSequence = minStatusOrder + 1;
        defaultStatus = statusOptions.find(status => {
          const statusCategory = (status.category || '').toLowerCase();
          return statusOrder[statusCategory] === nextSequence;
        });
        
        if (!defaultStatus) {
          defaultStatus = statusOptions.find(status => {
            const statusCategory = (status.category || '').toLowerCase();
            const statusSeq = statusOrder[statusCategory] || 0;
            return statusSeq > minStatusOrder && statusSeq <= minStatusOrder + 1;
          });
        }
        
        if (!defaultStatus) {
          defaultStatus = statusOptions.length > 0 ? statusOptions[0] : null;
        }
      } else {
        defaultStatus = statusOptions.length > 0 ? statusOptions[0] : null;
      }

      const isPlacedTab = (statusFilter || '').toLowerCase() === 'placed';
      const cancelledOption = statusOptions.find(
        (s) =>
          (s.name || '').toLowerCase() === 'cancelled' ||
          (s.category || '').toLowerCase() === 'cancelled'
      );
      let allowUnpaidBulkCancelFromPlaced = false;
      let bulkCancellationReason = '';
      if (isPlacedTab && cancelledOption) {
        defaultStatus = cancelledOption;
        allowUnpaidBulkCancelFromPlaced = true;
        bulkCancellationReason = '';
      }

      const selectedOrderWithStartKm = selectedOrders.find((order) => {
        const startKm = order.delivery_challan?.start_km;
        return startKm !== null && startKm !== undefined && String(startKm).trim() !== '';
      });
      const selectedDeliveryChallan = selectedOrderWithStartKm?.delivery_challan;
      
      setBulkChallanModal({
        open: true,
        orderBy: '',
        entityName: '',
        driverName: selectedDeliveryChallan?.driver_name || '',
        vehicleNo:
          selectedDeliveryChallan?.vehicle_number ||
          selectedDeliveryChallan?.vehicle_no ||
          selectedDeliveryChallan?.vehicle_name ||
          '',
        vehicleType: '',
        deliveryDate: new Date().toISOString().slice(0,10),
        generateInvoice: false,
        isCreating: false,
        error: '',
        emails: [],
        emailInput: '',
        selectedStatus: defaultStatus?.name || '',
        selectedStatusCategory: defaultStatus?.category || '',
        dispatchPersonName: '',
        customDispatchPerson: '',
        useCustomDispatchPerson: false,
        start_km:
          selectedDeliveryChallan?.start_km !== null && selectedDeliveryChallan?.start_km !== undefined
            ? String(selectedDeliveryChallan.start_km)
            : '',
        end_km: '',
        ...DEFAULT_DISPATCH_LOGISTICS,
        ...DEFAULT_DELIVERED_LOGISTICS,
        ...getChallanLogisticsDefaults(selectedDeliveryChallan),
        diesel_receipt_upload: null,
        diesel_receipt_upload_error: '',
        cancellationReason: bulkCancellationReason,
        returnReason: '',
        allowUnpaidBulkCancelFromPlaced,
      })
    }

    const createBulkDeliveryChallan = async (orderIds, driverName, vehicleNo) => {
      try {
        const dispatchPersonName = bulkChallanModal.useCustomDispatchPerson 
          ? bulkChallanModal.customDispatchPerson 
          : bulkChallanModal.dispatchPersonName;
        const meterNotWorking = isTruthyFlag(bulkChallanModal.meter_not_working);

        const payload = {
          order_id: orderIds.join(','),
          driver_name: driverName,
          vehicle_no: vehicleNo || '',
          vehicle_type: resolveVehicleTypeForPayload(
            bulkChallanModal.orderBy,
            bulkChallanModal.vehicleType
          ),
          invoice: bulkChallanModal.generateInvoice ? true : false,
          ...(bulkChallanModal.deliveryDate ? { delivery_date: bulkChallanModal.deliveryDate } : {}),
          ...(dispatchPersonName ? { dispatch_person_name: dispatchPersonName } : {}),
          ...(bulkChallanModal.orderBy ? { order_by: bulkChallanModal.orderBy } : {}),
          ...(bulkChallanModal.entityName ? { order_by_entity: bulkChallanModal.entityName } : {}),
          ...(!meterNotWorking && bulkChallanModal.start_km
            ? { start_km: bulkChallanModal.start_km }
            : {}),
          ...buildDispatchLogisticsPayload(bulkChallanModal),
        };

        const receiptFile = bulkChallanModal.diesel_receipt_upload;
        let response;
        if (receiptFile instanceof File) {
          const formData = new FormData();
          Object.entries(payload).forEach(([key, value]) => {
            if (value === undefined || value === null) return;
            if (Array.isArray(value)) {
              formData.append(key, value.join(','));
              return;
            }
            formData.append(key, typeof value === 'boolean' || typeof value === 'number' ? String(value) : value);
          });
          formData.append('diesel_receipt_photo', receiptFile, receiptFile.name);
          response = await apiService.post(
            API_ENDPOINTS.POST_create_delivery_challan,
            formData,
            'multipart/form-data'
          );
        } else {
          response = await apiService.post(
            API_ENDPOINTS.POST_create_delivery_challan,
            payload,
            'application/json'
          );
        }

        return response
      } catch (error) {
        console.error('Error creating bulk delivery challan:', error)
        throw error
      }
    }

    const handleCloseBulkChallanModal = () => {
      setBulkChallanModal({
        open: false,
        orderBy: '',
        entityName: '',
        driverName: '',
        vehicleNo: '',
        vehicleType: '',
        deliveryDate: new Date().toISOString().slice(0,10),
        generateInvoice: false,
        isCreating: false,
        error: '',
        emails: [],
        emailInput: '',
        selectedStatus: '',
        selectedStatusCategory: '',
        dispatchPersonName: '',
        customDispatchPerson: '',
        useCustomDispatchPerson: false,
        start_km: '',
        end_km: '',
        ...DEFAULT_DISPATCH_LOGISTICS,
        ...DEFAULT_DELIVERED_LOGISTICS,
        diesel_receipt_upload: null,
        diesel_receipt_upload_error: '',
        cancellationReason: '',
        returnReason: '',
        bulkPaymentStatus: '',
        allowUnpaidBulkCancelFromPlaced: false,
      })
      setBulkEntityOptions([])
    }

    const handleBulkChallanSubmit = async () => {
      const { driverName, vehicleNo, selectedStatus } = bulkChallanModal

      if (!selectedStatus.trim()) {
        setBulkChallanModal(prev => ({
          ...prev,
          error: 'Please select a status to update orders to'
        }))
        return
      }

      if (selectedProducts.length === 0) {
        setBulkChallanModal(prev => ({
          ...prev,
          error: 'No products selected'
        }))
        return
      }

      if (statusFilter === 'Dispatch' && selectedStatus === 'Dispatch') {
        setBulkChallanModal(prev => ({
          ...prev,
          error: 'Cannot change status to Dispatch when viewing Dispatch tab. You can only change to Delivered, Returned, or Cancelled.'
        }))
        return
      }

      const selectedOrders = filteredData.filter(order => selectedProducts.includes(order.id));
      const statusOrder = getStatusOrder();
      const selectedOption = statusOptions.find(opt => opt.name === selectedStatus);
      const newStatusCategory = (selectedOption?.name || '').toLowerCase();

      const requiresDispatchPaymentRule = (selectedStatus || '').toLowerCase() === 'dispatch';
      const hasUnpaidOrders = selectedOrders.some(order => {
        const paymentStatus = (
          order.payment_status?.name ||
          order.payment_status_name ||
          order.payment_status ||
          ''
        ).toLowerCase();
        const isPaymentSatisfied = requiresDispatchPaymentRule
          ? isPaymentSatisfiedForDispatch(paymentStatus)
          : isPaymentSatisfiedForOrderAdvance(paymentStatus);
        return !isPaymentSatisfied;
      });

      const allowUnpaidPlacedBulkCancel =
        bulkChallanModal.allowUnpaidBulkCancelFromPlaced &&
        (selectedStatus || '').toLowerCase() === 'cancelled';

      if (hasUnpaidOrders && selectedStatus !== 'Orders' && !allowUnpaidPlacedBulkCancel) {
        const paymentErrorMessage = requiresDispatchPaymentRule
          ? 'Some selected orders must have payment status Paid or Credit before moving to Dispatch.'
          : 'Some selected orders must be paid before they can be moved to any status other than "Orders". Please update the payment status first.';
        setBulkChallanModal(prev => ({
          ...prev,
          error: paymentErrorMessage
        }));
        return;
      }

      const isReturnedSelected = selectedStatus.toLowerCase() === 'returned';
      const isCancelledSelected = selectedStatus.toLowerCase() === 'cancelled';
      const hasDeliveredOrders = selectedOrders.some(order => {
        const statusName = (order.status?.name || order.status_name || '').toLowerCase();
        const statusCategory = (order.status?.category || '').toLowerCase();
        return statusName === 'delivered' || statusCategory === 'delivered';
      });
      const hasCancelledOrders = selectedOrders.some(order => {
        const statusName = (order.status?.name || order.status_name || '').toLowerCase();
        const statusCategory = (order.status?.category || '').toLowerCase();
        return statusName === 'cancelled' || statusCategory === 'cancelled';
      });

      if (hasCancelledOrders) {
        setBulkChallanModal(prev => ({
          ...prev,
          error: 'Cancelled is a final status. No further status changes are allowed for cancelled orders.'
        }));
        return;
      }

      if (hasDeliveredOrders && !isReturnedSelected) {
        setBulkChallanModal(prev => ({
          ...prev,
          error: 'When an order is Delivered, only Returned status is allowed.'
        }));
        return;
      }

      if (isReturnedSelected && !hasDeliveredOrders) {
        setBulkChallanModal(prev => ({
          ...prev,
          error: 'Returned status is only available when orders are Delivered.'
        }));
        return;
      }

      const alwaysAvailableCategories = ['cancelled', 'returned'];

      if (!alwaysAvailableCategories.includes(newStatusCategory)) {
        let minStatusOrder = 999;
        selectedOrders.forEach(order => {
          const orderStatusCategory = (order.status?.category || '').toLowerCase();
          const orderStatusName = order.status?.name || '';

          let orderCategory = orderStatusCategory;
          if (!orderCategory && orderStatusName) {
            const orderStatusOption = statusOptions.find(opt => opt.name === orderStatusName);
            orderCategory = orderStatusOption?.category?.toLowerCase() || '';
          }

          const orderSequence = statusOrder[orderCategory] || 0;
          if (orderSequence > 0 && orderSequence < minStatusOrder) {
            minStatusOrder = orderSequence;
          }
        });

        if (minStatusOrder === 999) {
          minStatusOrder = 0;
        }
      }

      for (const order of selectedOrders) {
        const originalStatusCategory = (order.status?.category || '').toLowerCase();
        const originalStatusName = (order.status?.name || order.status_name || '').toLowerCase();
        const disabledForOrder = getStatusesToDisable(originalStatusCategory, originalStatusName) || [];
        const newCat = (selectedOption?.category || '').toLowerCase();

        if (!allowUnpaidPlacedBulkCancel && disabledForOrder.includes(newCat)) {
          setBulkChallanModal(prev => ({
            ...prev,
            error: 'Selected status is not allowed for one or more of the selected orders due to sequential/final status rules.'
          }));
          return;
        }

        const isDispatch = newCat === 'shipped' || newCat === 'dispatch' || selectedStatus === 'Dispatch';
        if (isDispatch) {
          const processingOrder = statusOrder['processing'] || 0;
          const currentStatusName = (order.status?.name || order.status_name || '').toLowerCase();
          const currentStatusCat = (order.status?.category || '').toLowerCase();
          const currentOrder = statusOrder[currentStatusCat] || 0;

          const isActuallyProcessing = (currentStatusName === 'processing' || currentStatusCat === 'processing') && currentOrder === processingOrder && processingOrder > 0;

          if (!isActuallyProcessing) {
            setBulkChallanModal(prev => ({
              ...prev,
              error: `Dispatch status is only available when orders are in Processing status. One or more selected orders are not in Processing.`
            }));
            return;
          }
        }
      }

      if (selectedStatus === 'Dispatch') {
        const isTruckOrderBy = String(bulkChallanModal.orderBy || '').trim().toLowerCase() === 'truck';
        if (!driverName.trim() || (isTruckOrderBy && !vehicleNo.trim())) {
          setBulkChallanModal(prev => ({
            ...prev,
            error: 'Driver name and vehicle number are required for Dispatch status'
          }))
          return
        }

        const dispatchLogisticsError = validateDispatchLogistics(bulkChallanModal);
        if (dispatchLogisticsError) {
          setBulkChallanModal(prev => ({
            ...prev,
            error: dispatchLogisticsError
          }));
          return;
        }
      }

      setBulkChallanModal(prev => ({
        ...prev,
        isCreating: true,
        error: ''
      }))

      try {
        const selectedOrders = filteredData.filter(product => selectedProducts.includes(product.id));
        const selectedOrderIds = selectedOrders
          .map(product => product.order_id)
          .filter(Boolean)
        const isDispatchTabForBulk = ['dispatch', 'shipped'].includes((statusFilter || '').toLowerCase());
        const anySelectedOrderHasStartKm = selectedOrders.some((order) => {
          const startKm = order.delivery_challan?.start_km;
          const hasStartKm = startKm !== null && startKm !== undefined && String(startKm).trim() !== '';
          if (isDispatchTabForBulk && !isOrderByTruck(order)) return false;
          return hasStartKm;
        });
        const referenceChallan =
          selectedOrders.find((order) => getOrderChallan(order))?.delivery_challan ||
          selectedOrders.find((order) => getOrderChallan(order))?.deliveryChallan ||
          getOrderChallan(selectedOrders[0]);

        if (selectedOrderIds.length === 0) {
          throw new Error('No valid orders found for selected products')
        }

        let response = { success: true };
        
        if (selectedStatus === 'Dispatch') {
          console.log('Creating bulk delivery challan for orders:', selectedOrderIds)
          response = await createBulkDeliveryChallan(selectedOrderIds, driverName, vehicleNo)
        }

        if (response?.success) {
          console.log('Updating order statuses to:', selectedStatus);
          console.log('Order IDs to update:', selectedOrderIds);
          
          try {
            if (selectedStatus === 'Delivered') {
              const truckOrderWithStartKm = isDispatchTabForBulk
                ? selectedOrders.find((order) => {
                    const startKm = order.delivery_challan?.start_km;
                    return (
                      isOrderByTruck(order) &&
                      startKm !== null &&
                      startKm !== undefined &&
                      String(startKm).trim() !== ''
                    );
                  })
                : null;
              const startKm = parseFloat(
                bulkChallanModal.start_km || truckOrderWithStartKm?.delivery_challan?.start_km
              );
              const endKm = parseFloat(bulkChallanModal.end_km);
              const hasStartKm = !isNaN(startKm);
              const meterNotWorking = isTruthyFlag(
                bulkChallanModal.meter_not_working ?? referenceChallan?.meter_not_working
              );
              
              if (!meterNotWorking && hasStartKm && !String(bulkChallanModal.end_km || '').trim()) {
                setBulkChallanModal(prev => ({
                  ...prev,
                  error: 'End KM is required',
                  isCreating: false
                }));
                return;
              }

              if (!meterNotWorking && hasStartKm && !isNaN(endKm) && endKm <= startKm) {
                alert('End KM must be greater than Start KM. Please enter a valid End KM value.');
                setBulkChallanModal(prev => ({
                  ...prev,
                  error: 'End KM must be greater than Start KM',
                  isCreating: false
                }));
                return;
              }

              const deliveredLogisticsError = validateDeliveredLogistics(
                bulkChallanModal,
                referenceChallan
              );
              if (deliveredLogisticsError) {
                setBulkChallanModal(prev => ({
                  ...prev,
                  error: deliveredLogisticsError,
                  isCreating: false
                }));
                return;
              }
            }

            const userId = localStorage.getItem("user_id");
            const dispatchPersonName = bulkChallanModal.useCustomDispatchPerson 
              ? bulkChallanModal.customDispatchPerson 
              : bulkChallanModal.dispatchPersonName;
            const resolvedBulkVehicleType = resolveVehicleTypeForPayload(
              bulkChallanModal.orderBy,
              bulkChallanModal.vehicleType
            );
            const payload = {
              user_id: userId,
              status: selectedStatus,
              order_id: selectedOrderIds.join(','),
              emails: bulkChallanModal.emails?.length > 0 ? bulkChallanModal.emails.join(',') : '',
              generateInvoice: bulkChallanModal.generateInvoice,
              driverName: selectedStatus === 'Dispatch' ? bulkChallanModal.driverName : '',
              vehicleNo: selectedStatus === 'Dispatch' ? bulkChallanModal.vehicleNo : '',
              delivery_date: bulkChallanModal.deliveryDate || new Date().toISOString().slice(0,10),
              dispatch_person_name: dispatchPersonName,
              start_km:
                selectedStatus === 'Dispatch' && isTruthyFlag(bulkChallanModal.meter_not_working)
                  ? ''
                  : (bulkChallanModal.start_km || ''),
              ...(selectedStatus === 'Dispatch' && bulkChallanModal.orderBy && { order_by: bulkChallanModal.orderBy }),
              ...(selectedStatus === 'Dispatch' &&
                resolvedBulkVehicleType && { vehicle_type: resolvedBulkVehicleType }),
              ...(selectedStatus === 'Dispatch' && bulkChallanModal.entityName && { order_by_entity: bulkChallanModal.entityName }),
              ...(selectedStatus === 'Dispatch' ? buildDispatchLogisticsPayload(bulkChallanModal) : {}),
              ...(selectedStatus === 'Delivered' && anySelectedOrderHasStartKm && !isTruthyFlag(bulkChallanModal.meter_not_working ?? referenceChallan?.meter_not_working)
                ? { end_km: bulkChallanModal.end_km || '' }
                : {}),
              ...(selectedStatus === 'Delivered'
                ? buildDeliveredLogisticsPayload(bulkChallanModal, referenceChallan)
                : {}),
              ...(selectedStatus === 'Cancelled' && { reason: bulkChallanModal.cancellationReason || '' }),
              ...(selectedStatus === 'Returned' && { reason: bulkChallanModal.returnReason || '' })
            };

            console.log('Bulk status update payload:', payload);

            const bulkReceiptFile = bulkChallanModal.diesel_receipt_upload;
            let statusResponse;
            if (
              selectedStatus === 'Dispatch' &&
              bulkReceiptFile instanceof File
            ) {
              const formData = new FormData();
              Object.entries(payload).forEach(([key, value]) => {
                if (value === undefined || value === null) return;
                if (Array.isArray(value)) {
                  formData.append(key, value.join(','));
                  return;
                }
                formData.append(key, typeof value === 'boolean' || typeof value === 'number' ? String(value) : value);
              });
              formData.append('diesel_receipt_photo', bulkReceiptFile, bulkReceiptFile.name);
              statusResponse = await apiService.putMultipart(
                API_ENDPOINTS.UPDATE_product_status,
                formData
              );
            } else {
              statusResponse = await apiService.put(
                API_ENDPOINTS.UPDATE_product_status,
                payload,
                "application/json"
              );
            }
            
            console.log('Bulk status update response:', statusResponse);

            if (statusResponse?.success) {
              setBulkChallanModal({
                open: false,
                orderBy: '',
                entityName: '',
                driverName: '',
                vehicleNo: '',
                vehicleType: '',
                deliveryDate: new Date().toISOString().slice(0,10),
                generateInvoice: false,
                isCreating: false,
                error: '',
                emails: [],
                emailInput: '',
                selectedStatus: '',
                selectedStatusCategory: '',
                dispatchPersonName: '',
                customDispatchPerson: '',
                useCustomDispatchPerson: false,
                start_km: '',
                end_km: '',
                ...DEFAULT_DISPATCH_LOGISTICS,
                ...DEFAULT_DELIVERED_LOGISTICS,
                diesel_receipt_upload: null,
                diesel_receipt_upload_error: '',
                cancellationReason: '',
                returnReason: '',
                bulkPaymentStatus: '',
                allowUnpaidBulkCancelFromPlaced: false,
              })
              
              if (onSelectAll) {
                onSelectAll(false)
              }
              setTimeout(() => {
                onRefresh?.()
              }, 100)
            } else {
              throw new Error(getApiErrorMessage(statusResponse, 'Failed to update order statuses'))
            }
          } catch (statusError) {
            console.error('Error updating order statuses:', statusError);
            throw new Error(getApiErrorMessage(statusError, 'Failed to update order statuses'));
          }
        } else {
          throw new Error(getApiErrorMessage(response, 'Failed to create bulk delivery challan'))
        }
      } catch (error) {
        console.error('Error creating bulk delivery challan:', error)
        setBulkChallanModal(prev => ({
          ...prev,
          error: getApiErrorMessage(error, 'Failed to create bulk delivery challan'),
          isCreating: false
        }))
      }
    }

    const fetchBulkEntitiesByOrderBy = async (orderBy) => {
      if (!orderBy) {
        setBulkEntityOptions([]);
        return;
      }
      
      setLoadingBulkEntities(true);
      try {
        const entityMapping = {
          'indian post': ['Mumbai Branch', 'Delhi Branch', 'Bangalore Branch', 'Chennai Branch'],
          'courier services': ['BlueDart', 'FedEx', 'DTDC', 'First Flight', 'Professional Courier'],
          'name': ['Mumbai Branch', 'Delhi Branch', 'Bangalore Branch', 'Chennai Branch']
        };
        
        const entities = entityMapping[orderBy.toLowerCase()] || [];
        setBulkEntityOptions(entities);
      } catch (error) {
        console.error('Error fetching bulk entities:', error);
        setBulkEntityOptions([]);
      } finally {
        setLoadingBulkEntities(false);
      }
    };

    const handleBulkChallanInputChange = (field, value) => {
      setBulkChallanModal(prev => ({
        ...prev,
        [field]: value,
        error: ''
      }))
      
      if (field === 'orderBy') {
        setBulkChallanModal(prev => ({
          ...prev,
          orderBy: value,
          entityName: '',
          error: ''
        }));
        fetchBulkEntitiesByOrderBy(value);
      }
    }

    const isBulkSubmitDisabled = () => {
      if (bulkChallanModal.isCreating) return true;
      const selectedStatus = bulkChallanModal.selectedStatus;
      if (!selectedStatus || !selectedStatus.trim()) return true;

      const selectedOrders = filteredData.filter(order => selectedProducts.includes(order.id));
      const requiresDispatchPaymentRule = (selectedStatus || '').toLowerCase() === 'dispatch';
      const hasUnpaidOrders = selectedOrders.some(order => {
        const paymentStatus = (
          order.payment_status?.name ||
          order.payment_status_name ||
          order.payment_status ||
          ''
        ).toLowerCase();
        const isPaymentSatisfied = requiresDispatchPaymentRule
          ? isPaymentSatisfiedForDispatch(paymentStatus)
          : isPaymentSatisfiedForOrderAdvance(paymentStatus);
        return !isPaymentSatisfied;
      });
      const allowUnpaidPlacedBulkCancel =
        bulkChallanModal.allowUnpaidBulkCancelFromPlaced &&
        (selectedStatus || '').toLowerCase() === 'cancelled';

      if (hasUnpaidOrders && selectedStatus !== 'Orders' && !allowUnpaidPlacedBulkCancel) return true;

      const selCat = (bulkChallanModal.selectedStatusCategory || '').toLowerCase();
      const isDispatchTabForBulk = ['dispatch', 'shipped'].includes((statusFilter || '').toLowerCase());
      const anySelectedOrderHasStartKm = selectedOrders.some(order => {
        const startKm = order.delivery_challan?.start_km;
        const hasStartKm = startKm !== null && startKm !== undefined && String(startKm).trim() !== '';
        if (isDispatchTabForBulk && !isOrderByTruck(order)) return false;
        return hasStartKm;
      });

      if (selCat === 'shipped' || selectedStatus === 'Dispatch') {
        const dispatchPersonName = bulkChallanModal.useCustomDispatchPerson ? bulkChallanModal.customDispatchPerson : bulkChallanModal.dispatchPersonName;
        const isTruckOrderBy = String(bulkChallanModal.orderBy || '').trim().toLowerCase() === 'truck';
        const isProcessingTab = (statusFilter || '').toLowerCase() === 'processing';
        const meterNotWorking = isTruthyFlag(bulkChallanModal.meter_not_working);
        if (
          !bulkChallanModal.orderBy?.trim() ||
          !bulkChallanModal.entityName?.trim() ||
          !bulkChallanModal.driverName?.trim() ||
          (isTruckOrderBy && !bulkChallanModal.vehicleType?.trim()) ||
          !bulkChallanModal.vehicleNo?.trim() ||
          !bulkChallanModal.deliveryDate?.trim() ||
          !dispatchPersonName?.trim() ||
          !!validateDispatchLogistics(bulkChallanModal)
        ) return true;
        if (
          !meterNotWorking &&
          isTruckOrderBy &&
          isProcessingTab &&
          !String(bulkChallanModal.start_km || '').trim()
        ) return true;
        if (
          !meterNotWorking &&
          isTruckOrderBy &&
          String(bulkChallanModal.start_km || '').trim() !== '' &&
          Number(bulkChallanModal.start_km) < 1
        ) return true;
      }

      if (selectedStatus === 'Delivered') {
        const selectedOrdersForDelivered = filteredData.filter(order => selectedProducts.includes(order.id));
        const referenceChallan =
          selectedOrdersForDelivered.find((order) => getOrderChallan(order))?.delivery_challan ||
          getOrderChallan(selectedOrdersForDelivered[0]);
        if (validateDeliveredLogistics(bulkChallanModal, referenceChallan)) return true;

        const meterNotWorking = isTruthyFlag(
          bulkChallanModal.meter_not_working ?? referenceChallan?.meter_not_working
        );
        if (!meterNotWorking && anySelectedOrderHasStartKm) {
          const truckOrderWithStartKm = isDispatchTabForBulk
            ? selectedOrders.find((order) => {
                const startKm = order.delivery_challan?.start_km;
                return (
                  isOrderByTruck(order) &&
                  startKm !== null &&
                  startKm !== undefined &&
                  String(startKm).trim() !== ''
                );
              })
            : null;
          const startKm = parseFloat(
            bulkChallanModal.start_km || truckOrderWithStartKm?.delivery_challan?.start_km
          );
          const endKm = parseFloat(bulkChallanModal.end_km);
          if (!String(bulkChallanModal.end_km || '').trim()) return true;
          if (!isNaN(startKm) && !isNaN(endKm) && endKm <= startKm) return true;
        }
      }

      if (selectedStatus === 'Cancelled' && !bulkChallanModal.cancellationReason?.trim()) return true;
      if (selectedStatus === 'Returned' && !bulkChallanModal.returnReason?.trim()) return true;

      return false;
    }

    const addEmailToBulkModal = () => {
      const email = bulkChallanModal.emailInput?.trim()
      if (email && !bulkChallanModal.emails?.includes(email)) {
        setBulkChallanModal(prev => ({
          ...prev,
          emails: [...(prev.emails || []), email],
          emailInput: ''
        }))
      }
    }

    const removeEmailFromBulkModal = (emailToRemove) => {
      setBulkChallanModal(prev => ({
        ...prev,
        emails: prev.emails?.filter(email => email !== emailToRemove) || []
      }))
    }

    const getEligibleProductsCount = () => {
      return filteredData
        .filter(order => selectedProducts.includes(order.id))
        .filter(order => !extractDeliveryChallan(order)?.path)
        .length
    }

    const getSelectedProductsCount = () => {
      return filteredData
        .filter(order => selectedProducts.includes(order.id))
        .length
    }

    const handleProductSelection = (orderId, isSelected) => {
      if (onProductSelect) {
        onProductSelect(orderId, isSelected)
      }
    }

    const handleSelectAllToggle = () => {
      if (onSelectAll) {
        onSelectAll(!isAllSelected)
      }
    }

    const isProductSelected = (orderId) => {
      return selectedProducts.includes(orderId)
    }

    const openOrderDetails = (order) => {
      const orderRouteId = order?.order_id || order?.id
      if (!orderRouteId) return
      rememberLastOpenedAppOrder(order)
      if (statusFilter) {
        sessionStorage.setItem(APP_ORDER_STATUS_TAB_STORAGE_KEY, statusFilter)
      }
      const listPage = Number(currentPage) > 0 ? Number(currentPage) : 1
      const listPageSize = Number(pageSize) > 0 ? Number(pageSize) : 10
      const startDateStored = toStoredAppOrderDate(listFilters?.startDate)
      const endDateStored = toStoredAppOrderDate(listFilters?.endDate)
      const listState = {
        page: listPage,
        pageSize: listPageSize,
        searchTerm: typeof searchQuery === 'string' ? searchQuery : '',
        selectedCompany: listFilters?.selectedCompany ?? 'all',
        showFilters: Boolean(listFilters?.showFilters),
        // Only persist real dates — never write blank dates that would wipe filters on restore
        ...(startDateStored ? { startDate: startDateStored } : {}),
        ...(endDateStored ? { endDate: endDateStored } : {}),
        datesCleared: Boolean(listFilters) && !startDateStored && !endDateStored,
      }
      saveAppOrderListState(listState)
      // Tell the list page to restore the in-memory snapshot on Back (no API refresh).
      markAppOrderExpectReturn()
      navigate(`/dashboard/orders-management/${encodeURIComponent(orderRouteId)}`, {
        state: {
          order,
          statusFilter,
          ...listState,
        },
      })
    }

    const createDeliveryChallan = async (orderId, driverName, vehicleNo) => {
      try {
        const dispatchPersonName = statusUpdateModal.useCustomDispatchPerson 
          ? statusUpdateModal.customDispatchPerson 
          : statusUpdateModal.dispatchPersonName;
        const meterNotWorking = isTruthyFlag(statusUpdateModal.meter_not_working);

        const payload = {
          order_id: orderId,
          driver_name: driverName,
          vehicle_no: vehicleNo || '',
          vehicle_type: resolveVehicleTypeForPayload(
            statusUpdateModal.orderBy,
            statusUpdateModal.vehicleType
          ),
          invoice: statusUpdateModal.generateInvoice ? true : false,
          ...(statusUpdateModal.shippedDate ? { delivery_date: statusUpdateModal.shippedDate } : {}),
          ...(dispatchPersonName ? { dispatch_person_name: dispatchPersonName } : {}),
          ...(statusUpdateModal.orderBy ? { order_by: statusUpdateModal.orderBy } : {}),
          ...(statusUpdateModal.entityName ? { order_by_entity: statusUpdateModal.entityName } : {}),
          ...(!meterNotWorking && statusUpdateModal.start_km
            ? { start_km: statusUpdateModal.start_km }
            : {}),
          ...buildDispatchLogisticsPayload(statusUpdateModal),
        };

        const response = await apiService.post(
          API_ENDPOINTS.POST_create_delivery_challan,
          payload,
          'application/json'
        );

        return response;
      } catch (error) {
        console.error('Error creating delivery challan:', error);
        throw error;
      }
    };

    const getStatusOrder = () => {
      if (!statusOptions || statusOptions.length === 0) {
        return {};
      }
      
      const orderMap = {};
      statusOptions.forEach(option => {
        const category = (option.category || '').toLowerCase();
        const sequence = option.sequence || 0;
        if (category) {
          orderMap[category] = sequence;
        }
      });
      
      return orderMap;
    };

    const getNextSequentialStatus = (currentStatusName, currentStatusCategory) => {
      if (!statusOptions || statusOptions.length === 0) {
        return null;
      }

      const statusOrder = getStatusOrder();
      let currentStatusLower = (currentStatusCategory || '').toLowerCase();
      let currentOrder = statusOrder[currentStatusLower] || 0;

      if (currentOrder === 0 && currentStatusName) {
        const currentStatusOption = statusOptions.find(opt => opt.name === currentStatusName);
        if (currentStatusOption) {
          currentStatusLower = (currentStatusOption.category || '').toLowerCase();
          currentOrder = statusOrder[currentStatusLower] || 0;
        }
      }

      if (currentOrder === 0) {
        return null;
      }
      
      const isDelivered = (currentStatusName || '').toLowerCase() === 'delivered' ||
                        (currentStatusCategory || '').toLowerCase() === 'delivered';
      
      if (isDelivered) {
        const returnedStatus = statusOptions.find(option => {
          const optionCategory = (option.category || '').toLowerCase();
          const optionName = (option.name || '').toLowerCase();
          return optionCategory === 'returned' || optionName === 'returned';
        });
        return returnedStatus || null;
      }
      
      const nextOrder = currentOrder + 1;
      const nextStatus = statusOptions.find(option => {
        const optionCategory = (option.category || '').toLowerCase();
        return statusOrder[optionCategory] === nextOrder;
      });

      return nextStatus || null;
    };

    const getStatusesToDisable = (currentStatusCategory, currentStatusName) => {
      if (!statusOptions || statusOptions.length === 0) {
        return [];
      }
      
      const statusOrder = getStatusOrder();
      let currentStatusLower = (currentStatusCategory || '').toLowerCase();
      let currentOrder = statusOrder[currentStatusLower] || 0;
      
      if (currentOrder === 0 && currentStatusName) {
        const currentStatusOption = statusOptions.find(opt => opt.name === currentStatusName);
        if (currentStatusOption) {
          currentStatusLower = (currentStatusOption.category || '').toLowerCase();
          currentOrder = statusOrder[currentStatusLower] || 0;
        }
      }
      
      const dispatchOrder = statusOrder['shipped'] || statusOrder['dispatch'] || 999;
      const processingOrder = statusOrder['processing'] || 0;
      const deliveredOrder = statusOrder['delivered'] || 999;
      
      const isProcessingStatus = currentStatusName?.toLowerCase() === 'processing' || 
                                  currentStatusLower === 'processing' ||
                                  currentOrder === processingOrder;
      
      const isDeliveredStatus = currentStatusName?.toLowerCase() === 'delivered' || 
                                currentStatusLower === 'delivered' ||
                                currentOrder === deliveredOrder;
      
      const isCancelledStatus = currentStatusName?.toLowerCase() === 'cancelled' || 
                                currentStatusLower === 'cancelled' ||
                                currentStatusLower === 'cancelled';
      
      if (currentOrder === 0) {
        return statusOptions
          .filter(option => {
            const optionCategory = (option.category || '').toLowerCase();
            const optionName = (option.name || '').toLowerCase();
            if (optionCategory === 'shipped' || optionCategory === 'dispatch' || optionName === 'dispatch') {
              return true;
            }
            return true;
          })
          .map(option => option.category?.toLowerCase() || '');
      }
      
      if (isCancelledStatus) {
        return statusOptions.filter(option => {
          const optionCategory = (option.category || '').toLowerCase();
          const optionName = (option.name || '').toLowerCase();
          const isReturned = optionCategory === 'returned' || optionName === 'returned';
          return !isReturned;
        }).map(option => option.category?.toLowerCase() || '');
      }
      
      if (isDeliveredStatus) {
        return statusOptions
          .filter(option => {
            const optionCategory = (option.category || '').toLowerCase();
            const optionName = (option.name || '').toLowerCase();
            const isReturned = optionCategory === 'returned' || optionName === 'returned';
            return !isReturned;
          })
          .map(option => option.category?.toLowerCase() || '');
      }
      
      return statusOptions.filter(option => {
        const optionCategory = (option.category || '').toLowerCase();
        const optionName = (option.name || '').toLowerCase();
        const isReturned = optionCategory === 'returned' || optionName === 'returned';
        const isCancelled = optionCategory === 'cancelled' || optionName === 'cancelled';
        const isDispatch = optionCategory === 'shipped' || optionCategory === 'dispatch' || optionName === 'dispatch';
        
        if (isDispatch) {
          return !isProcessingStatus;
        }
        
        if (isReturned) {
          return !isDeliveredStatus;
        }
        
        const statusOrderValue = statusOrder[optionCategory] || 0;
        const dispatchOrder = statusOrder['shipped'] || statusOrder['dispatch'] || 999;
        
        if (statusOrderValue < currentOrder) {
          return true;
        }
        
        if (statusOrderValue === currentOrder) {
          return true;
        }
        
        if (isCancelled) {
          if (currentOrder <= dispatchOrder) {
            return false;
          } else {
            return true;
          }
        }
        
        if (statusOrderValue > currentOrder + 1) {
          return true;
        }
        
        return false;
      }).map(option => option.category?.toLowerCase() || '');
    };

    const isStatusUpdateDispatchFormIncomplete = (modalState) => {
      const dispatchPersonName = modalState.useCustomDispatchPerson
        ? modalState.customDispatchPerson
        : modalState.dispatchPersonName;
      const isTruckOrderBy = String(modalState.orderBy || '').trim().toLowerCase() === 'truck';
      const isProcessingTab = (statusFilter || '').toLowerCase() === 'processing';
      const meterNotWorking = isTruthyFlag(modalState.meter_not_working);

      return (
        !modalState.orderBy?.trim() ||
        !modalState.entityName?.trim() ||
        !modalState.driverName?.trim() ||
        (isTruckOrderBy && !modalState.vehicleType?.trim()) ||
        !modalState.vehicleNo?.trim() ||
        !modalState.shippedDate?.trim() ||
        !dispatchPersonName?.trim() ||
        (!meterNotWorking &&
          isTruckOrderBy &&
          isProcessingTab &&
          !String(modalState.start_km || '').trim()) ||
        (!meterNotWorking &&
          isTruckOrderBy &&
          String(modalState.start_km || '').trim() !== '' &&
          Number(modalState.start_km) < 1) ||
        !!validateDispatchLogistics(modalState)
      );
    };

    const isSingleStatusSubmitDisabled = () => {
      if (updatingStatus || statusUpdateModal.isCreatingChallan) return true;

      const selectedStatus = statusUpdateModal.order?.status?.name || statusUpdateModal.order?.status_name;
      if (!selectedStatus) return true;

      const paymentStatus = (statusUpdateModal.order?.payment_status?.name || statusUpdateModal.order?.payment_status || '').toLowerCase();
      const isPaid = isPaymentSatisfiedForOrderAdvance(paymentStatus);
      const isOrdersStatus = selectedStatus === 'Orders';
      const isDispatchTab = ['dispatch', 'shipped'].includes((statusFilter || '').toLowerCase());
      const isTruckOrder = isDispatchTab
        ? isOrderByTruck(statusUpdateModal.order)
        : String(statusUpdateModal.orderBy || '').trim().toLowerCase() === 'truck';

      const originalStatusName = (statusUpdateModal.originalStatus?.name || '').toLowerCase();
      const originalStatusCategory = (statusUpdateModal.originalStatus?.category || '').toLowerCase();
      const isOriginallyDelivered = originalStatusName === 'delivered' || originalStatusCategory === 'delivered';
      const isReturnedSelected = selectedStatus === 'Returned';

      const isShippedCategory = (statusUpdateModal.order?.status?.category || '').toLowerCase() === 'shipped';
      if (
        isShippedCategory &&
        !(isDispatchTab && selectedStatus === 'Delivered') &&
        isStatusUpdateDispatchFormIncomplete(statusUpdateModal)
      ) {
        return true;
      }

      if (selectedStatus === 'Delivered') {
        const challan = getOrderChallan(statusUpdateModal.order);
        if (validateDeliveredLogistics(statusUpdateModal, challan)) return true;

        const meterNotWorking = isTruthyFlag(
          statusUpdateModal.meter_not_working ?? challan?.meter_not_working
        );
        if (isDispatchTab && !isTruckOrder && !meterNotWorking) return false;
        if (meterNotWorking) return false;

        const startKmFromModal = statusUpdateModal.start_km;
        const startKmFromOrder = statusUpdateModal.order?.delivery_challan?.start_km;
        const startKm = parseFloat(startKmFromModal || startKmFromOrder);
        const endKm = parseFloat(statusUpdateModal.end_km);
        const hasStartKm = !isNaN(startKm);
        if (hasStartKm && !String(statusUpdateModal.end_km || '').trim()) return true;
        if (hasStartKm && !isNaN(endKm) && endKm <= startKm) return true;
      }

      if (selectedStatus === 'Cancelled' && !statusUpdateModal.cancellationReason?.trim()) return true;
      if (selectedStatus === 'Returned' && !statusUpdateModal.returnReason?.trim()) return true;

      if (
        selectedStatus !== 'Returned' &&
        selectedStatus !== 'Cancelled' &&
        !isPaid &&
        !isOrdersStatus &&
        !(isOriginallyDelivered && isReturnedSelected) &&
        !(isDispatchTab && selectedStatus === 'Delivered')
      ) {
        return true;
      }

      return false;
    };

    const handleStatusUpdate = async (id, statusName, orderId) => {
      try {
        setUpdatingStatus(true);
        
        const originalStatusCategory = (statusUpdateModal.originalStatus?.category || '').toLowerCase();
        const originalStatusName = (statusUpdateModal.originalStatus?.name || '').toLowerCase();
        const currentStatusCategory = originalStatusCategory || (statusUpdateModal.order?.status?.category || statusUpdateModal.order?.status_name || '').toLowerCase();
        const currentStatusName = originalStatusName || (statusUpdateModal.order?.status?.name || statusUpdateModal.order?.status_name || '').toLowerCase();
        const selectedOption = statusOptions.find(opt => opt.name === statusName);
        const newStatusCategory = (selectedOption?.category || '').toLowerCase();
        const newStatusName = (statusName || '').toLowerCase();
        
        const statusOrder = getStatusOrder();
        const currentOrder = statusOrder[currentStatusCategory] || 0;
        const newOrder = statusOrder[newStatusCategory] || 0;
        const processingOrder = statusOrder['processing'] || 0;
        const deliveredOrder = statusOrder['delivered'] || 999;
        
        const isProcessingStatus = currentStatusName === 'processing' || 
                                    currentStatusCategory === 'processing' ||
                                    currentOrder === processingOrder;
        
        const isDeliveredStatus = currentStatusName === 'delivered' || 
                                  currentStatusCategory === 'delivered' ||
                                  currentOrder === deliveredOrder;
        
        const isCancelledStatus = currentStatusName === 'cancelled' || 
                                  currentStatusCategory === 'cancelled';
        
        const isCancelledOrReturned = newStatusCategory === 'cancelled' || newStatusCategory === 'returned' ||
                                      newStatusName === 'cancelled' || newStatusName === 'returned';
        const isReturned = newStatusCategory === 'returned' || newStatusName === 'returned';
        const isDispatch = newStatusCategory === 'shipped' || newStatusCategory === 'dispatch' || newStatusName === 'dispatch';
        
        const paymentStatus = (statusUpdateModal.order?.payment_status?.name || statusUpdateModal.order?.payment_status || '').toLowerCase();
        const isPaid = isPaymentSatisfiedForOrderAdvance(paymentStatus);
        const isOrdersStatus = statusName === 'Orders';
        const isOriginallyDelivered = originalStatusName === 'delivered' || originalStatusCategory === 'delivered';
        
        if (!isPaid && !isOrdersStatus) {
          const allowUnpaidPlacedCancel =
            statusUpdateModal.allowUnpaidCancelFromPlaced &&
            (statusName || '').toLowerCase() === 'cancelled';

          if (!allowUnpaidPlacedCancel) {
            if (isReturned && !isOriginallyDelivered) {
              setStatusUpdateModal(prev => ({
                ...prev,
                challanError: 'Returned status is only available when the order is Delivered.'
              }));
              setUpdatingStatus(false);
              return;
            }

            if (!(isOriginallyDelivered && isReturned)) {
              setStatusUpdateModal(prev => ({
                ...prev,
                challanError: 'This order must be paid before it can be moved to any status other than "Orders". Please update the payment status first.'
              }));
              setUpdatingStatus(false);
              return;
            }
          }
        }
        
        if (isDeliveredStatus) {
          if (!isReturned) {
            setStatusUpdateModal(prev => ({
              ...prev,
              challanError: `When an order is Delivered, only Returned status is allowed.`
            }));
            setUpdatingStatus(false);
            return;
          }
        } else {
          if (isReturned) {
            setStatusUpdateModal(prev => ({
              ...prev,
              challanError: `Returned status is only available when the order is Delivered.`
            }));
            setUpdatingStatus(false);
            return;
          }
          
          if (isCancelledStatus) {
            setStatusUpdateModal(prev => ({
              ...prev,
              challanError: `Cancelled is a final status. No further status changes are allowed.`
            }));
            setUpdatingStatus(false);
            return;
          }
          
          if (isDispatch) {
            const dispatchOrder = statusOrder['shipped'] || statusOrder['dispatch'] || 999;
            
            const isActuallyProcessing = (currentStatusName === 'processing' || 
                                        currentStatusCategory === 'processing') &&
                                        currentOrder === processingOrder &&
                                        processingOrder > 0;
            
            if (!isActuallyProcessing) {
              const currentStatusDisplay = statusUpdateModal.originalStatus?.name || 
                                        statusUpdateModal.order?.status?.name || 
                                        statusUpdateModal.order?.status_name || 
                                        currentStatusCategory ||
                                        'Unknown';
              setStatusUpdateModal(prev => ({
                ...prev,
                challanError: `Dispatch status is only available when the order is in Processing status. Current status: ${currentStatusDisplay}.`
              }));
              setUpdatingStatus(false);
              return;
            }
          }
        }
        
        if (!isCancelledOrReturned) {
          if (newOrder > currentOrder + 1) {
            setStatusUpdateModal(prev => ({
              ...prev,
              challanError: `Status must be updated sequentially. You can only move to the next status in the sequence. Current: ${statusUpdateModal.order?.status?.name || currentStatusCategory}, Next allowed: ${statusOptions.find(opt => {
                const optOrder = statusOrder[(opt.category || '').toLowerCase()] || 0;
                return optOrder === currentOrder + 1;
              })?.name || 'N/A'}.`
            }));
            setUpdatingStatus(false);
            return;
          }
          
          if (newOrder < currentOrder) {
            setStatusUpdateModal(prev => ({
              ...prev,
              challanError: `Cannot move to a previous status. Current status: ${statusUpdateModal.order?.status?.name || currentStatusCategory}.`
            }));
            setUpdatingStatus(false);
            return;
          }
        }
        
        const selectedOptionForShipping = statusOptions.find(opt => opt.name === statusName);
        const statusCategoryForShipping = (selectedOptionForShipping?.category || '').toLowerCase();
        if (statusCategoryForShipping === 'shipped') {
          const { driverName, vehicleNo } = statusUpdateModal;
          
          if (isStatusUpdateDispatchFormIncomplete(statusUpdateModal)) {
            const isTruckOrderBy = String(statusUpdateModal.orderBy || '').trim().toLowerCase() === 'truck';
            const isProcessingTab = (statusFilter || '').toLowerCase() === 'processing';
            const meterNotWorking = isTruthyFlag(statusUpdateModal.meter_not_working);
            let challanError = 'Please fill all required delivery details before shipping.';
            if (validateDispatchLogistics(statusUpdateModal)) {
              challanError = validateDispatchLogistics(statusUpdateModal);
            } else if (
              !meterNotWorking &&
              isTruckOrderBy &&
              isProcessingTab &&
              !String(statusUpdateModal.start_km || '').trim()
            ) {
              challanError = 'Start KM is required for Truck orders.';
            } else if (
              !meterNotWorking &&
              isTruckOrderBy &&
              String(statusUpdateModal.start_km || '').trim() !== '' &&
              Number(statusUpdateModal.start_km) < 1
            ) {
              challanError = 'Start KM must be 1 or greater';
            }
            setStatusUpdateModal(prev => ({
              ...prev,
              challanError
            }));
            setUpdatingStatus(false);
            return;
          }

          setStatusUpdateModal(prev => ({
            ...prev,
            isCreatingChallan: true,
            challanError: ''
          }));

          try {
            const challanResponse = await createDeliveryChallan(orderId, driverName, vehicleNo);
            
            if (!challanResponse?.success) {
              throw new Error(getApiErrorMessage(challanResponse, 'Failed to create delivery challan'));
            }

            console.log('Delivery challan created successfully');
          } catch (challanError) {
            console.error('Error creating delivery challan:', challanError);
            const apiMessage = getApiErrorMessage(challanError, 'Failed to create delivery challan');
            setStatusUpdateModal(prev => ({
              ...prev,
              challanError: apiMessage,
              isCreatingChallan: false
            }));
            setUpdatingStatus(false);
            return;
          }

          setStatusUpdateModal(prev => ({
            ...prev,
            isCreatingChallan: false
          }));
        }

        if (statusName === 'Delivered') {
          const isDispatchTab = ['dispatch', 'shipped'].includes((statusFilter || '').toLowerCase());
          const isTruckOrder = isDispatchTab
            ? isOrderByTruck(statusUpdateModal.order)
            : String(statusUpdateModal.orderBy || '').trim().toLowerCase() === 'truck';
          const challan = getOrderChallan(statusUpdateModal.order);
          const meterNotWorking = isTruthyFlag(
            statusUpdateModal.meter_not_working ?? challan?.meter_not_working
          );
          const deliveredLogisticsError = validateDeliveredLogistics(statusUpdateModal, challan);
          if (deliveredLogisticsError) {
            setStatusUpdateModal(prev => ({
              ...prev,
              challanError: deliveredLogisticsError
            }));
            setUpdatingStatus(false);
            return;
          }
          if ((!isDispatchTab || isTruckOrder) && !meterNotWorking) {
            const startKmFromModal = statusUpdateModal.start_km;
            const startKmFromOrder = statusUpdateModal.order?.delivery_challan?.start_km;
            const startKm = parseFloat(startKmFromModal || startKmFromOrder);
            const endKm = parseFloat(statusUpdateModal.end_km);
            const hasStartKm = !isNaN(startKm);
            
            if (hasStartKm && !String(statusUpdateModal.end_km || '').trim()) {
              setStatusUpdateModal(prev => ({
                ...prev,
                challanError: 'End KM is required'
              }));
              setUpdatingStatus(false);
              return;
            }

            if (hasStartKm && !isNaN(endKm) && endKm <= startKm) {
              alert('End KM must be greater than Start KM. Please enter a valid End KM value.');
              setStatusUpdateModal(prev => ({
                ...prev,
                challanError: 'End KM must be greater than Start KM'
              }));
              setUpdatingStatus(false);
              return;
            }
          }
        }

        const userId = localStorage.getItem("user_id");
        const dispatchPersonName = statusUpdateModal.useCustomDispatchPerson 
          ? statusUpdateModal.customDispatchPerson 
          : statusUpdateModal.dispatchPersonName;
        const challanForPayload = getOrderChallan(statusUpdateModal.order);
        const meterNotWorkingForPayload = isTruthyFlag(
          statusUpdateModal.meter_not_working ?? challanForPayload?.meter_not_working
        );
        const resolvedVehicleType = resolveVehicleTypeForPayload(
          statusUpdateModal.orderBy,
          statusUpdateModal.vehicleType
        );
        const payload = {
          user_id: userId,
          status: statusName,
          order_id: orderId,
          emails: statusUpdateModal.emails,
          generateInvoice: statusUpdateModal.generateInvoice,
          driverName: statusUpdateModal.driverName,
          vehicleNo: statusUpdateModal.vehicleNo,
          delivery_date: statusName === 'Processing' ? null : statusUpdateModal.shippedDate,
          dispatch_person_name: dispatchPersonName,
          start_km:
            statusName === 'Dispatch' && isTruthyFlag(statusUpdateModal.meter_not_working)
              ? ''
              : statusUpdateModal.start_km,
          ...(statusUpdateModal.orderBy && { order_by: statusUpdateModal.orderBy }),
          ...(resolvedVehicleType && { vehicle_type: resolvedVehicleType }),
          ...(statusUpdateModal.entityName && { order_by_entity: statusUpdateModal.entityName }),
          ...(statusCategoryForShipping === 'shipped'
            ? buildDispatchLogisticsPayload(statusUpdateModal)
            : {}),
          ...(statusName === 'Delivered' && !meterNotWorkingForPayload
            ? { end_km: statusUpdateModal.end_km }
            : {}),
          ...(statusName === 'Delivered'
            ? buildDeliveredLogisticsPayload(statusUpdateModal, challanForPayload)
            : {}),
          ...(statusName === 'Cancelled' && { reason: statusUpdateModal.cancellationReason || '' }),
          ...(statusName === 'Returned' && { reason: statusUpdateModal.returnReason || '' })
        };

        const isSequentialNext = isSequentialNextStatusTransition(
          statusOptions,
          statusUpdateModal.originalStatus?.name,
          statusUpdateModal.originalStatus?.category,
          statusName
        );

        const receiptFile = statusUpdateModal.diesel_receipt_upload;
        let response;
        if (
          statusCategoryForShipping === 'shipped' &&
          receiptFile instanceof File
        ) {
          const formData = new FormData();
          Object.entries(payload).forEach(([key, value]) => {
            if (value === undefined || value === null) return;
            if (Array.isArray(value)) {
              formData.append(key, value.join(','));
              return;
            }
            formData.append(key, typeof value === 'boolean' || typeof value === 'number' ? String(value) : value);
          });
          formData.append('diesel_receipt_photo', receiptFile, receiptFile.name);
          response = await apiService.putMultipart(
            API_ENDPOINTS.UPDATE_product_status,
            formData
          );
        } else {
          response = await apiService.put(
            API_ENDPOINTS.UPDATE_product_status,
            payload,
            'application/json'
          );
        }

        if (response?.success) {
          setStatusUpdateModal({ 
            open: false, 
            order: null,
            driverName: '',
            vehicleNo: '',
            vehicleType: '',
            delivery_date: new Date().toISOString().slice(0,10),
            generateInvoice: false,
            isCreatingChallan: false,
            challanError: '',
            dispatchPersonName: '',
            customDispatchPerson: '',
            useCustomDispatchPerson: false,
            start_km: '',
            end_km: '',
            diesel_receipt_upload: null,
            diesel_receipt_upload_error: '',
            ...DEFAULT_DISPATCH_LOGISTICS,
            ...DEFAULT_DELIVERED_LOGISTICS,
            cancellationReason: '',
            returnReason: '',
            allowUnpaidCancelFromPlaced: false,
          });
          
          if (onSelectAll) {
            onSelectAll(false);
          }
          setTimeout(() => {
            onRefresh?.();
          }, 100);
        } else {
          console.error('Status update failed:', response?.message);
          setStatusUpdateModal(prev => ({
            ...prev,
            challanError: getApiErrorMessage(response, 'Failed to update status')
          }));
        }
      } catch (error) {
        console.error('Error updating status:', error);
        setStatusUpdateModal(prev => ({
          ...prev,
          challanError: getApiErrorMessage(error, 'An error occurred while updating status')
        }));
      } finally {
        setUpdatingStatus(false);
      }
    };

    const validatePaidPaymentDetails = (modalState) => {
      const apiValidation = validateNewPaidPaymentRequest(modalState);
      if (apiValidation) return apiValidation;
      const mandatoryError = validatePaymentMethodMandatoryFields(modalState);
      if (mandatoryError) return mandatoryError;
      return validatePaidPaymentFormDetails(
        modalState,
        enforcePaymentMethodRequiredFields(modalState.paymentMethodConfig)
      );
    };

    const canSubmitPaidPayment = (modalState) => {
      if (modalState?.paymentOption === 'credit') return false;
      if (!modalState?.orderPaymentDetails) return false;
      if (!String(modalState.creditNote || '').trim()) return false;
      if (isZeroRemainingPayment(modalState)) return true;
      if (!modalState.paymentMethod || !modalState.paymentMethodConfig) return false;
      if (validatePaymentMethodMandatoryFields(modalState)) return false;
      return !validatePaidPaymentFormDetails(
        modalState,
        enforcePaymentMethodRequiredFields(modalState.paymentMethodConfig)
      );
    };

    const canSubmitCreditPayment = (modalState) => {
      if (modalState?.paymentOption !== 'credit') return false;
      if (!String(modalState.creditNote || '').trim()) return false;

      const remainingAmount = Number(modalState?.orderPaymentDetails?.remainingAmount ?? 0);
      const hasInstallmentAmount =
        String(modalState?.installmentAmount ?? '').trim() !== '';

      if (remainingAmount > 0 && hasInstallmentAmount) {
        if (!modalState.paymentMethod || !modalState.paymentMethodConfig) return false;
        if (validatePaymentMethodMandatoryFields(modalState)) return false;
      }

      return true;
    };

    const shouldShowCreditSubmitButton = (modalState) => {
      if (modalState?.paymentOption !== 'credit') return false;
      const remainingAmount = Number(modalState?.orderPaymentDetails?.remainingAmount ?? 0);
      return remainingAmount >= 0;
    };

    const submitPaymentStatusRequest = async (order_id, newStatus, extras = {}) => {
      const normalizedStatus = String(newStatus || '').toLowerCase();

      if (normalizedStatus === 'paid') {
        if (!extras.modalState) {
          throw { success: false, message: 'Payment details are required' };
        }
        return submitNewPaidPaymentStatus(extras.modalState, order_id);
      }

      const userId = localStorage.getItem('user_id');
      const { refundAmount, orderTotalAmount, installmentAmount, creditNote } = extras;
      const isRefunded = normalizedStatus === 'refunded';
      const isCredit = normalizedStatus === 'credit';
      const isFailed = normalizedStatus === 'failed';
      const isPending = normalizedStatus === 'pending';
      const isOutstanding = normalizedStatus === 'outstanding';
      const refundVal =
        refundAmount != null && refundAmount !== '' ? refundAmount : orderTotalAmount ?? 0;

      const payload = {
        order_id,
        payment_status: newStatus,
        user_id: userId,
        ...(isRefunded && { refund_amount: refundVal }),
        ...(isCredit && installmentAmount != null && { installment_amount: installmentAmount }),
        ...((isCredit || isFailed || isPending || isOutstanding || isRefunded) && creditNote && { note: creditNote }),
      };
      return apiService.put(API_ENDPOINTS.UPDATE_payment_status, payload);
    };

    const handlePaymentStatusUpdate = async (
      orderId,
      newStatus,
      order_id,
      refundAmount,
      orderTotalAmount,
      paidPaymentExtras = null
    ) => {
      setUpdatingPayment(true)
      try {
        const response = await submitPaymentStatusRequest(order_id, newStatus, {
          refundAmount,
          orderTotalAmount,
          installmentAmount: paidPaymentExtras?.installmentAmount,
          creditNote: paidPaymentExtras?.creditNote,
          paidPayload: paidPaymentExtras?.paidPayload,
          paymentFiles: paidPaymentExtras?.paymentFiles,
        });
        if (response?.success) {
          toast.showSuccess(response?.message || 'Payment status updated successfully');
          if (shouldAdvanceTabAfterPaymentUpdate(newStatus)) {
            await refreshOrdersAfterPaymentUpdate(paymentUpdateModal.order, response)
          } else {
            await onRefresh()
          }
          setPaymentUpdateModal(closePaymentUpdateModalState())
          setPaymentStatusChoices([])
        } else {
          const message = response?.message || 'Failed to update payment status';
          toast.showError(message);
          setPaymentUpdateModal((prev) => ({ ...prev, paymentError: message }));
        }
      } catch (error) {
        console.error("Error updating payment status:", error)
        const message = extractPaymentApiError(error);
        toast.showError(message);
        setPaymentUpdateModal((prev) => ({
          ...prev,
          paymentError: message,
        }));
      } finally {
        setUpdatingPayment(false)
      }
    }

    const handleConfirmCreditPaymentDetails = async () => {
      const methodValidationError = validatePaymentMethodMandatoryFields(paymentUpdateModal);
      if (methodValidationError) {
        setPaymentUpdateModal((prev) => ({ ...prev, paymentError: methodValidationError }));
        return;
      }

      if (!canSubmitCreditPayment(paymentUpdateModal)) {
        return;
      }

      const remainingAmount = Number(
        paymentUpdateModal.orderPaymentDetails?.remainingAmount ?? 0
      );
      const orderId = paymentUpdateModal.order?.order_id;
      const filledInstallment = paymentUpdateModal.installmentAmount;
      const installmentAmount =
        remainingAmount === 0
          ? 0
          : filledInstallment !== '' && !Number.isNaN(Number(filledInstallment))
            ? Number(filledInstallment)
            : 0;
      const creditNote = paymentUpdateModal.creditNote || '';

      setUpdatingPayment(true);
      setPaymentUpdateModal((prev) => ({ ...prev, paymentError: '' }));

      try {
        const formData = buildNewCreditPaymentFormData(
          paymentUpdateModal,
          orderId,
          installmentAmount,
          creditNote
        );

        const response = await apiService.putMultipart(
          API_ENDPOINTS.UPDATE_new_payment_status,
          formData
        );

        if (response?.success) {
          toast.showSuccess(response?.message || 'Payment status updated successfully');
          await refreshOrdersAfterPaymentUpdate(paymentUpdateModal.order, response);
          setPaymentUpdateModal(closePaymentUpdateModalState());
          setPaymentStatusChoices([]);
        } else {
          const message = response?.message || 'Failed to update payment status';
          toast.showError(message);
          setPaymentUpdateModal((prev) => ({ ...prev, paymentError: message }));
        }
      } catch (error) {
        console.error('Error submitting credit payment:', error);
        const message = extractPaymentApiError(error);
        toast.showError(message);
        setPaymentUpdateModal((prev) => ({ ...prev, paymentError: message }));
      } finally {
        setUpdatingPayment(false);
      }
    };

    const handleConfirmPaidPaymentDetails = async () => {
      const validationError = validatePaidPaymentDetails(paymentUpdateModal);
      if (validationError) {
        setPaymentUpdateModal((prev) => ({ ...prev, paymentError: validationError }));
        return;
      }

      const orderId = paymentUpdateModal.order?.order_id;
      setUpdatingPayment(true);
      setPaymentUpdateModal((prev) => ({ ...prev, paymentError: '' }));

      try {
        const formData = buildNewPaidPaymentFormData(paymentUpdateModal, orderId);
        formData.append('note', paymentUpdateModal.creditNote.trim());
        const response = await apiService.putMultipart(
          API_ENDPOINTS.UPDATE_new_payment_status,
          formData
        );
        if (response?.success) {
          toast.showSuccess(
            response?.message || 'Payment status updated successfully'
          );
          await refreshOrdersAfterPaymentUpdate(paymentUpdateModal.order, response);
          setPaymentUpdateModal(closePaymentUpdateModalState());
          setPaymentStatusChoices([]);
        } else {
          const message = response?.message || 'Failed to update payment status';
          toast.showError(message);
          setPaymentUpdateModal((prev) => ({ ...prev, paymentError: message }));
        }
      } catch (error) {
        console.error('Error submitting paid payment:', error);
        const message = extractPaymentApiError(error);
        toast.showError(message);
        setPaymentUpdateModal((prev) => ({ ...prev, paymentError: message }));
      } finally {
        setUpdatingPayment(false);
      }
    };

    const getSelectedOrdersForBulkPayment = () =>
      filteredData.filter((order) => selectedProducts.includes(order.id));

    const getUnpaidOrdersFromList = (orders = []) =>
      orders.filter((order) => {
        const paymentStatus = getOrderPaymentStatusValue(order);
        const status = normalizeStatusKey(paymentStatus);
        if (status === 'paid') {
          return false;
        }
        const details = getOrderPaymentDetailsFromOrder(order) || {};
        const totalAmount = Number(
          details.totalAmount ?? order?.total_amount ?? order?.total ?? 0
        );
        const paidAmount = Number(
          details.paidAmount ?? order?.amount_paid ?? order?.paid ?? 0
        );
        const remainingAmount = Number(
          details.remainingAmount ?? Math.max(0, totalAmount - paidAmount)
        );
        if (remainingAmount > 0) {
          return true;
        }
        if (status === 'credit' || status === 'outstanding') {
          return true;
        }
        return !isPaymentSatisfiedForOrderAdvance(paymentStatus);
      });

    const getBulkSelectedPaymentSummary = () => {
      const selectedOrders = getSelectedOrdersForBulkPayment();
      return selectedOrders.reduce(
        (acc, order) => {
          const details = getOrderPaymentDetailsFromOrder(order) || {};
          const totalAmount = Number(
            details.totalAmount ?? order?.total_amount ?? order?.total ?? 0
          );
          const paidAmount = Number(
            details.paidAmount ?? order?.amount_paid ?? order?.paid ?? 0
          );
          const remainingAmount = Number(
            details.remainingAmount ?? Math.max(0, totalAmount - paidAmount)
          );

          return {
            totalAmount: acc.totalAmount + (Number.isFinite(totalAmount) ? totalAmount : 0),
            paidAmount: acc.paidAmount + (Number.isFinite(paidAmount) ? paidAmount : 0),
            remainingAmount:
              acc.remainingAmount + (Number.isFinite(remainingAmount) ? remainingAmount : 0),
          };
        },
        { totalAmount: 0, paidAmount: 0, remainingAmount: 0 }
      );
    };

    const getBulkUnpaidPaymentSummary = () => {
      const unpaidOrders = getUnpaidOrdersFromList(getSelectedOrdersForBulkPayment());
      return unpaidOrders.reduce(
        (acc, order) => {
          const details = getOrderPaymentDetailsFromOrder(order) || {};
          const totalAmount = Number(
            details.totalAmount ?? order?.total_amount ?? order?.total ?? 0
          );
          const paidAmount = Number(
            details.paidAmount ?? order?.amount_paid ?? order?.paid ?? 0
          );
          const remainingAmount = Number(
            details.remainingAmount ?? Math.max(0, totalAmount - paidAmount)
          );

          return {
            totalAmount: acc.totalAmount + (Number.isFinite(totalAmount) ? totalAmount : 0),
            paidAmount: acc.paidAmount + (Number.isFinite(paidAmount) ? paidAmount : 0),
            remainingAmount:
              acc.remainingAmount + (Number.isFinite(remainingAmount) ? remainingAmount : 0),
          };
        },
        { totalAmount: 0, paidAmount: 0, remainingAmount: 0 }
      );
    };

    const getBulkPaidPaymentModalState = () => {
      const unpaidSummary = getBulkUnpaidPaymentSummary();
      const baseDetails = bulkPaymentStatusModal.orderPaymentDetails;
      return {
        ...bulkPaymentStatusModal,
        orderPaymentDetails: baseDetails
          ? {
              ...baseDetails,
              totalAmount: unpaidSummary.totalAmount,
              paidAmount: unpaidSummary.paidAmount,
              remainingAmount: unpaidSummary.remainingAmount,
            }
          : null,
      };
    };

    const openPaidPaymentFlowForOrder = (order) => {
      openPaymentModalForOrder(order);
    };

    const openBulkPaidPaymentFlow = (orders = null) => {
      const targetOrders = orders?.length
        ? orders
        : getUnpaidOrdersFromList(getSelectedOrdersForBulkPayment());
      if (targetOrders.length === 0) return;
      const summaryOrder = targetOrders[0];
      setBulkPaymentStatusModal({
        ...DEFAULT_BULK_PAYMENT_STATUS_MODAL,
        open: true,
        selectedPaymentStatus: 'paid',
        step: 'payment_details',
        paymentOption: 'remaining',
        summaryOrder,
      });
      fetchPaymentStatusChoicesForOrder(summaryOrder?.order_id);
      closeActionsMenu();
    };

    const handleConfirmBulkCreditPaymentDetails = async () => {
      const bulkCreditState = {
        ...bulkPaymentStatusModal,
        order: bulkPaymentStatusModal.summaryOrder,
      };

      const bulkMethodValidationError = validatePaymentMethodMandatoryFields(bulkCreditState);
      if (bulkMethodValidationError) {
        setBulkPaymentStatusModal((prev) => ({
          ...prev,
          paymentError: bulkMethodValidationError,
        }));
        return;
      }

      if (!canSubmitCreditPayment(bulkCreditState)) {
        return;
      }

      const unpaidOrders = getUnpaidOrdersFromList(getSelectedOrdersForBulkPayment());
      if (unpaidOrders.length === 0) {
        setBulkPaymentStatusModal((prev) => ({
          ...prev,
          paymentError: 'No unpaid orders in the current selection.',
        }));
        return;
      }

      const remainingAmount = Number(
        bulkPaymentStatusModal.orderPaymentDetails?.remainingAmount ?? 0
      );
      const filledInstallment = bulkPaymentStatusModal.installmentAmount;
      const installmentAmount =
        remainingAmount === 0
          ? 0
          : filledInstallment !== '' && !Number.isNaN(Number(filledInstallment))
            ? Number(filledInstallment)
            : 0;
      const creditNote = bulkPaymentStatusModal.creditNote || '';

      setBulkPaymentStatusModal((prev) => ({ ...prev, isUpdating: true, error: '', paymentError: '' }));

      try {
        const responses = await Promise.all(
          unpaidOrders.map((order) => {
            const orderDetails = getOrderPaymentDetailsFromOrder(order);
            const orderRemaining = Number(orderDetails.remainingAmount ?? 0);
            const orderInstallment = orderRemaining === 0 ? 0 : installmentAmount;
            const orderModalState = {
              ...bulkCreditState,
              order,
              orderPaymentDetails: orderDetails,
            };
            const formData = buildNewCreditPaymentFormData(
              orderModalState,
              order.order_id,
              orderInstallment,
              creditNote
            );
            return apiService.putMultipart(API_ENDPOINTS.UPDATE_new_payment_status, formData);
          })
        );
        const allSuccess = responses.every((response) => response?.success);
        if (allSuccess) {
          toast.showSuccess(responses[0]?.message || 'Payment status updated successfully');
          setBulkPaymentStatusModal({ ...DEFAULT_BULK_PAYMENT_STATUS_MODAL });
          await refreshOrdersAfterPaymentUpdate();
        } else {
          const failed = responses.find((response) => !response?.success);
          const message = failed?.message || 'Some payment status updates failed. Please try again.';
          toast.showError(message);
          setBulkPaymentStatusModal((prev) => ({
            ...prev,
            isUpdating: false,
            error: message,
          }));
        }
      } catch (error) {
        console.error('Error submitting bulk credit payment:', error);
        const message = extractPaymentApiError(error);
        toast.showError(message);
        setBulkPaymentStatusModal((prev) => ({
          ...prev,
          isUpdating: false,
          error: message,
        }));
      }
    };

    const handleConfirmBulkPaidPaymentDetails = async () => {
      const bulkPaidState = {
        ...bulkPaymentStatusModal,
        order: bulkPaymentStatusModal.summaryOrder,
      };
      const unpaidOrders = getUnpaidOrdersFromList(getSelectedOrdersForBulkPayment());
      if (unpaidOrders.length === 0) {
        setBulkPaymentStatusModal((prev) => ({
          ...prev,
          paymentError: 'No unpaid orders in the current selection.',
        }));
        return;
      }

      for (const order of unpaidOrders) {
        const orderDetails = getOrderPaymentDetailsFromOrder(order);
        const orderRemaining = Number(orderDetails.remainingAmount ?? 0);
        if (orderRemaining === 0) {
          continue;
        }
        const orderModalState = {
          ...bulkPaidState,
          order,
          orderPaymentDetails: orderDetails,
          advanceAmount: String(orderRemaining),
          paymentMethodConfig:
            paymentMethodsById[bulkPaidState.paymentMethod] || bulkPaidState.paymentMethodConfig,
        };
        const validationError = validatePaidPaymentDetails(orderModalState);
        if (validationError) {
          setBulkPaymentStatusModal((prev) => ({
            ...prev,
            paymentError: `${order.order_id}: ${validationError}`,
          }));
          return;
        }
      }

      setBulkPaymentStatusModal((prev) => ({ ...prev, isUpdating: true, error: '', paymentError: '' }));

      try {
        const responses = await Promise.all(
          unpaidOrders.map((order) => {
            const orderDetails = getOrderPaymentDetailsFromOrder(order);
            const orderModalState = {
              ...bulkPaidState,
              order,
              orderPaymentDetails: orderDetails,
              advanceAmount: String(orderDetails.remainingAmount),
            };
            const formData = buildNewPaidPaymentFormData(orderModalState, order.order_id);
            formData.append('note', bulkPaidState.creditNote.trim());
            return apiService.putMultipart(
              API_ENDPOINTS.UPDATE_new_payment_status,
              formData
            );
          })
        );
        const allSuccess = responses.every((response) => response?.success);
        if (allSuccess) {
          toast.showSuccess(
            responses[0]?.message || 'Payment status updated successfully'
          );
          setBulkPaymentStatusModal({ ...DEFAULT_BULK_PAYMENT_STATUS_MODAL });
          await refreshOrdersAfterPaymentUpdate(
            bulkPaymentStatusModal.summaryOrder || unpaidOrders[0],
            responses[0]
          );
        } else {
          const failed = responses.find((response) => !response?.success);
          const message =
            failed?.message || 'Some payment status updates failed. Please try again.';
          toast.showError(message);
          setBulkPaymentStatusModal((prev) => ({
            ...prev,
            isUpdating: false,
            error: message,
          }));
        }
      } catch (error) {
        console.error('Error updating bulk paid payment:', error);
        const message = extractPaymentApiError(error);
        toast.showError(message);
        setBulkPaymentStatusModal((prev) => ({
          ...prev,
          isUpdating: false,
          error: message,
        }));
      }
    };

    const handleBulkPaymentStatusUpdate = async () => {
      if (!bulkChallanModal.bulkPaymentStatus) {
        setBulkChallanModal(prev => ({
          ...prev,
          error: 'Please select a payment status'
        }))
        return
      }

      setIsUpdatingBulkPayment(true)
      try {
        const userId = localStorage.getItem("user_id");
        const selectedOrders = filteredData.filter(order => selectedProducts.includes(order.id));
        
        const unpaidOrders = selectedOrders.filter(order => {
          const paymentStatus = (order.payment_status?.name || order.payment_status || '').toLowerCase();
          const isPaid = isPaymentSatisfiedForOrderAdvance(paymentStatus);
          return !isPaid;
        });

        if (unpaidOrders.length === 0) {
          setBulkChallanModal(prev => ({
            ...prev,
            error: 'All selected orders are already paid or outstanding. No payment status update needed.'
          }))
          setIsUpdatingBulkPayment(false)
          return
        }

        const unpaidOrderIds = unpaidOrders.map(order => order.order_id).filter(Boolean);

        const updatePromises = unpaidOrderIds.map(orderId => {
          const payload = {
            order_id: orderId,
            payment_status: bulkChallanModal.bulkPaymentStatus,
            user_id: userId,
          };
          return apiService.put(
            `${API_ENDPOINTS.UPDATE_payment_status}`,
            payload
          );
        });

        const responses = await Promise.all(updatePromises);
        const allSuccess = responses.every(response => response?.success);

        if (allSuccess) {
          setBulkChallanModal(prev => ({
            ...prev,
            bulkPaymentStatus: '',
            error: ''
          }))
          await onRefresh()
        } else {
          setBulkChallanModal(prev => ({
            ...prev,
            error: 'Some payment status updates failed. Please try again.'
          }))
        }
      } catch (error) {
        console.error("Error updating bulk payment status:", error)
        setBulkChallanModal(prev => ({
          ...prev,
          error: 'Error updating payment status. Please try again.'
        }))
      } finally {
        setIsUpdatingBulkPayment(false)
      }
    }

    const handleBulkPaymentStatusUpdateFromModal = async () => {
      if (!bulkPaymentStatusModal.selectedPaymentStatus) {
        setBulkPaymentStatusModal(prev => ({
          ...prev,
          error: 'Please select a payment status'
        }))
        return
      }

      if (bulkPaymentStatusModal.selectedPaymentStatus === 'paid') {
        handleConfirmBulkPaidPaymentDetails();
        return;
      }

      if (
        ['failed', 'pending', 'outstanding'].includes(
          bulkPaymentStatusModal.selectedPaymentStatus
        ) &&
        !String(bulkPaymentStatusModal.creditNote || '').trim()
      ) {
        setBulkPaymentStatusModal((prev) => ({
          ...prev,
          error: 'Please enter a note',
        }));
        return;
      }

      if (bulkPaymentStatusModal.selectedPaymentStatus === 'refunded') {
        if (!String(bulkPaymentStatusModal.creditNote || '').trim()) {
          setBulkPaymentStatusModal((prev) => ({
            ...prev,
            error: 'Please enter a note',
          }));
          return;
        }
        const refundNum = Number(bulkPaymentStatusModal.refundAmount);
        const apiDetails = bulkPaymentStatusModal.orderPaymentDetails;
        const advancePaidTotal = getAdvancePaymentEntries(
          bulkPaymentStatusModal.summaryOrder || bulkPaymentStatusModal
        ).reduce((sum, entry) => sum + Number(entry.advance_amount || 0), 0);
        const paidFallback =
          advancePaidTotal > 0
            ? advancePaidTotal
            : (bulkPaymentStatusModal.summaryOrder?.amount_paid ??
                bulkPaymentStatusModal.summaryOrder?.paid ??
                0);
        const maxRefund = Number(apiDetails?.paidAmount ?? paidFallback);
        if (!Number.isFinite(refundNum) || refundNum <= 0 || refundNum > maxRefund) {
          setBulkPaymentStatusModal((prev) => ({
            ...prev,
            error: 'Please enter a valid refund amount',
          }));
          return;
        }
      }

      setBulkPaymentStatusModal(prev => ({ ...prev, isUpdating: true, error: '' }))
      try {
        const userId = localStorage.getItem("user_id");
        const selectedOrders = filteredData.filter(order => selectedProducts.includes(order.id));
        const selectedOrderIds = selectedOrders.map(order => order.order_id).filter(Boolean);

        if (selectedOrderIds.length === 0) {
          setBulkPaymentStatusModal(prev => ({
            ...prev,
            error: 'No orders selected',
            isUpdating: false
          }))
          return
        }

        const isRefunded = bulkPaymentStatusModal.selectedPaymentStatus === 'refunded';
        const refundVal =
          bulkPaymentStatusModal.refundAmount != null &&
          bulkPaymentStatusModal.refundAmount !== ''
            ? bulkPaymentStatusModal.refundAmount
            : bulkPaymentStatusModal.summaryOrder?.total_amount ?? 0;

        const updatePromises = selectedOrderIds.map(orderId => {
          const payload = {
            order_id: orderId,
            payment_status: bulkPaymentStatusModal.selectedPaymentStatus,
            user_id: userId,
            ...(isRefunded && { refund_amount: refundVal }),
            ...(bulkPaymentStatusModal.creditNote?.trim()
              ? { note: bulkPaymentStatusModal.creditNote.trim() }
              : {}),
          };
          return apiService.put(
            `${API_ENDPOINTS.UPDATE_payment_status}`,
            payload
          );
        });

        const responses = await Promise.all(updatePromises);
        const allSuccess = responses.every(response => response?.success);

        if (allSuccess) {
          setBulkPaymentStatusModal({ ...DEFAULT_BULK_PAYMENT_STATUS_MODAL })
          await onRefresh()
        } else {
          setBulkPaymentStatusModal(prev => ({
            ...prev,
            error: 'Some payment status updates failed. Please try again.',
            isUpdating: false
          }))
        }
      } catch (error) {
        console.error("Error updating bulk payment status:", error)
        setBulkPaymentStatusModal(prev => ({
          ...prev,
          error: 'Error updating payment status. Please try again.',
          isUpdating: false
        }))
      }
    }

    const handleOpenBulkPaymentStatusModal = () => {
      if (isBulkPaymentUpdateDisabled()) return;
      const selectedOrders = getSelectedOrdersForBulkPayment();
      const summaryOrder = selectedOrders[0] || null;
      const paymentStatus = summaryOrder ? getOrderPaymentStatusValue(summaryOrder) : '';
      const orderDetails = summaryOrder ? getOrderPaymentDetailsFromOrder(summaryOrder) : null;
      setBulkPaymentStatusModal({
        ...DEFAULT_BULK_PAYMENT_STATUS_MODAL,
        open: true,
        summaryOrder,
        initialPaymentStatus: paymentStatus,
        refundAmount:
          summaryOrder?.refunded_amount ??
          summaryOrder?.refund_amount ??
          summaryOrder?.total_amount ??
          '',
        orderPaymentDetails: orderDetails,
        ...(paymentStatus === 'credit' ? getCreditPaymentStepState(orderDetails) : {}),
      });
      fetchPaymentStatusChoicesForOrder(summaryOrder?.order_id);
      loadAdvancePaymentMethods();
      closeActionsMenu();
    };

    const handleCloseBulkPaymentStatusModal = () => {
      if (bulkPaymentStatusModal.isUpdating) return;
      setBulkPaymentStatusModal({ ...DEFAULT_BULK_PAYMENT_STATUS_MODAL });
      setPaymentStatusChoices([]);
    };

    const handleOpenStatusModal = (order) => {
      const originalStatus = {
        name: order?.status?.name || order?.status_name || '',
        category: order?.status?.category || ''
      };
      const isCancelledStatus = (originalStatus.name || '').toLowerCase() === 'cancelled' || 
                                (originalStatus.category || '').toLowerCase() === 'cancelled';
      const isPlacedOrdersTab = (statusFilter || '').toLowerCase() === 'placed';

      let nextStatus = null;
      let allowUnpaidCancelFromPlaced = false;
      let defaultCancellationReason = '';

      if (isPlacedOrdersTab && !isCancelledStatus) {
        nextStatus = getNextSequentialStatus(originalStatus.name, originalStatus.category);
        allowUnpaidCancelFromPlaced = true;
        defaultCancellationReason = '';
      } else if (isCancelledStatus) {
        nextStatus = statusOptions.find(opt => 
          (opt.name || '').toLowerCase() === 'returned' || 
          (opt.category || '').toLowerCase() === 'returned'
        );
      } else {
        nextStatus = getNextSequentialStatus(originalStatus.name, originalStatus.category);
      }
      
      const updatedOrder = nextStatus ? {
        ...order,
        status: {
          name: nextStatus.name,
          category: nextStatus.category
        },
        status_name: nextStatus.name
      } : order;

      const dispatchChallan = getOrderChallan(order);
      const isTruckOrder = isOrderByTruck(order);
      
      setStatusUpdateModal({
        open: true,
        order: updatedOrder,
        originalStatus: originalStatus,
        entityName: '',
        orderBy: '',
        driverName: isTruckOrder ? (dispatchChallan?.driver_name || '') : '',
        vehicleNo: isTruckOrder
          ? (
              dispatchChallan?.vehicle_number ||
              dispatchChallan?.vehicle_no ||
              dispatchChallan?.vehicle_name ||
              ''
            )
          : '',
        vehicleType: '',
        shippedDate: new Date().toISOString().slice(0, 10),
        start_km:
          isTruckOrder &&
          dispatchChallan?.start_km !== null &&
          dispatchChallan?.start_km !== undefined
            ? String(dispatchChallan.start_km)
            : '',
        end_km: '',
        ...DEFAULT_DISPATCH_LOGISTICS,
        ...DEFAULT_DELIVERED_LOGISTICS,
        ...getChallanLogisticsDefaults(dispatchChallan),
        diesel_receipt_upload: null,
        diesel_receipt_upload_error: '',
        generateInvoice: false,
        isCreatingChallan: false,
        challanError: '',
        dispatchPersonName: '',
        customDispatchPerson: '',
        useCustomDispatchPerson: false,
        cancellationReason: defaultCancellationReason,
        returnReason: '',
        allowUnpaidCancelFromPlaced,
      });
      setEntityOptions([]);
    };

    const handleDownload = async (type, downloadFunction) => {
      setIsDownloading(true);
      closeActionsMenu();
      
      try {
        await downloadFunction(type);
      } catch (error) {
        console.error('Download failed:', error);
      } finally {
        setIsDownloading(false);
      }
    };

    const handleCloseStatusModal = () => {
      setStatusUpdateModal({
        open: false,
        order: null,
        entityName: '',
        orderBy: '',
        driverName: '',
        vehicleNo: '',
        vehicleType: '',
        delivery_date: new Date().toISOString().slice(0,10),
        shippedDate: new Date().toISOString().slice(0, 10),
        generateInvoice: false,
        isCreatingChallan: false,
        challanError: '',
        dispatchPersonName: '',
        customDispatchPerson: '',
        useCustomDispatchPerson: false,
        start_km: '',
        end_km: '',
        diesel_receipt_upload: null,
        diesel_receipt_upload_error: '',
        ...DEFAULT_DISPATCH_LOGISTICS,
        ...DEFAULT_DELIVERED_LOGISTICS,
        cancellationReason: '',
        returnReason: '',
        allowUnpaidCancelFromPlaced: false,
      });
    };

    const handleStatusModalChange = (field, value) => {
      setStatusUpdateModal(prev => ({
        ...prev,
        [field]: value,
        challanError: ''
      }));
    };

    // `search` is sent to the API from `product.jsx` (list-user-orders). Do not filter again here:
    // a second pass on email / order_id / customer name only hid valid results when the match was
    // in expanded row data (e.g. product name, SKU on order line items).
    const filteredData = products;

    const formatCurrency = (amount) => {
      return `₹${parseFloat(amount).toLocaleString('en-IN')}`;
    };

    const getStatusIcon = (status) => {
      const category = typeof status === 'object' && status !== null && 'category' in status
        ? status.category
        : status
      return getIconForCategory(category)
    };


    if (isLoading) {
      const skeletonRowCount = Math.min(Math.max(Number(pageSize) || 10, 5), 10)
      return (
        <Box sx={{ width: "100%", p: 2 }} aria-busy="true" aria-live="polite">
          <TableContainer
            component={Paper}
            sx={(theme) => ({
              maxHeight: 600,
              width: "100%",
              maxWidth: "100%",
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
              borderRadius: 2,
              scrollbarWidth: "thin",
              scrollbarColor: theme.palette.mode === "dark" ? "#555 #1e1e1e" : "#c1c1c1 #f1f1f1",
              "&::-webkit-scrollbar": { height: 8, width: 8 },
              "&::-webkit-scrollbar-track": {
                background: theme.palette.mode === "dark" ? "#1e1e1e" : "#f1f1f1",
                borderRadius: 8,
              },
              "&::-webkit-scrollbar-thumb": {
                background: theme.palette.mode === "dark" ? "#555" : "#c1c1c1",
                borderRadius: 8,
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: theme.palette.mode === "dark" ? "#777" : "#a8a8a8",
              },
            })}
          >
            <Table
              stickyHeader
              sx={{
                width: "100%",
                minWidth: { xs: 1100, lg: "100%" },
                tableLayout: { xs: "auto", lg: "fixed" },
              }}
            >
              <TableHead
                sx={(theme) => ({
                  '& .MuiTableCell-root': {
                    backgroundColor: theme.palette.grey[200],
                  },
                  '& .MuiTableCell-stickyHeader': {
                    backgroundColor: theme.palette.grey[200],
                    zIndex: 10,
                  },
                })}
              >
                <TableRow sx={{ backgroundColor: (theme) => theme.palette.grey[200] }}>
                  <StyledTableCell sx={{ width: 40, minWidth: 40, backgroundColor: (theme) => theme.palette.grey[200] }} />
                  <StyledTableCell align="center" sx={{ width: 64, minWidth: 64, backgroundColor: (theme) => theme.palette.grey[200] }}>Sr. No.</StyledTableCell>
                  <StyledTableCell align="center" sx={{ width: 155, minWidth: { xs: 150, lg: 155 }, backgroundColor: (theme) => theme.palette.grey[200] }}>Order ID</StyledTableCell>
                  <StyledTableCell align="center" sx={{ width: 150, minWidth: { xs: 130, lg: 150 }, maxWidth: 220, backgroundColor: (theme) => theme.palette.grey[200] }}>Customer Name</StyledTableCell>
                  <StyledTableCell align="center" sx={{ width: 200, minWidth: 140, whiteSpace: "normal", backgroundColor: (theme) => theme.palette.grey[200] }}>Company Name</StyledTableCell>
                  <StyledTableCell align="center" sx={{ width: 110, minWidth: { xs: 88, md: 100 }, backgroundColor: (theme) => theme.palette.grey[200] }}>Status</StyledTableCell>
                  <StyledTableCell align="center" sx={{ width: 70, minWidth: { xs: 72, md: 88 }, backgroundColor: (theme) => theme.palette.grey[200] }}>Total Amount</StyledTableCell>
                  <StyledTableCell align="center" sx={{ width: 180, minWidth: 180, position: "sticky", top: 0, right: 0, zIndex: 12, backgroundColor: (theme) => theme.palette.grey[200] }}>Actions</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.from({ length: skeletonRowCount }).map((_, index) => (
                  <TableRow key={`app-order-skeleton-${index}`} sx={{ pointerEvents: "none" }}>
                    <StyledTableCell sx={{ width: 40, minWidth: 40 }}>
                      <Skeleton animation="wave" variant="rounded" width={18} height={18} />
                    </StyledTableCell>
                    <StyledTableCell align="center" sx={{ width: 64, minWidth: 64 }}>
                      <Skeleton animation="wave" variant="text" width={24} height={24} sx={{ mx: "auto" }} />
                    </StyledTableCell>
                    <StyledTableCell align="center" sx={{ width: 155, minWidth: { xs: 150, lg: 155 } }}>
                      <Skeleton animation="wave" variant="text" width="80%" height={24} sx={{ mx: "auto" }} />
                    </StyledTableCell>
                    <StyledTableCell align="center" sx={{ width: 150, minWidth: { xs: 130, lg: 150 }, maxWidth: 220 }}>
                      <Skeleton animation="wave" variant="text" width="70%" height={24} sx={{ mx: "auto" }} />
                    </StyledTableCell>
                    <StyledTableCell align="center" sx={{ width: 200, minWidth: 140 }}>
                      <Skeleton animation="wave" variant="text" width="85%" height={24} sx={{ mx: "auto" }} />
                    </StyledTableCell>
                    <StyledTableCell align="center" sx={{ width: 110, minWidth: { xs: 88, md: 100 } }}>
                      <Skeleton animation="wave" variant="rounded" width={72} height={24} sx={{ mx: "auto", borderRadius: 4 }} />
                    </StyledTableCell>
                    <StyledTableCell align="center" sx={{ width: 70, minWidth: { xs: 72, md: 88 } }}>
                      <Skeleton animation="wave" variant="text" width={56} height={24} sx={{ mx: "auto" }} />
                    </StyledTableCell>
                    <StyledTableCell align="center" sx={{ width: 180, minWidth: 180, position: "sticky", right: 0, backgroundColor: "background.paper" }}>
                      <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
                        <Skeleton animation="wave" variant="circular" width={28} height={28} />
                        <Skeleton animation="wave" variant="circular" width={28} height={28} />
                        <Skeleton animation="wave" variant="circular" width={28} height={28} />
                      </Box>
                    </StyledTableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )
    }

    return (
      <Box sx={{ width: "100%", p: 2 }}>
        {/* Export Controls */}
        {selectedProducts.length > 0 && (
          <Box sx={{ mb: 2, p: 2, backgroundColor: alpha(theme.palette.primary.main, 0.1), borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="body1" fontWeight={600}>
                {selectedProducts.length} product{selectedProducts.length > 1 ? 's' : ''} selected
              </Typography>
              {getEligibleProductsCount() > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1, display: 'block' }}>
                  {getEligibleProductsCount()} item Selected
                </Typography>
              )}
            </Box>
            <Box>
              <Button variant="contained" size="small" startIcon={isDownloading ? <CircularProgress size={20} sx={{ color: "#ffffff" }} /> : <FileDownload />} onClick={openActionsMenu} sx={{ textTransform: 'none' }} disabled={isDownloading}>
                {isDownloading ? (<span style={{ color: '#fff7f7', fontWeight: 'bold' }}>Downloading..</span>) : ('Actions')}
              </Button>
              <Menu anchorEl={actionsAnchorEl} open={isActionsMenuOpen} onClose={closeActionsMenu} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                <ListSubheader disableSticky>Export</ListSubheader>
                <MenuItem onClick={() => handleDownload('excel', onExport)} disabled={isDownloading}>
                  <ListItemIcon><FileDownload fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Export to Excel" />
                </MenuItem>
                <MenuItem onClick={() => handleDownload('csv', onExport)} disabled={isDownloading}>
                  <ListItemIcon><FileDownload fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Export to CSV" />
                </MenuItem>
                <Divider />
                <ListSubheader disableSticky>Documents ZIP</ListSubheader>
                <MenuItem onClick={() => handleDownload('invoice', onZipDownload)} disabled={shouldDisableDocumentsAndEmail() || isDownloading || (statusFilter || '').toLowerCase() === 'dispatch'}>
                  <ListItemIcon><ReceiptIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Invoices ZIP" />
                </MenuItem>
                <MenuItem onClick={() => handleDownload('challan', onZipDownload)} disabled={shouldDisableDocumentsAndEmail() || isDownloading}>
                  <ListItemIcon><ChallanIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Challans ZIP" />
                </MenuItem>
                <MenuItem onClick={() => handleDownload('both', onZipDownload)} disabled={shouldDisableDocumentsAndEmail() || isDownloading || (statusFilter || '').toLowerCase() === 'dispatch'}>
                  <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Both ZIP" />
                </MenuItem>
                <Divider />
                <ListSubheader disableSticky>Email</ListSubheader>
                <MenuItem onClick={handleOpenEmailDialog} disabled={shouldDisableDocumentsAndEmail() || isDownloading}>
                  <ListItemIcon><EmailIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Send via Email" />
                </MenuItem>
                <Divider />
                <ListSubheader disableSticky>Operations</ListSubheader>
                <MenuItem
                  onClick={handleOpenBulkPaymentStatusModal}
                  disabled={selectedProducts.length === 0 || isDownloading || isBulkPaymentUpdateDisabled()}
                >
                  <ListItemIcon><Payment fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Bulk Payment Status Update" />
                </MenuItem>
                <MenuItem onClick={() => { closeActionsMenu(); handleBulkDeliveryChallan(); }} disabled={selectedProducts.length === 0 || ['Returned', 'Cancelled'].includes(statusFilter) || isDownloading}>
                  <ListItemIcon><DeliveryIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary={statusFilter === 'Dispatch' ? 'Status Update' : 'Bulk Delivery Status'} />
                </MenuItem>
              </Menu>
            </Box>
          </Box>
        )}

       <TableContainer
          component={Paper}
          sx={(theme) => ({
            maxHeight: 600,
            width: "100%",
            maxWidth: "100%",
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
            borderRadius: 2,
            scrollbarWidth: "thin",
            scrollbarColor: theme.palette.mode === "dark" ? "#555 #1e1e1e" : "#c1c1c1 #f1f1f1",
            "&::-webkit-scrollbar": { height: 8, width: 8 },
            "&::-webkit-scrollbar-track": {
              background: theme.palette.mode === "dark" ? "#1e1e1e" : "#f1f1f1",
              borderRadius: 8,
            },
            "&::-webkit-scrollbar-thumb": {
              background: theme.palette.mode === "dark" ? "#555" : "#c1c1c1",
              borderRadius: 8,
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: theme.palette.mode === "dark" ? "#777" : "#a8a8a8",
            },
          })}
        >
          <Table
            stickyHeader
            sx={{
              width: "100%",
              minWidth: { xs: 1100, lg: "100%" },
              tableLayout: { xs: "auto", lg: "fixed" },
              "& .MuiTableCell-root": {
                verticalAlign: "middle",
                [theme.breakpoints.down("md")]: {
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  padding: "10px 8px",
                  fontSize: "0.8125rem",
                },
              },
            }}
          >
            <TableHead
              sx={(theme) => ({
                '& .MuiTableCell-root': {
                  backgroundColor: theme.palette.grey[200],
                },
                '& .MuiTableCell-stickyHeader': {
                  backgroundColor: theme.palette.grey[200],
                  zIndex: 10,
                },
              })}
            >
              <TableRow sx={{ backgroundColor: (theme) => theme.palette.grey[200] }}>
                <StyledTableCell sx={{ width: 40, minWidth: 40, backgroundColor: (theme) => theme.palette.grey[200] }}>
                  <Checkbox checked={isAllSelected} indeterminate={selectedProducts.length > 0 && !isAllSelected} onChange={handleSelectAllToggle} color="primary" />
                </StyledTableCell>
                <StyledTableCell align="center" sx={{ width: 64, minWidth: 64, backgroundColor: (theme) => theme.palette.grey[200] }}>Sr. No.</StyledTableCell>
                <StyledTableCell
                  align="center"
                  sx={{
                    width: 155,
                    minWidth: { xs: 150, lg: 155 },
                    backgroundColor: (theme) => theme.palette.grey[200],
                  }}
                >
                  Order ID
                </StyledTableCell>
                <StyledTableCell
                  align="center"
                  sx={{
                    width: 150,
                    minWidth: { xs: 130, lg: 150 },
                    maxWidth: 220,
                    backgroundColor: (theme) => theme.palette.grey[200],
                  }}
                >
                  Customer Name
                </StyledTableCell>
                <StyledTableCell align="center" sx={{ width: 200, minWidth: 140, whiteSpace: "normal", backgroundColor: (theme) => theme.palette.grey[200] }}>Company Name</StyledTableCell>
                <StyledTableCell align="center" sx={{ width: 110, minWidth: { xs: 88, md: 100 }, backgroundColor: (theme) => theme.palette.grey[200] }}>Status</StyledTableCell>
                <StyledTableCell align="center" sx={{ width: 70, minWidth: { xs: 72, md: 88 }, backgroundColor: (theme) => theme.palette.grey[200] }}>Total Amount</StyledTableCell>
                <StyledTableCell align="center" sx={{ width: 180, minWidth: 180, position: "sticky", top: 0, right: 0, zIndex: 12, backgroundColor: (theme) => theme.palette.grey[200] }}>Actions</StyledTableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ border: 'none' }}>
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                      <Typography color="text.secondary">No Orders found</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                filteredData.map((order, index) => {
                  const isSelected = isProductSelected(order.id)
                  const isCancelRequested = order?.is_cancel_requested === true
                  const isReturnRequested = order?.is_return_requested === true
                  const lastOpenedId = getLastOpenedAppOrderId()
                  const isLastOpened =
                    !!lastOpenedId &&
                    (String(order.order_id) === String(lastOpenedId) ||
                      String(order.id) === String(lastOpenedId))
                  
                  return (
                    <React.Fragment key={order.id}>
                      <StyledTableRow
                        hover
                        selected={isSelected}
                        highlighted={isLastOpened ? 1 : 0}
                        onClick={() => openOrderDetails(order)}
                        
                        sx={
                          
                          isCancelRequested
                            ? {
                                borderLeft: "4px solid #ef4444",
                              }
                            : isReturnRequested
                              ? {
                                  borderLeft: "4px solid #3b82f6",
                                }
                              : undefined

                  
                        }
                      >
                        <StyledTableCell
                          onClick={(e) => e.stopPropagation()}
                          sx={{ width: 40, minWidth: 40 }}
                        >
                          <Checkbox checked={isSelected} onChange={(e) => handleProductSelection(order.id, e.target.checked)} color="primary" size="small" />
                        </StyledTableCell>
                        <StyledTableCell align="center" sx={{ width: 64, minWidth: 64 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>{(currentPage - 1) * pageSize + index + 1}</Typography>
                        </StyledTableCell>
                        <StyledTableCell
                          align="center"
                          variant="body2"
                          sx={{
                            width: 155,
                            minWidth: { xs: 150, lg: 155 },
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, minWidth: 0 }}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                openOrderDetails(order);
                              }}
                              sx={{
                                p: 0.25,
                                flexShrink: 0,
                              }}
                            >
                              <KeyboardArrowRightIcon fontSize="small" />
                            </IconButton>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Typography
                                  fontWeight={500}
                                  sx={{
                                    fontSize: { xs: "0.75rem", md: "0.875rem" },
                                    wordBreak: "break-word",
                                    lineHeight: 1.35,
                                  }}
                                >
                                  {order.order_id}
                                </Typography>
                              </Box>
                              {isCancelRequested ? (
                                <Chip
                                  label="Cancel Requested"
                                  size="small"
                                  sx={{
                                    mt: 0.5,
                                    height: 18,
                                    fontSize: "10px",
                                    bgcolor: "#fee2e2",
                                    color: "#b91c1c",
                                    fontWeight: 600,
                                  }}
                                />
                              ) : isReturnRequested ? (
                                <Chip
                                  label="Return Requested"
                                  size="small"
                                  sx={{
                                    mt: 0.5,
                                    height: 18,
                                    fontSize: "10px",
                                    bgcolor: "#dbeafe",
                                    color: "#1d4ed8",
                                    fontWeight: 600,
                                  }}
                                />
                              ) : null}
                            </Box>
                          </Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mt: 0.25, wordBreak: "break-word", lineHeight: 1.35 }}
                          >
                            {order.order_date_formatted || "Date N/A"}
                          </Typography>
                        </StyledTableCell>
                        <StyledTableCell
                          align="center"
                          style={{ fontSize: "14px" }}
                          variant="body2"
                          sx={{
                            width: 150,
                            minWidth: { xs: 130, lg: 150 },
                            maxWidth: 220,
                          }}
                        >
                          <Typography variant="body2" sx={{ wordBreak: "break-word", lineHeight: 1.35 }}>
                            {order.user.full_name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary" sx={{ wordBreak: "break-word", display: "block" }}>
                            {order.user.mobile_number}
                          </Typography>
                          {(order.farmer_name || getAdvancePaymentEntries(order)[0]?.order?.farmer_name) && (
                            <Tooltip
                              title={order.farmer_name || getAdvancePaymentEntries(order)[0]?.order?.farmer_name}
                              arrow
                              placement="top"
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  mt: 0.5,
                                  color: theme.palette.info.main,
                                  fontWeight: 600,
                                  fontStyle: 'italic',
                                  cursor: 'help',
                                  maxWidth: '100%',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                Behalf of: {order.farmer_name || getAdvancePaymentEntries(order)[0]?.order?.farmer_name}
                              </Typography>
                            </Tooltip>
                          )}
                        </StyledTableCell>
                        <StyledTableCell align="center" style={{ fontSize: "14px" }} variant="body2" sx={{ width: 200, minWidth: 140, whiteSpace: "normal", wordBreak: "break-word" }}>
                          <Typography variant="body2">
                            {order?.company?.name || order?.company_name || order?.company_details?.name || order?.items?.[0]?.company?.name || order?.items?.[0]?.company_name || 'N/A'}
                          </Typography>
                        </StyledTableCell>
                        <StyledTableCell align="center" sx={{ width: 110, minWidth: { xs: 88, md: 100 } }}>
                          <Box sx={{ display: "flex", justifyContent: "center" }}>
                            <StatusChip label={order.status.name} status={order.status.category?.toLowerCase()} size="small" />
                          </Box>
                        </StyledTableCell>
                        <StyledTableCell align="center" sx={{ width: 70, minWidth: { xs: 72, md: 88 } }}>
                          <Typography sx={{ fontWeight: 500, fontSize: "14px" }}>{formatCurrency(order.total_amount)}</Typography>
                        </StyledTableCell>
                        <StyledTableCell
                          align="center"
                          onClick={(e) => e.stopPropagation()}
                          sx={(theme) => ({
                            width: 180,
                            minWidth: 180,
                            position: "sticky",
                            right: 0,
                            zIndex: 4,
                            backgroundColor: theme.palette.background.paper,
                          })}
                        >
                         <Box
  display="flex"
  gap={0.5}
  sx={{
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    flexWrap: "nowrap",
  }}
>
  {showCancelRequestActions && onApproveCancelRequest && onRejectCancelRequest ? (
    <>
      <Button size="small" color="success" variant="outlined" disabled={updatingCancelRequestOrderId === (order?.order_id ?? order?.id)} onClick={(e) => { e.stopPropagation(); onApproveCancelRequest(order); }} sx={{ textTransform: 'none', fontWeight: 600 }}>
        {updatingCancelRequestOrderId === (order?.order_id ?? order?.id) ? <CircularProgress size={18} /> : 'Accepted'}
      </Button>
      <Button size="small" color="error" variant="outlined" disabled={updatingCancelRequestOrderId === (order?.order_id ?? order?.id)} onClick={(e) => { e.stopPropagation(); onRejectCancelRequest(order); }} sx={{ textTransform: 'none', fontWeight: 600 }}>
        Rejected
      </Button>
    </>
  ) : showReturnRequestActions && onApproveReturnRequest && onRejectReturnRequest ? (
    <>
      <Button size="small" color="success" variant="outlined" disabled={updatingReturnRequestOrderId === (order?.order_id ?? order?.id)} onClick={(e) => { e.stopPropagation(); onApproveReturnRequest(order); }} sx={{ textTransform: 'none', fontWeight: 600 }}>
        {updatingReturnRequestOrderId === (order?.order_id ?? order?.id) ? <CircularProgress size={18} /> : 'Accepted'}
      </Button>
      <Button size="small" color="error" variant="outlined" disabled={updatingReturnRequestOrderId === (order?.order_id ?? order?.id)} onClick={(e) => { e.stopPropagation(); onRejectReturnRequest(order); }} sx={{ textTransform: 'none', fontWeight: 600 }}>
        Rejected
      </Button>
    </>
  ) : (
    <>
      {/* Update Status — always visible */}
      <Tooltip
        title={
          ['cancelled', 'returned'].includes((statusFilter || '').toLowerCase())
            ? 'Status update is not available on Cancelled or Returned tabs'
            : !shouldEnableButton?.('update_status')
              ? "You don't have permission to update status"
              : "Update Status"
        }
      >
        <span>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); handleOpenStatusModal(order); }}
            disabled={
              !shouldEnableButton?.('update_status') ||
              ['cancelled', 'returned'].includes((statusFilter || '').toLowerCase())
            }
            sx={{
              opacity:
                !shouldEnableButton?.('update_status') ||
                ['cancelled', 'returned'].includes((statusFilter || '').toLowerCase())
                  ? 0.5 : 1
            }}
          >
            <UpdateIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      {/* Update Payment — always visible */}
      <Tooltip title={getPaymentUpdateTooltip(order)}>
        <span>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); openPaymentModalForOrder(order); }}
            disabled={!shouldEnableButton?.('update_payment') || isPaymentUpdateDisabled(order)}
            sx={{
              opacity: !shouldEnableButton?.('update_payment') || isPaymentUpdateDisabled(order) ? 0.5 : 1,
            }}
          >
            <Payment fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      {/* Send Email — always takes up space, hidden on placed/confirmed/processing */}
      <span style={{
        visibility: ['placed', 'confirmed', 'processing'].includes((statusFilter || '').toLowerCase())
          ? 'hidden' : 'visible'
      }}>
        <Tooltip title={!shouldEnableButton?.('send_email') ? "You don't have permission to send email" : "Send Email"}>
          <span>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); openSingleOrderEmail(order); }}
              disabled={!shouldEnableButton?.('send_email')}
              sx={{ opacity: !shouldEnableButton?.('send_email') ? 0.5 : 1 }}
            >
              <EmailIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </span>

      {/* View Invoice — not shown on Dispatch tab */}
      {!['dispatch', 'shipped'].includes((statusFilter || '').toLowerCase()) && (
      <span style={{
        visibility: extractInvoice(order)?.path ? 'visible' : 'hidden'
      }}>
        <Tooltip title={!shouldEnableButton?.('view_invoice') ? "You don't have permission to view invoice" : "View Invoice"}>
          <span>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); handleViewInvoice(order); }}
              disabled={!shouldEnableButton?.('view_invoice')}
              color="primary"
              sx={{ opacity: !shouldEnableButton?.('view_invoice') ? 0.5 : 1 }}
            >
              <ReceiptIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </span>
      )}

      {/* View Delivery Challan — always takes up space, invisible when no challan */}
      <span style={{
        visibility: extractDeliveryChallan(order)?.path ? 'visible' : 'hidden'
      }}>
        <Tooltip title={!shouldEnableButton?.('view_delivery_challan') ? "You don't have permission to view delivery challan" : "View Delivery Challan"}>
          <span>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); handleViewDeliveryChallan(order); }}
              disabled={!shouldEnableButton?.('view_delivery_challan')}
              color="secondary"
              sx={{ opacity: !shouldEnableButton?.('view_delivery_challan') ? 0.5 : 1 }}
            >
              <ChallanIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </span>
    </>
  )}
</Box>
                        </StyledTableCell>
                      </StyledTableRow>
                    </React.Fragment>
                  )
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Rest of the modals remain the same - Status Update Modal, Payment Status Update Modal, etc. */}
        {/* Status Update Modal */}
        <Dialog 
          open={statusUpdateModal.open} 
          onClose={handleCloseStatusModal}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <UpdateIcon color="primary" />
              <Typography variant="h6">Update Order Status</Typography>
            </Box>
            {statusUpdateModal.order && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Order ID: {statusUpdateModal.order.order_id}
              </Typography>
            )}
          </DialogTitle>
          
          <DialogContent
          sx={{ /* Firefox */
            scrollbarWidth: "thin",
            scrollbarColor:
              theme.palette.mode === "dark"
                ? "#555 #1e1e1e"
                : "#c1c1c1 #f1f1f1",
            /* Chrome, Edge, Safari */
            "&::-webkit-scrollbar": {
              width: 8,
            },
            "&::-webkit-scrollbar-track": {
              background:
                theme.palette.mode === "dark" ? "#1e1e1e" : "#f1f1f1",
              borderRadius: 8,
            },
            "&::-webkit-scrollbar-thumb": {
              background:
                theme.palette.mode === "dark" ? "#555" : "#c1c1c1",
              borderRadius: 8,
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background:
                theme.palette.mode === "dark" ? "#777" : "#a8a8a8",
            },
            }}>
            {statusUpdateModal.challanError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {statusUpdateModal.challanError}
              </Alert>
            )}

            {statusUpdateModal.isCreatingChallan && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  Creating delivery challan...
                </Box>
              </Alert>
            )}

          {/* Payment Status Warning */}
          {(() => {
            const paymentStatus = (statusUpdateModal.order?.payment_status?.name || statusUpdateModal.order?.payment_status || '').toLowerCase();
            const isPaid = isPaymentSatisfiedForOrderAdvance(paymentStatus);

            const selectedStatus = (statusUpdateModal.order?.status?.name || statusUpdateModal.order?.status_name || '').toLowerCase();
            const originalStatusName = (statusUpdateModal.originalStatus?.name || '').toLowerCase();
            const originalStatusCategory = (statusUpdateModal.originalStatus?.category || '').toLowerCase();
            const isOriginallyDelivered = originalStatusName === 'delivered' || originalStatusCategory === 'delivered';
            const isReturnedSelected = selectedStatus === 'returned';

            // Show payment warning for unpaid orders, but NOT when user has selected
            // Returned (in that case we rely on the Returned-specific validation message).
            if (!isPaid && selectedStatus !== 'returned' && !(isOriginallyDelivered && isReturnedSelected)) {
              return (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight={600}>
                    Payment Required
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                    This order must be paid before it can be moved to any status other than "Orders". 
                    Please update the payment status first.
                  </Typography>
                  {/* <Button
                    size="small"
                    variant="outlined"
                    sx={{ mt: 1.5, textTransform: 'none' }}
                    startIcon={<Payment fontSize="small" />}
                    onClick={() => openPaidPaymentFlowForOrder(statusUpdateModal.order)}
                    disabled={!shouldEnableButton?.('update_payment')}
                  >
                    Update Payment
                  </Button> */}
                </Alert>
              );
            }
            return null;
          })()}

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Order Status</InputLabel>
            <Select
              value={
                statusUpdateModal.order?.status?.name || statusUpdateModal.order?.status_name || ''
              }
              label="Order Status"
              onChange={(e) => {
                const selectedOption = statusOptions.find(opt => opt.name === e.target.value);
                handleStatusModalChange('order', { 
                  ...statusUpdateModal.order, 
                  status: {
                    name: e.target.value,
                    category: selectedOption?.category || ''
                  },
                  status_name: e.target.value
                });
              }}
              disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
            >
              {statusOptions
                .map((option) => {
                // Use original status for validation, not the auto-selected next status
                // This ensures we only show the next sequential status, not skip ahead
                const originalStatusCategory = (statusUpdateModal.originalStatus?.category || '').toLowerCase();
                const originalStatusName = (statusUpdateModal.originalStatus?.name || '').toLowerCase();
                
                // Get current status category and name - try multiple sources (for display)
                let currentStatusCategory = statusUpdateModal.order?.status?.category?.toLowerCase();
                const currentStatusName = statusUpdateModal.order?.status?.name || statusUpdateModal.order?.status_name;
                
                // If category not available, find it from statusOptions by name
                if (!currentStatusCategory && currentStatusName) {
                  const currentStatusOption = statusOptions.find(opt => opt.name === currentStatusName);
                  currentStatusCategory = currentStatusOption?.category?.toLowerCase() || '';
                }
                
                // Get statuses to disable based on ORIGINAL status (not the auto-selected one)
                const statusesToDisable = getStatusesToDisable(originalStatusCategory, originalStatusName);
                
                // Check payment status
                const paymentStatus = (statusUpdateModal.order?.payment_status?.name || statusUpdateModal.order?.payment_status || '').toLowerCase();
                const isPaid = isPaymentSatisfiedForOrderAdvance(paymentStatus);
                
                // If not paid, only allow "Orders" status (name: "Orders"), except Cancelled
                // from the Placed-tab flow (allowUnpaidCancelFromPlaced).
                const isOrdersStatus = option.name === 'Orders';
                const isCancelledOption = (option.name || '').toLowerCase() === 'cancelled';
                const shouldDisableByPayment =
                  !isPaid &&
                  !isOrdersStatus &&
                  !(statusUpdateModal.allowUnpaidCancelFromPlaced && isCancelledOption);
                
                // Check if this option should be disabled
                const optionCategory = (option.category || '').toLowerCase();
                // Only disable if category is valid and in the disabled list
                const shouldDisableBySequence = optionCategory && statusesToDisable.includes(optionCategory);
                const allowPlacedTabCancelledPick =
                  statusUpdateModal.allowUnpaidCancelFromPlaced && isCancelledOption;
                const shouldDisable =
                  (shouldDisableByPayment || shouldDisableBySequence) && !allowPlacedTabCancelledPick;

                // Placed tab: keep Confirmed visible so it shows as the default selected value
                const isPlacedTabFlow = !!statusUpdateModal.allowUnpaidCancelFromPlaced;
                const isConfirmedOption = (option.name || '').toLowerCase() === 'confirmed';
                // Dispatch tab: hide Cancelled option
                const isDispatchTab = (statusFilter || '').toLowerCase() === 'dispatch';
                if (isDispatchTab && isCancelledOption) return null;
                if (shouldDisable && !(isPlacedTabFlow && isConfirmedOption)) return null;
                
                return (
                  <MenuItem 
                    key={option.id || option.name} 
                    value={option.name}
                    disabled={shouldDisable}
                    sx={shouldDisable ? { 
                      opacity: 0.5,
                      backgroundColor: theme.palette.grey[100],
                      // '&:hover': {
                      //   backgroundColor: theme.palette.grey[100],
                      //   cursor: 'not-allowed'
                      // }
                    } : {}}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      {getIconForCategory(option.category)}
                      <StatusChip 
                        status={option.category?.toLowerCase()}
                        label={option.name}
                        size="small"
                      />
                      {!shouldDisable && statusUpdateModal.order?.status?.name === option.name && (
                        <CheckCircle 
                          color="primary" 
                          sx={{ 
                            ml: 'auto', 
                            fontSize: '16px' 
                          }} 
                        />
                      )}
                      {shouldDisableByPayment && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontSize: '0.7rem' }}>
                          Payment required
                        </Typography>
                      )}
                      {shouldDisableBySequence && !shouldDisableByPayment && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontSize: '0.7rem' }}>
                          Sequential only
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          

          {/* Conditional reason field for Cancelled status */}
          {(statusUpdateModal.order?.status?.name === 'Cancelled' || statusUpdateModal.order?.status_name === 'Cancelled') && (
            <TextField
              fullWidth
              label="Reason"
              value={statusUpdateModal.cancellationReason}
              onChange={(e) => handleStatusModalChange('cancellationReason', e.target.value)}
              disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
              required
              placeholder="Enter reason for cancellation"
              sx={{ mt: 2 }}
              multiline
              rows={3}
            />
          )}

          {/* Conditional reason field for Returned status */}
          {(statusUpdateModal.order?.status?.name === 'Returned' || statusUpdateModal.order?.status_name === 'Returned') && (
            <TextField
              fullWidth
              label="Reason"
              value={statusUpdateModal.returnReason}
              onChange={(e) => handleStatusModalChange('returnReason', e.target.value)}
              disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
              required
              placeholder="Enter reason for returned"
              sx={{ mt: 2 }}
              multiline
              rows={3}

              
            />
          )}

            {/* Conditional fields for shipping */}
            {(statusUpdateModal.order?.status?.category || statusUpdateModal.order?.status)?.toLowerCase() === 'shipped' && (
              <Box sx={{ mt: 3 }}>
                <Box sx={{ 
                  p: 2, 
                  backgroundColor: alpha(theme.palette.info.main, 0.1),
                  borderRadius: 2,
                  mb: 2
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <DeliveryIcon color="info" />
                    <Typography variant="subtitle1" fontWeight={600}>
                      Delivery Information Required
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Please provide delivery details to create the delivery challan
                  </Typography>
                </Box>

                <Grid container spacing={2}>

                    <Grid item xs={12}>
                    <FormControl fullWidth required>
                      <InputLabel>Order By</InputLabel>
                      <Select
                        value={statusUpdateModal.orderBy}
                        onChange={(e) => handleStatusModalChange('orderBy', e.target.value)}
                        disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                        label="Order By"
                      >
                        <MenuItem value="">
                          <em>Select order by</em>
                        </MenuItem>
                        {orderByChoices.map((choice) => (
                          <MenuItem key={choice.key} value={choice.key}>
                            {choice.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>


                {/* Entity Name */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Delivery Partner Name"
                    value={statusUpdateModal.entityName}
                    onChange={(e) => {
                      const lettersOnly = e.target.value.replace(/[^A-Za-z ]/g, '');
                      handleStatusModalChange('entityName', lettersOnly);
                    }}
                    disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                    required
                    placeholder="Enter Delivery Partner"
                  />
                </Grid>

              
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Driver/Authorized Name"
                    value={statusUpdateModal.driverName}
                    onChange={(e) => {
                      const lettersOnly = e.target.value.replace(/[^A-Za-z ]/g, '');
                      handleStatusModalChange('driverName', lettersOnly);
                    }}
                    disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                    required
                    placeholder="Driver/Authorized Name"
                    // error={statusUpdateModal.challanError.includes('Driver name')}
                  />
                </Grid>
                  {String(statusUpdateModal.orderBy || '').trim().toLowerCase() === 'truck' && (
                  <Grid item xs={12}>
                    <FormControl fullWidth required>
                      <InputLabel>Vehicle Type</InputLabel>
                      <Select
                        value={statusUpdateModal.vehicleType}
                        label="Vehicle Type"
                        onChange={(e) => handleStatusModalChange('vehicleType', e.target.value)}
                        disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                      >
                        <MenuItem value="">
                          <em>Select vehicle type</em>
                        </MenuItem>
                        {vehicleTypes.map((type) => (
                          <MenuItem key={type.id} value={type.value}>
                            {type.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  )}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Vehicle No / Reference No"
                      value={statusUpdateModal.vehicleNo}
                      onChange={(e) => {
                        handleStatusModalChange(
                          'vehicleNo',
                          e.target.value.replace(/[^a-zA-Z0-9]/g, '')
                        );
                      }}
                      disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                      required
                      placeholder="Vehicle No / Reference No"
                      error={
                        !!(statusUpdateModal.challanError && statusUpdateModal.challanError.includes('vehicle number'))
                      }
                      helperText=""
                    />
                  </Grid>

                  {['truck', 'by truck'].includes(String(statusUpdateModal.orderBy || '').trim().toLowerCase()) && (
                  <>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isTruthyFlag(statusUpdateModal.meter_not_working)}
                          onChange={(e) => {
                            handleStatusModalChange('meter_not_working', e.target.checked);
                            if (e.target.checked) {
                              handleStatusModalChange('start_km', '');
                            } else {
                              handleStatusModalChange('google_maps_km', '');
                            }
                          }}
                          disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                        />
                      }
                      label="Meter not working"
                    />
                  </Grid>

                  {/* Start KM - only show when meter is working */}
                  {!isTruthyFlag(statusUpdateModal.meter_not_working) && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Start KM"
                        type="number"
                        // required={(statusFilter || '').toLowerCase() === 'processing'}
                        value={statusUpdateModal.start_km}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || /^[1-9]\d*$/.test(value)) {
                            handleStatusModalChange('start_km', value);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                        required={(statusFilter || '').toLowerCase() === 'processing'}
                        placeholder="Enter starting kilometer reading"
                        inputProps={{
                          min: 1,
                          step: 1,
                          onWheel: (e) => e.currentTarget.blur(),
                        }}
                      />
                    </Grid>
                  )}

                  {isTruthyFlag(statusUpdateModal.meter_not_working) && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        required
                        label="Google Maps KM (round trip)"
                        type="number"
                        value={statusUpdateModal.google_maps_km}
                        onChange={(e) => handleStatusModalChange('google_maps_km', e.target.value)}
                        disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                        placeholder="Enter Google Maps KM"
                        inputProps={{
                          min: 0.01,
                          step: 'any',
                          onWheel: (e) => e.currentTarget.blur(),
                        }}
                      />
                    </Grid>
                  )}

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Diesel Receipt Number"
                      value={statusUpdateModal.diesel_receipt_number}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^[a-zA-Z0-9]+$/.test(value)) {
                          handleStatusModalChange('diesel_receipt_number', value);
                        }
                      }}
                      disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                      placeholder="Enter diesel receipt number"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 0.75 }}>
                      Receipt Upload
                      <Box component="span" sx={{ color: 'error.main', ml: 0.25 }}>
                        *
                      </Box>
                    </Typography>
                    <input
                      id="diesel-receipt-camera-input"
                      type="file"
                      accept="image/*"
                      capture="environment"
                      style={{ display: 'none' }}
                      disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        e.target.value = '';
                        if (!file) return;
                        if (!file.type.startsWith('image/') && !/\.(jpe?g|png|gif|webp|bmp)$/i.test(file.name || '')) {
                          setStatusUpdateModal((prev) => ({
                            ...prev,
                            diesel_receipt_upload: null,
                            diesel_receipt_upload_error: 'Only image files are allowed for camera capture.',
                            challanError: '',
                          }));
                          return;
                        }
                        if (file.size > 5 * 1024 * 1024) {
                          setStatusUpdateModal((prev) => ({
                            ...prev,
                            diesel_receipt_upload: null,
                            diesel_receipt_upload_error: 'File size must not exceed 5 MB.',
                            challanError: '',
                          }));
                          return;
                        }
                        setStatusUpdateModal((prev) => ({
                          ...prev,
                          diesel_receipt_upload: file,
                          diesel_receipt_upload_error: '',
                          challanError: '',
                        }));
                      }}
                    />
                    <input
                      id="diesel-receipt-file-input"
                      type="file"
                      accept="image/*,.pdf,application/pdf"
                      style={{ display: 'none' }}
                      disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        e.target.value = '';
                        if (!file) return;
                        const isImageOrPdf =
                          file.type.startsWith('image/') ||
                          file.type === 'application/pdf' ||
                          /\.(pdf|jpe?g|png|gif|webp|bmp)$/i.test(file.name || '');
                        if (!isImageOrPdf) {
                          setStatusUpdateModal((prev) => ({
                            ...prev,
                            diesel_receipt_upload: null,
                            diesel_receipt_upload_error: 'Only image or PDF files are allowed.',
                            challanError: '',
                          }));
                          return;
                        }
                        if (file.size > 5 * 1024 * 1024) {
                          setStatusUpdateModal((prev) => ({
                            ...prev,
                            diesel_receipt_upload: null,
                            diesel_receipt_upload_error: 'File size must not exceed 5 MB.',
                            challanError: '',
                          }));
                          return;
                        }
                        setStatusUpdateModal((prev) => ({
                          ...prev,
                          diesel_receipt_upload: file,
                          diesel_receipt_upload_error: '',
                          challanError: '',
                        }));
                      }}
                    />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                      <Button
                        variant="outlined"
                        component="label"
                        htmlFor="diesel-receipt-camera-input"
                        startIcon={<CameraAltIcon />}
                        disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                        sx={{ textTransform: 'none' }}
                      >
                        Take Photo
                      </Button>
                      <Button
                        variant="outlined"
                        component="label"
                        htmlFor="diesel-receipt-file-input"
                        startIcon={<UploadFileIcon />}
                        disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                        sx={{ textTransform: 'none' }}
                      >
                        Upload from Device
                      </Button>
                      {statusUpdateModal.diesel_receipt_upload && (
                        <Button
                          variant="text"
                          color="inherit"
                          startIcon={<CloseIcon />}
                          disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                          onClick={() => {
                            setStatusUpdateModal((prev) => ({
                              ...prev,
                              diesel_receipt_upload: null,
                              diesel_receipt_upload_error: '',
                              challanError: '',
                            }));
                          }}
                          sx={{ textTransform: 'none' }}
                        >
                          Clear
                        </Button>
                      )}
                    </Stack>
                    {statusUpdateModal.diesel_receipt_upload_error && (
                      <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                        {statusUpdateModal.diesel_receipt_upload_error}
                      </Typography>
                    )}
                    {statusUpdateModal.diesel_receipt_upload && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Selected: {statusUpdateModal.diesel_receipt_upload.name}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Diesel Amount"
                      type="number"
                      value={statusUpdateModal.diesel_amount}
                      onChange={(e) => handleStatusModalChange('diesel_amount', e.target.value)}
                      disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                      placeholder="Enter diesel amount"
                      inputProps={{
                        min: 0,
                        step: 'any',
                        onWheel: (e) => e.currentTarget.blur(),
                      }}
                    />
                  </Grid>
                  </>
                  )}

                  {(() => {
                    const companyKind = getOrderCompanyKind(statusUpdateModal.order);
                    const isProcessingTab = (statusFilter || '').toLowerCase() === 'processing';
                    return (
                      <>
                        {companyKind === 'other' && (
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Crates Dispatched"
                              type="number"
                              value={statusUpdateModal.crates_dispatched}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value === '' || /^\d+$/.test(value)) {
                                  handleStatusModalChange('crates_dispatched', value);
                                }
                              }}
                              disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                              placeholder="0"
                              inputProps={{
                                min: 0,
                                step: 1,
                                onWheel: (e) => e.currentTarget.blur(),
                              }}
                            />
                          </Grid>
                        )}

                        {isProcessingTab && companyKind === 'other' && (
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Extra Plants"
                              type="number"
                              value={statusUpdateModal.extra_plants ?? '0'}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value === '' || /^\d+$/.test(value)) {
                                  handleStatusModalChange('extra_plants', value);
                                }
                              }}
                              disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                              placeholder="0"
                              inputProps={{
                                min: 0,
                                step: 1,
                                onWheel: (e) => e.currentTarget.blur(),
                              }}
                            />
                          </Grid>
                        )}

                        {isProcessingTab && companyKind === 'seeds' && (
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Extra Packages"
                              type="number"
                              value={statusUpdateModal.extra_packages ?? '0'}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value === '' || /^\d+$/.test(value)) {
                                  handleStatusModalChange('extra_packages', value);
                                }
                              }}
                              disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                              placeholder="0"
                              inputProps={{
                                min: 0,
                                step: 1,
                                onWheel: (e) => e.currentTarget.blur(),
                              }}
                            />
                          </Grid>
                        )}

                        {isProcessingTab && companyKind === 'fertilizers' && (
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Sample Packages Quantity"
                              type="number"
                              value={statusUpdateModal.sample_packages_quantity ?? '0'}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value === '' || /^\d+$/.test(value)) {
                                  handleStatusModalChange('sample_packages_quantity', value);
                                }
                              }}
                              disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                              placeholder="0"
                              inputProps={{
                                min: 0,
                                step: 1,
                                onWheel: (e) => e.currentTarget.blur(),
                              }}
                            />
                          </Grid>
                        )}
                      </>
                    );
                  })()}

                  <Grid item xs={12}>
                    <Box>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={statusUpdateModal.useCustomDispatchPerson}
                            onChange={(e) => handleStatusModalChange('useCustomDispatchPerson', e.target.checked)}
                            disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                          />
                        }
                        label="Add custom dispatch person"
                      />
                      {statusUpdateModal.useCustomDispatchPerson ? (
                        <TextField
                          fullWidth
                          label="Custom Dispatch Person Name"
                          value={statusUpdateModal.customDispatchPerson}
                          onChange={(e) => handleStatusModalChange('customDispatchPerson', e.target.value)}
                          disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                          required
                          placeholder="Enter custom dispatch person name"
                          sx={{ mt: 1 }}
                        />
                      ) : (
                        <FormControl fullWidth required sx={{ mt: 1 }}>
                          <InputLabel>Dispatch Person Name</InputLabel>
                          <Select
                            value={statusUpdateModal.dispatchPersonName}
                            label="Dispatch Person Name"
                            onChange={(e) => handleStatusModalChange('dispatchPersonName', e.target.value)}
                            disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                          >
                            <MenuItem value="">
                              <em>Select dispatch person</em>
                            </MenuItem>
                            {dispatchPersons.map((person) => (
                              <MenuItem key={person.id} value={person.name}>
                                {person.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </Box>
                  </Grid>
                    <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Delivery Date"
                    type="date"
                    value={statusUpdateModal.shippedDate}
                    onChange={(e) => handleStatusModalChange('shippedDate', e.target.value)}
                    disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                    required
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      min: new Date().toISOString().split("T")[0], // 🔑 disables all past dates
                    }}
                  />
                </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={statusUpdateModal.generateInvoice}
                          onChange={(e) => handleStatusModalChange('generateInvoice', e.target.checked)}
                          disabled={
                            updatingStatus ||
                            statusUpdateModal.isCreatingChallan ||
                            (['processing'].includes((statusFilter || '').toLowerCase()) &&
                              !shouldEnableButton?.('generate_invoice_along_with_delivery_challan'))
                          }
                        />
                      }
                      label="Generate Invoice along with Delivery Challan"
                    />
                  </Grid>
                </Grid>

                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  * A delivery challan will be created automatically with these details
                  {statusUpdateModal.generateInvoice && (
                    <><br />* An invoice will also be generated for this order.</>
                  )}
                </Typography>
              </Box>
            )}

            {/* End KM - shown when Start KM is present (optional) */}
            {/* {(String(statusUpdateModal.start_km || statusUpdateModal.order?.delivery_challan?.start_km || '').trim() !== '') && (
              <Box sx={{ mt: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="End KMss"
                      type="number"
                      value={statusUpdateModal.end_km}
                      onChange={(e) => {
                        const endKmValue = e.target.value;
                        handleStatusModalChange('end_km', endKmValue);
                        // Validate End KM > Start KM
                        const startKm = parseFloat(statusUpdateModal.start_km);
                        const endKm = parseFloat(endKmValue);
                        if (!isNaN(startKm) && !isNaN(endKm) && endKm <= startKm) {
                          // Error will be shown via button disabled state and alert on submit
                        }
                      }}
                      disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                      placeholder="Enter ending kilometer reading"
                      error={(() => {
                        const startKmFromModal = statusUpdateModal.start_km;
                        const startKmFromOrder = statusUpdateModal.order?.delivery_challan?.start_km;
                        const startKm = parseFloat(startKmFromModal || startKmFromOrder);
                        const endKm = parseFloat(statusUpdateModal.end_km);
                        return !isNaN(startKm) && !isNaN(endKm) && endKm <= startKm;
                      })()}
                      helperText={(() => {
                        const startKmFromModal = statusUpdateModal.start_km;
                        const startKmFromOrder = statusUpdateModal.order?.delivery_challan?.start_km;
                        const startKm = parseFloat(startKmFromModal || startKmFromOrder);
                        const endKm = parseFloat(statusUpdateModal.end_km);
                        if (!isNaN(startKm) && !isNaN(endKm) && endKm <= startKm) {
                          return 'End KM must be greater than Start KM';
                        }
                        return '';
                      })()}
                    />
                  </Grid>
                </Grid>
              </Box>
            )} */}

            {(() => {
              const order = statusUpdateModal.order;
              const orderChallan = getOrderChallan(order);
              const isDeliveredSelected =
                (
                  order?.status?.name ||
                  order?.status_name ||
                  ''
                ).toLowerCase() === 'delivered';
              const isDispatchTab = ['dispatch', 'shipped'].includes((statusFilter || '').toLowerCase());

              if (!isDeliveredSelected) return null;

              const meterNotWorking = isTruthyFlag(
                statusUpdateModal.meter_not_working ?? orderChallan?.meter_not_working
              );
              const googleMapsKm =
                statusUpdateModal.google_maps_km ||
                (orderChallan?.google_maps_km !== null && orderChallan?.google_maps_km !== undefined
                  ? String(orderChallan.google_maps_km)
                  : '');
              const cratesDispatched = Number(
                statusUpdateModal.crates_dispatched !== undefined &&
                  String(statusUpdateModal.crates_dispatched).trim() !== ''
                  ? statusUpdateModal.crates_dispatched
                  : (orderChallan?.crates_dispatched ?? 0)
              );
              const cratesReturned = Number(statusUpdateModal.crates_returned);
              const plantsReturnedRaw = String(statusUpdateModal.plants_returned ?? '').trim();
              const plantsReturned = plantsReturnedRaw === '' ? 0 : Number(plantsReturnedRaw);
              const packagesReturnedRaw = String(statusUpdateModal.packages_returned ?? '').trim();
              const packagesReturned = packagesReturnedRaw === '' ? 0 : Number(packagesReturnedRaw);
              const companyKind = getOrderCompanyKind(order);
              const finalKm = Number(statusUpdateModal.final_km);
              const mapsKmNumber = Number(googleMapsKm);
              const showKmExcessReason =
                meterNotWorking &&
                String(statusUpdateModal.final_km || '').trim() !== '' &&
                !Number.isNaN(finalKm) &&
                !Number.isNaN(mapsKmNumber) &&
                finalKm !== mapsKmNumber;
              const showCratesShortReason =
                companyKind === 'other' &&
                String(statusUpdateModal.crates_returned || '').trim() !== '';
              const showPlantsReason = companyKind === 'other' && plantsReturned > 0;
              const showPackagesReason = companyKind === 'seeds' && packagesReturned > 0;

              const startKmValue =
                statusUpdateModal.start_km ||
                (
                  orderChallan?.start_km !== null && orderChallan?.start_km !== undefined
                    ? String(orderChallan.start_km)
                    : ''
                );
              const hasStartKm = String(startKmValue).trim() !== '';
              const isTruckOrder = isOrderByTruck(order);
              const showEndKmSection =
                !meterNotWorking &&
                hasStartKm &&
                ((isDispatchTab && isTruckOrder) || (!isDispatchTab && isTruckOrder) || (!isDispatchTab && hasStartKm));

              const driverName =
                statusUpdateModal.driverName ||
                orderChallan?.driver_name ||
                '';
              const vehicleNo =
                statusUpdateModal.vehicleNo ||
                orderChallan?.vehicle_number ||
                orderChallan?.vehicle_no ||
                orderChallan?.vehicle_name ||
                '';

              return (
                <Box sx={{ mt: 3 }}>
                  <Grid container spacing={2}>
                    {showEndKmSection && (
                      <>
                        {(isTruckOrder || isDispatchTab) && (
                          <>
                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                label="Driver Name"
                                value={driverName}
                                disabled
                                InputProps={{ readOnly: true }}
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                label="Vehicle No / Reference No"
                                value={vehicleNo}
                                disabled
                                InputProps={{ readOnly: true }}
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                label="Start KM"
                                value={startKmValue}
                                disabled
                                InputProps={{ readOnly: true }}
                              />
                            </Grid>
                          </>
                        )}
                        <Grid item xs={12} md={(isTruckOrder || isDispatchTab) ? 6 : 12}>
                          <TextField
                            fullWidth
                            required
                            label="End KM"
                            type="number"
                            value={statusUpdateModal.end_km}
                            onChange={(e) => {
                              handleStatusModalChange('end_km', e.target.value);
                            }}
                            disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                            placeholder="Enter ending kilometer reading"
                            InputLabelProps={{ shrink: true }}
                            inputProps={{
                              onWheel: (e) => e.currentTarget.blur(),
                            }}
                            sx={{
                              '& .MuiFormLabel-asterisk': { color: 'error.main' },
                            }}
                            error={(() => {
                              const startKm = parseFloat(startKmValue);
                              const endKm = parseFloat(statusUpdateModal.end_km);
                              const hasEndKm = String(statusUpdateModal.end_km || '').trim() !== '';
                              return !hasEndKm || (!isNaN(startKm) && !isNaN(endKm) && endKm <= startKm);
                            })()}
                            helperText={(() => {
                              const startKm = parseFloat(startKmValue);
                              const endKm = parseFloat(statusUpdateModal.end_km);
                              const hasEndKm = String(statusUpdateModal.end_km || '').trim() !== '';
                              if (!hasEndKm) return 'End KM is required';
                              if (!isNaN(startKm) && !isNaN(endKm) && endKm <= startKm) {
                                return 'End KM must be greater than Start KM';
                              }
                              return '';
                            })()}
                          />
                        </Grid>
                      </>
                    )}

                    <Grid item xs={12}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Delivery Logistics
                      </Typography>
                    </Grid>

                    {isTruckOrder && (
                      <>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Meter not Working"
                            value={meterNotWorking ? 'Yes' : 'No'}
                            disabled
                            InputProps={{ readOnly: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Google Maps KM"
                            value={googleMapsKm || '—'}
                            disabled
                            InputProps={{ readOnly: true }}
                          />
                        </Grid>
                      </>
                    )}
                    {companyKind === 'other' && (
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Crates Dispatched"
                          value={String(cratesDispatched)}
                          disabled
                          InputProps={{ readOnly: true }}
                        />
                      </Grid>
                    )}
                    {isTruckOrder && (
                      <>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Diesel Receipt Number"
                            value={
                              statusUpdateModal.diesel_receipt_number ||
                              orderChallan?.diesel_receipt_number ||
                              '—'
                            }
                            disabled
                            InputProps={{ readOnly: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Diesel Amount"
                            value={
                              statusUpdateModal.diesel_amount ||
                              (orderChallan?.diesel_amount !== null &&
                              orderChallan?.diesel_amount !== undefined
                                ? String(orderChallan.diesel_amount)
                                : '—')
                            }
                            disabled
                            InputProps={{ readOnly: true }}
                          />
                        </Grid>
                      </>
                    )}

                    {meterNotWorking && (
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="small"
                          required
                          label="Final KM"
                          type="number"
                          value={statusUpdateModal.final_km}
                          onChange={(e) => handleStatusModalChange('final_km', e.target.value)}
                          disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                          placeholder="Enter actual trip KM"
                          InputLabelProps={{ shrink: true }}
                          inputProps={{
                            min: 0,
                            step: 'any',
                            onWheel: (e) => {
                              e.preventDefault();
                              e.currentTarget.blur();
                            },
                          }}
                          sx={{
                            '& .MuiFormLabel-asterisk': { color: 'error.main' },
                          }}
                        />
                      </Grid>
                    )}

                    {showKmExcessReason && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          required
                          label="KM Excess Reason"
                          value={statusUpdateModal.km_excess_reason}
                          onChange={(e) => handleStatusModalChange('km_excess_reason', e.target.value)}
                          disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                          placeholder="Why KM exceeded Maps estimate"
                          InputLabelProps={{ shrink: true }}
                          sx={{
                            '& .MuiFormLabel-asterisk': { color: 'error.main' },
                          }}
                        />
                      </Grid>
                    )}

                    {companyKind === 'other' && (
                      <>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            size="small"
                            required
                            label="Crates Returned"
                            type="number"
                            value={statusUpdateModal.crates_returned}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || /^\d+$/.test(value)) {
                                handleStatusModalChange('crates_returned', value);
                              }
                            }}
                            disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                            placeholder="Enter crates returned"
                            InputLabelProps={{ shrink: true }}
                            inputProps={{
                              min: 0,
                              step: 1,
                              onWheel: (e) => e.currentTarget.blur(),
                            }}
                            sx={{
                              '& .MuiFormLabel-asterisk': { color: 'error.main' },
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            size="small"
                            required
                            label="Crates Return Person"
                            value={statusUpdateModal.crates_returned_person || ''}
                            onChange={(e) => {
                              const value = e.target.value.replace(/[^a-zA-Z\s]/g, '');
                              handleStatusModalChange('crates_returned_person', value);
                            }}
                            disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                            placeholder="Enter crates return person name"
                            InputLabelProps={{ shrink: true }}
                            sx={{
                              '& .MuiFormLabel-asterisk': { color: 'error.main' },
                            }}
                          />
                        </Grid>

                        {showCratesShortReason && (
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              size="small"
                              required
                              label="Crates Short Reason"
                              value={statusUpdateModal.crates_short_reason}
                              onChange={(e) => handleStatusModalChange('crates_short_reason', e.target.value)}
                              disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                              placeholder="Why returned crates are less than dispatched"
                              InputLabelProps={{ shrink: true }}
                              sx={{
                                '& .MuiFormLabel-asterisk': { color: 'error.main' },
                              }}
                            />
                          </Grid>
                        )}

                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Plants Returned"
                            type="number"
                            value={statusUpdateModal.plants_returned}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || /^\d+$/.test(value)) {
                                handleStatusModalChange('plants_returned', value);
                              }
                            }}
                            disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                            placeholder="0"
                            inputProps={{
                              min: 0,
                              step: 1,
                              onWheel: (e) => e.currentTarget.blur(),
                            }}
                          />
                        </Grid>

                        {showPlantsReason && (
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              size="small"
                              required
                              label="Plants Returned Reason"
                              value={statusUpdateModal.plants_returned_reason}
                              onChange={(e) => handleStatusModalChange('plants_returned_reason', e.target.value)}
                              disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                              placeholder="Why plants were returned"
                              InputLabelProps={{ shrink: true }}
                              sx={{
                                '& .MuiFormLabel-asterisk': { color: 'error.main' },
                              }}
                            />
                          </Grid>
                        )}
                      </>
                    )}

                    {companyKind === 'seeds' && (
                      <>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Packages Returned"
                            type="number"
                            value={statusUpdateModal.packages_returned ?? '0'}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || /^\d+$/.test(value)) {
                                handleStatusModalChange('packages_returned', value);
                              }
                            }}
                            disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                            placeholder="0"
                            inputProps={{
                              min: 0,
                              step: 1,
                              onWheel: (e) => e.currentTarget.blur(),
                            }}
                          />
                        </Grid>
                        {showPackagesReason && (
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              size="small"
                              required
                              label="Packages Returned Reason"
                              value={statusUpdateModal.packages_returned_reason || ''}
                              onChange={(e) =>
                                handleStatusModalChange('packages_returned_reason', e.target.value)
                              }
                              disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                              placeholder="Why packages were returned"
                              InputLabelProps={{ shrink: true }}
                              sx={{
                                '& .MuiFormLabel-asterisk': { color: 'error.main' },
                              }}
                            />
                          </Grid>
                        )}
                      </>
                    )}

                    {companyKind === 'fertilizers' && (
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Package Returned"
                          type="number"
                          value={statusUpdateModal.package_returned ?? '0'}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '' || /^\d+$/.test(value)) {
                              handleStatusModalChange('package_returned', value);
                            }
                          }}
                          disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                          placeholder="0"
                          inputProps={{
                            min: 0,
                            step: 1,
                            onWheel: (e) => e.currentTarget.blur(),
                          }}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Box>
              );
            })()}

          </DialogContent>
          
          <DialogActions>
            <Button 
              onClick={handleCloseStatusModal}
              disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
            >
              Cancel
            </Button>
            <Button 
              onClick={() => handleStatusUpdate(
                statusUpdateModal.order?.id,
                statusUpdateModal.order?.status?.name || statusUpdateModal.order?.status_name,
                statusUpdateModal.order?.order_id
              )}
              variant="contained"
              disabled={isSingleStatusSubmitDisabled()}
              startIcon={
                updatingStatus || statusUpdateModal.isCreatingChallan ? 
                <CircularProgress size={16} /> : 
                (statusUpdateModal.order?.status?.category || '').toLowerCase() === 'shipped' ? <DeliveryIcon /> : <UpdateIcon />
              }
            >
              {updatingStatus ? 'Updating Status...' : 
              statusUpdateModal.isCreatingChallan ? 'Creating Challan...' :
              (statusUpdateModal.order?.status?.category || '').toLowerCase() === 'shipped' ? 'Ship Order' : 'Update Status'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Payment Status Update Modal */}
        <Dialog 
          open={paymentUpdateModal.open} 
          onClose={() => {
            if (updatingPayment) return;
            setPaymentUpdateModal(closePaymentUpdateModalState());
            setPaymentStatusChoices([]);
          }}
          maxWidth={paymentUpdateModal.step === 'payment_details' ? false : 'sm'}
          fullWidth={paymentUpdateModal.step !== 'payment_details'}
          scroll={paymentUpdateModal.step === 'payment_details' ? 'paper' : 'body'}
          PaperProps={
            paymentUpdateModal.step === 'payment_details'
              ? PAYMENT_DETAILS_DIALOG_PAPER_PROPS
              : undefined
          }
        >
          <DialogTitle>
            {paymentUpdateModal.step === 'payment_option'
              ? 'Payment Option'
              : paymentUpdateModal.step === 'payment_details'
                ? 'Payment Details'
                : 'Update Payment Status'}
            {paymentUpdateModal.order && (
              <Typography variant="body2" color="text.secondary">
                Order ID: {paymentUpdateModal.order.order_id}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent
            sx={
              paymentUpdateModal.step === 'payment_details'
                ? PAYMENT_DETAILS_DIALOG_CONTENT_SX
                : undefined
            }
          >
            {paymentUpdateModal.paymentError && (
              <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                {paymentUpdateModal.paymentError}
              </Alert>
            )}

            {paymentUpdateModal.step === 'payment_option' && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Choose your payment method:
                </Typography>
                <RadioGroup
                  value={paymentUpdateModal.paymentOption}
                  onChange={(e) =>
                    setPaymentUpdateModal((prev) => ({
                      ...prev,
                      paymentOption: e.target.value,
                      paymentError: '',
                      ...(e.target.value === 'full'
                        ? { advanceAmount: String(prev.order?.total_amount ?? prev.order?.total ?? '') }
                        : { advanceAmount: '' }),
                    }))
                  }
                >
                  <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, borderRadius: 2 }}>
                    <FormControlLabel
                      value="full"
                      control={<Radio />}
                      label="Order with full Payment"
                    />
                  </Paper>
                  {/* <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <FormControlLabel
                      value="advance"
                      control={<Radio />}
                      label="Order with Advanced Payment"
                    />
                  </Paper> */}
                </RadioGroup>
              </Box>
            )}

            {paymentUpdateModal.step === 'status' && (
            <>
            {isPaymentUpdateDisabled(paymentUpdateModal.order) && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {getPaymentUpdateDisabledReason(paymentUpdateModal.order, statusFilter)}
              </Alert>
            )}
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Payment Status</InputLabel>
              <Select
                value={getOrderPaymentStatusValue(paymentUpdateModal.order)}
                label="Payment Status"
                disabled={
                  loadingPaymentStatusChoices ||
                  updatingPayment ||
                  isPaymentUpdateDisabled({
                    ...paymentUpdateModal.order,
                    payment_status: {
                      name: paymentUpdateModal.initialPaymentStatus || '',
                    },
                  })
                }
                MenuProps={{
                  disableScrollLock: true,
                  PaperProps: {
                    sx: { maxHeight: 300 },
                  },
                  sx: {
                    zIndex: (theme) => theme.zIndex.modal + 1,
                  },
                }}
                onChange={(e) => {
                  const newStatus = e.target.value;
                  if (newStatus === 'paid') {
                    loadAdvancePaymentMethods();
                  }
                  setPaymentUpdateModal(prev => ({
                    ...prev,
                    order: { 
                      ...prev.order, 
                      payment_status: { 
                        ...(typeof prev.order?.payment_status === 'object' &&
                        prev.order?.payment_status !== null
                          ? prev.order.payment_status
                          : {}),
                        name: newStatus,
                      }
                    },
                    refundAmount: newStatus === 'refunded' ? (prev.order?.total_amount ?? prev.refundAmount ?? '') : prev.refundAmount,
                    paymentError: '',
                    ...(newStatus === 'paid'
                      ? getInlinePaidPaymentStepState(prev.orderPaymentDetails)
                      : newStatus === 'credit'
                        ? getCreditPaymentStepState(prev.orderPaymentDetails)
                        : {
                            step: 'status',
                            paymentOption: '',
                            advanceAmount: '',
                            installmentAmount: '',
                            creditNote: '',
                            paymentMethod: '',
                            paymentMethodConfig: null,
                            paymentFields: {},
                            paymentFiles: {},
                          }),
                  }));
                }}
              >
                {renderPaymentStatusMenuItems()}
              </Select>
            </FormControl>
            {(paymentUpdateModal.paymentOption === 'credit' ||
              paymentUpdateModal.paymentOption === 'remaining') && (
              <Box sx={{ mt: 2 }}>
                <PaymentDetailsStepContent
                  idPrefix="single-payment-inline"
                  order={paymentUpdateModal.order}
                  orderPaymentDetails={paymentUpdateModal.orderPaymentDetails}
                  paymentOption={paymentUpdateModal.paymentOption}
                  advanceAmount={paymentUpdateModal.advanceAmount}
                  onAdvanceAmountChange={(e) =>
                    setPaymentUpdateModal((prev) => ({
                      ...prev,
                      advanceAmount: e.target.value,
                      paymentError: '',
                    }))
                  }
                  installmentAmount={paymentUpdateModal.installmentAmount || ''}
                  onInstallmentAmountChange={(e) => {
                    const value = e.target.value;
                    const remaining = Number(
                      paymentUpdateModal.orderPaymentDetails?.remainingAmount ?? 0
                    );
                    if (value !== '' && Number(value) > remaining) {
                      setPaymentUpdateModal((prev) => ({
                        ...prev,
                        paymentError: 'Installment amount cannot exceed the remaining amount',
                      }));
                      return;
                    }
                    setPaymentUpdateModal((prev) => ({
                      ...prev,
                      installmentAmount: value,
                      paymentError: '',
                      ...(prev.paymentOption === 'credit'
                        ? {
                            paymentFields: getCreditChequePaymentFields(
                              prev.paymentFields,
                              value,
                              prev.paymentMethodConfig
                            ),
                          }
                        : {}),
                    }));
                  }}
                  creditNote={paymentUpdateModal.creditNote || ''}
                  onCreditNoteChange={(e) =>
                    setPaymentUpdateModal((prev) => ({
                      ...prev,
                      creditNote: e.target.value,
                      paymentError: '',
                    }))
                  }
                  paymentMethod={paymentUpdateModal.paymentMethod}
                  paymentMethodConfig={paymentUpdateModal.paymentMethodConfig}
                  advancePaymentMethods={advancePaymentMethods}
                  loadingAdvancePaymentMethods={loadingAdvancePaymentMethods}
                  onPaymentMethodChange={(e) => {
                    const methodId = e.target.value;
                    setPaymentUpdateModal((prev) => {
                      const payload = buildPaymentMethodChangePayload(
                        methodId,
                        prev.orderPaymentDetails
                      );
                      if (prev.paymentOption === 'credit' && payload.paymentMethodConfig?.type === 'cheque') {
                        payload.paymentFields = getCreditChequePaymentFields(
                          payload.paymentFields,
                          prev.installmentAmount ?? '',
                          payload.paymentMethodConfig
                        );
                      }
                      return { ...prev, ...payload };
                    });
                  }}
                  paymentFields={paymentUpdateModal.paymentFields}
                  paymentFiles={paymentUpdateModal.paymentFiles}
                  onFieldChange={(key, value) => {
                    if (key === 'cheque_amount' && paymentUpdateModal.orderPaymentDetails) {
                      return;
                    }
                    setPaymentUpdateModal((prev) => ({
                      ...prev,
                      paymentFields: { ...prev.paymentFields, [key]: value },
                      paymentError: '',
                    }));
                  }}
                  onFileChange={(key, file) =>
                    setPaymentUpdateModal((prev) => ({
                      ...prev,
                      paymentFiles: { ...prev.paymentFiles, [key]: file },
                      paymentError: '',
                    }))
                  }
                />
              </Box>
            )}
            {['failed', 'pending', 'outstanding'].includes(
              getOrderPaymentStatusValue(paymentUpdateModal.order)
            ) && (
              <Box sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Note"
                  value={paymentUpdateModal.creditNote || ''}
                  onChange={(e) =>
                    setPaymentUpdateModal((prev) => ({
                      ...prev,
                      creditNote: e.target.value,
                      paymentError: '',
                    }))
                  }
                  placeholder="Enter note"
                  multiline
                  minRows={3}
                  required
                />
              </Box>
            )}
            {(paymentUpdateModal.order?.payment_status?.name || paymentUpdateModal.order?.payment_status || '')
              .toLowerCase() === 'refunded' && (() => {
                const apiDetails = paymentUpdateModal.orderPaymentDetails;
                const totalAmount = Number(
                  apiDetails?.totalAmount ??
                    paymentUpdateModal.order?.total_amount ??
                    paymentUpdateModal.order?.total ??
                    0
                );
                const advancePaidTotal = getAdvancePaymentEntries(
                  paymentUpdateModal.order || paymentUpdateModal
                ).reduce((sum, entry) => sum + Number(entry.advance_amount || 0), 0);
                const paidAmountFallback =
                  advancePaidTotal > 0
                    ? advancePaidTotal
                    : (paymentUpdateModal.order?.amount_paid ??
                        paymentUpdateModal.order?.paid ??
                        0);
                const paidAmount = Number(
                  apiDetails?.paidAmount ?? paidAmountFallback
                );
                const pendingAmount = Number(
                  apiDetails?.remainingAmount ?? Math.max(0, totalAmount - paidAmount)
                );
                const maxAmount = paidAmount;
                const refundAmount = Number(paymentUpdateModal.refundAmount || 0);
                const isExceeding =
                  paymentUpdateModal.refundAmount !== '' &&
                  (!Number.isFinite(refundAmount) || refundAmount <= 0 || refundAmount > maxAmount);
                const formatAmount = (n) =>
                  Number.isNaN(n) ? '0.00' : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

                return (
                  <Box sx={{ mt: 2 }}>
                    {
                      (() => {
                        const advanceAmount = paidAmount;
                        const remainingAdvance = pendingAmount;

                    //     return (
                    //       <Box sx={{ mb: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    //          <TextField
                    //   fullWidth
                    //   label="Refund Amount"
                    //   type="number"
                    //   value={paymentUpdateModal.refundAmount ?? ''}
                    //   placeholder="Enter refund amount"
                    //   inputProps={{
                    //     min: 0,
                    //     max: maxAmount,
                    //     step: 0.01,
                    //   }}
                    //   error={isExceeding}
                    //   helperText={
                    //     isExceeding
                    //       ? "You can’t enter more than the actual amount"
                    //       : ''
                    //   }
                    //   onChange={(e) =>
                    //     setPaymentUpdateModal((prev) => ({
                    //       ...prev,
                    //       refundAmount: e.target.value,
                    //     }))
                    //   }
                    // />
                    //         <Typography variant="body2" color="text.secondary">
                    //           Total: ₹{formatAmount(totalAmount)}
                    //         </Typography>
                    //         {/* <Typography variant="body2" color="text.secondary">
                    //           Paid: ₹{formatAmount(paidAmount)}
                    //         </Typography>
                    //         <Typography variant="body2" color="text.secondary">
                    //           Pending: ₹{formatAmount(pendingAmount)}
                    //         </Typography> */}
                    //         <Typography variant="body2" color="text.secondary">
                    //           Paid: ₹{formatAmount(advanceAmount)}
                    //         </Typography>
                    //         <Typography variant="body2" color="text.secondary">
                    //           Pending: ₹{formatAmount(remainingAdvance)}
                    //         </Typography>
                    //       </Box>
                    //     );
                    return (
                      <Box sx={{ mb: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <TextField
                  fullWidth
                  label="Refund Amount"
                  type="number"
                  value={paymentUpdateModal.refundAmount ?? ''}
                  placeholder="Enter refund amount"
                  inputProps={{
                    min: 0,
                    max: maxAmount,
                    step: 0.01,
                  }}
                  error={isExceeding}
                  helperText={
                    isExceeding
                      ? 'Refund amount cannot exceed the available paid amount'
                      : ''
                  }
                  onChange={(e) =>
                    setPaymentUpdateModal((prev) => ({
                      ...prev,
                      refundAmount: e.target.value,
                    }))
                  }
                />
                        <Box display="flex" gap={2} mt={2}>
                      <TextField
                        label="Total"
                      value={formatAmount(totalAmount)}
                        size="small"
                        fullWidth
                          disabled
                        />

                        <TextField
                        label="Paid"
                      value={formatAmount(advanceAmount)}
                      size="small"
                      fullWidth
                      disabled
                      />

                    <TextField
                      label="Pending"
                      value={formatAmount(remainingAdvance)}
                        size="small"
                      fullWidth
                      disabled
                        />
                      </Box>
                        <TextField
                          fullWidth
                          size="small"
                          label="Note"
                          value={paymentUpdateModal.creditNote || ''}
                          onChange={(e) =>
                            setPaymentUpdateModal((prev) => ({
                              ...prev,
                              creditNote: e.target.value,
                              paymentError: '',
                            }))
                          }
                          placeholder="Enter note"
                          multiline
                          minRows={3}
                          required
                          sx={{ mt: 2 }}
                        />

                  </Box>
                    );
                  })()
                    }

                  
                  </Box>
                );
              })()
            }
            </>
            )}


          </DialogContent>
          <DialogActions
            sx={
              paymentUpdateModal.step === 'payment_details'
                ? PAYMENT_DETAILS_DIALOG_ACTIONS_SX
                : undefined
            }
          >
            <Button 
              onClick={() => {
                if (paymentUpdateModal.step === 'payment_details') {
                  const previousStatus = (
                    paymentUpdateModal.orderPaymentDetails?.current_payment_status ||
                    paymentUpdateModal.order?.payment_status?.name ||
                    paymentUpdateModal.order?.payment_status ||
                    'pending'
                  )
                    .toString()
                    .toLowerCase();
                  setPaymentUpdateModal((prev) => ({
                    ...prev,
                    step: 'status',
                    paymentOption: '',
                    advanceAmount: '',
                    installmentAmount: '',
                    creditNote: '',
                    paymentMethod: '',
                    paymentFields: {},
                    paymentFiles: {},
                    paymentError: '',
                    order: {
                      ...prev.order,
                      payment_status: {
                        ...prev.order?.payment_status,
                        name: previousStatus,
                      },
                    },
                  }));
                  return;
                }
                setPaymentUpdateModal(closePaymentUpdateModalState());
                setPaymentStatusChoices([]);
              }}
              disabled={updatingPayment}
            >
              {paymentUpdateModal.step === 'status' ? 'Cancel' : 'Back'}
            </Button>

            {paymentUpdateModal.paymentOption === 'credit' &&
              shouldShowCreditSubmitButton(paymentUpdateModal) && (
              <Button
                variant="contained"
                disabled={updatingPayment || !canSubmitCreditPayment(paymentUpdateModal)}
                onClick={handleConfirmCreditPaymentDetails}
                startIcon={updatingPayment ? <CircularProgress size={16} /> : <UpdateIcon />}
              >
                {updatingPayment ? 'Updating...' : 'Submit Payment'}
              </Button>
            )}

            {paymentUpdateModal.paymentOption === 'remaining' && (
              <Button
                variant="contained"
                disabled={updatingPayment || !canSubmitPaidPayment(paymentUpdateModal)}
                onClick={handleConfirmPaidPaymentDetails}
                startIcon={updatingPayment ? <CircularProgress size={16} /> : <UpdateIcon />}
              >
                {updatingPayment ? 'Updating...' : 'Submit Payment'}
              </Button>
            )}

            {paymentUpdateModal.step === 'status' &&
              getOrderPaymentStatusValue(paymentUpdateModal.order) !== 'paid' &&
              getOrderPaymentStatusValue(paymentUpdateModal.order) !== 'credit' && (
              <Button 
                onClick={() => handlePaymentStatusUpdate(
                  paymentUpdateModal.order?.id,
                  getOrderPaymentStatusValue(paymentUpdateModal.order),
                  paymentUpdateModal.order?.order_id,
                  paymentUpdateModal.refundAmount,
                  paymentUpdateModal.order?.total_amount,
                  { creditNote: paymentUpdateModal.creditNote }
                )}
                variant="contained"
                disabled={
                  updatingPayment ||
                  isPaymentUpdateDisabled(paymentUpdateModal.order) ||
                  (() => {
                    const ps = getOrderPaymentStatusValue(paymentUpdateModal.order);
                    if (
                      ['failed', 'pending', 'outstanding'].includes(ps) &&
                      !String(paymentUpdateModal.creditNote || '').trim()
                    ) {
                      return true;
                    }
                    if (ps !== 'refunded') return false;
                    if (!String(paymentUpdateModal.creditNote || '').trim()) return true;
                    const refundNum = Number(paymentUpdateModal.refundAmount);
                    const apiDetails = paymentUpdateModal.orderPaymentDetails;
                    const advancePaidTotal = getAdvancePaymentEntries(
                      paymentUpdateModal.order || paymentUpdateModal
                    ).reduce((sum, entry) => sum + Number(entry.advance_amount || 0), 0);
                    const paidFallback =
                      advancePaidTotal > 0
                        ? advancePaidTotal
                        : (paymentUpdateModal.order?.amount_paid ??
                            paymentUpdateModal.order?.paid ??
                            0);
                    const maxRefund = Number(apiDetails?.paidAmount ?? paidFallback);
                    return (
                      !Number.isFinite(refundNum) ||
                      refundNum <= 0 ||
                      refundNum > maxRefund
                    );
                  })()
                }
                startIcon={updatingPayment ? <CircularProgress size={16} /> : <UpdateIcon />}
              >
                {updatingPayment ? 'Updating...' : 'Update Status'}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Invoice View Modal */}
        <InvoiceModal open={invoiceModal.open} onClose={handleCloseInvoiceModal} maxWidth={false} fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PdfIcon color="error" />
                <Typography variant="h6" fontWeight={600}>Invoice - {invoiceModal.product?.product_name}</Typography>
              </Box>
              <IconButton onClick={handleCloseInvoiceModal}><CloseIcon /></IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 0, height: '100%' }}>
            {invoiceModal.invoice && invoiceModal.invoice.path && <InvoiceFrame src={invoiceModal.invoice.path} title="Invoice PDF" onError={(e) => { console.error('Error loading PDF:', e); alert('Error loading invoice PDF. Please try downloading instead.'); }} />}
          </DialogContent>
        </InvoiceModal>

        {/* Delivery Challan View Modal */}
        <InvoiceModal open={deliveryChallanModal.open} onClose={handleCloseDeliveryChallanModal} maxWidth={false} fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ChallanIcon color="secondary" />
                <Typography variant="h6" fontWeight={600}>Delivery Challan - {deliveryChallanModal.product?.product_name}</Typography>
              </Box>
              <IconButton onClick={handleCloseDeliveryChallanModal}><CloseIcon /></IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 0, height: '100%' }}>
            {deliveryChallanModal.challan && deliveryChallanModal.challan.path && <InvoiceFrame src={deliveryChallanModal.challan.path} title="Delivery Challan PDF" onError={(e) => { console.error('Error loading PDF:', e); alert('Error loading delivery challan PDF. Please try downloading instead.'); }} />}
          </DialogContent>
        </InvoiceModal>

        {/* Bulk Delivery Challan Modal */}
        <Dialog
          open={bulkChallanModal.open}
          onClose={handleCloseBulkChallanModal}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DeliveryIcon color="secondary" />
              <Typography variant="h6" fontWeight={600}>
                Bulk Status Update & Delivery Management
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Update status for {getSelectedProductsCount()} selected orders. For "Dispatch" status, delivery challans will be created.
            </Typography>
          </DialogTitle>

          <DialogContent>
            {bulkChallanModal.error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {bulkChallanModal.error}
              </Alert>
            )}

            {bulkChallanModal.isCreating && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  Creating bulk delivery challan...
                </Box>
              </Alert>
            )}

  {(() => {
              // Check payment status for all selected orders
              const selectedOrders = filteredData.filter(order => selectedProducts.includes(order.id));
              const requiresDispatchPaymentRule = (bulkChallanModal.selectedStatus || '').toLowerCase() === 'dispatch';
              const unpaidOrders = selectedOrders.filter(order => {
                const paymentStatus = (order.payment_status?.name || order.payment_status_name || order.payment_status || '').toLowerCase();
                const isPaid = requiresDispatchPaymentRule
                  ? isPaymentSatisfiedForDispatch(paymentStatus)
                  : isPaymentSatisfiedForOrderAdvance(paymentStatus);
                return !isPaid;
              });
              
              if (unpaidOrders.length > 0) {
                return (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="body2" fontWeight={600}>
                      Payment Required
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ mt: 0.5, mb: 1.5 }}>
                      {requiresDispatchPaymentRule
                        ? `${unpaidOrders.length} of ${selectedOrders.length} selected order${selectedOrders.length > 1 ? 's' : ''} must be Paid or Credit before moving to Dispatch.`
                        : `${unpaidOrders.length} of ${selectedOrders.length} selected order${selectedOrders.length > 1 ? 's' : ''} must be paid before status can be updated.`}
                      Please update the payment status first. Only unpaid orders will be updated.
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ textTransform: 'none' }}
                      startIcon={<Payment fontSize="small" />}
                      onClick={() => openBulkPaidPaymentFlow(unpaidOrders)}
                      disabled={
                        !shouldEnableButton?.('update_payment') ||
                        bulkChallanModal.isCreating ||
                        isBulkPaymentUpdateDisabled()
                      }
                    >
                      Update Payment
                    </Button>
                    {/* <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                      <FormControl sx={{ minWidth: 200 }} size="small">
                        <InputLabel>Update Payment Status To</InputLabel>
                        <Select
                          value={bulkChallanModal.bulkPaymentStatus || ''}
                          label="Update Payment Status To"
                          onChange={(e) => handleBulkChallanInputChange('bulkPaymentStatus', e.target.value)}
                          disabled={isUpdatingBulkPayment || bulkChallanModal.isCreating}
                        >
                          {paymentStatusChoices
                            .filter(choice => {
                              // Only show "paid" and "outstanding" options for this modal
                              const value = (choice.value || choice.key || choice.name || '').toLowerCase();
                              return value === 'paid' || value === 'outstanding';
                            })
                            .map((choice) => {
                              const value = choice.value || choice.key || choice.name || '';
                              const label = choice.label || choice.name || value;
                              return (
                                <MenuItem key={value} value={value}>
                                  <PaymentStatusChip 
                                    label={label} 
                                    status={value.toLowerCase()}
                                    size="small"
                                    sx={{ mr: 1 }}
                                  />
                                  {label}
                                </MenuItem>
                              );
                            })}
                        </Select>
                      </FormControl>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={handleBulkPaymentStatusUpdate}
                        disabled={!bulkChallanModal.bulkPaymentStatus || isUpdatingBulkPayment || bulkChallanModal.isCreating}
                        startIcon={isUpdatingBulkPayment ? <CircularProgress size={16} /> : <Payment />}
                        sx={{ mt: 0.5 }}
                      >
                        {isUpdatingBulkPayment ? 'Updating...' : 'Update Payment Status'}
                      </Button>
                    </Box> */}
                  </Alert>
                );
              }
              return null;
            })()}

            <Box sx={{ mt: 2 }}>
              {/* <Box sx={{ 
                p: 2, 
                backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                borderRadius: 2,
                mb: 3
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <ChallanIcon color="secondary" />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Delivery Information
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  This information will be used for all selected orders
                </Typography>
              </Box> */}

              <Grid container spacing={2}>
                <Grid item xs={12}>
              

                {/* <FormControl fullWidth>
                  <InputLabel>Update Status Tosdfgh</InputLabel>
                  <Select
                    value={
                      // If Dispatch tab is active and Dispatch is selected, clear it
                      (statusFilter === 'Dispatch' && bulkChallanModal.selectedStatus === 'Dispatch') 
                        ? '' 
                        : (bulkChallanModal.selectedStatus || '')
                    }
                    label="Update Status To"
                    onChange={(e) => {
                      const selectedValue = e.target.value;
                      const selectedOption = statusOptions.find(opt => opt.name === selectedValue);
                      
                      // Prevent selecting Dispatch when Dispatch tab is active
                      if (statusFilter === 'Dispatch' && selectedValue === 'Dispatch') {
                        return; // Don't allow selection
                      }
                      
                      handleBulkChallanInputChange('selectedStatus', selectedValue);
                      handleBulkChallanInputChange('selectedStatusCategory', selectedOption?.category || '');
                    }}
                    disabled={false}
                  >
                    {statusOptions.map((option) => {
                      // Find minimum status among selected orders for sequential validation
                      const selectedOrders = filteredData.filter(order => selectedProducts.includes(order.id));
                      const statusOrder = getStatusOrder();
                      
                      // Check payment status for all selected orders
                      const hasUnpaidOrders = selectedOrders.some(order => {
                        const paymentStatus = (order.payment_status?.name || order.payment_status || '').toLowerCase();
                        const isPaid = isPaymentSatisfiedForOrderAdvance(paymentStatus);
                        return !isPaid;
                      });
                      
                      // If not paid, only allow "Orders" status (name: "Orders")
                      const isOrdersStatus = option.name === 'Orders';
                      const shouldDisableByPayment = hasUnpaidOrders && !isOrdersStatus;
                      
                      // Get the minimum status order (lowest sequence) among selected orders
                      let minStatusOrder = 999;
                      selectedOrders.forEach(order => {
                        const orderStatusCategory = (order.status?.category || '').toLowerCase();
                        const orderStatusName = order.status?.name || '';
                        
                        // Try to find category from statusOptions if not directly available
                        let orderCategory = orderStatusCategory;
                        if (!orderCategory && orderStatusName) {
                          const orderStatusOption = statusOptions.find(opt => opt.name === orderStatusName);
                          orderCategory = orderStatusOption?.category?.toLowerCase() || '';
                        }
                        
                        const orderSequence = statusOrder[orderCategory] || 0;
                        if (orderSequence > 0 && orderSequence < minStatusOrder) {
                          minStatusOrder = orderSequence;
                        }
                      });
                      
                      // If no valid status found, allow all (safety fallback)
                      if (minStatusOrder === 999) {
                        minStatusOrder = 0;
                      }
                      
                      // Always allow Cancelled and Returned (check by category)
                      const alwaysAvailableCategories = ['cancelled', 'returned'];
                      const optionCategory = (option.category || '').toLowerCase();
                      const isAlwaysAvailable = alwaysAvailableCategories.includes(optionCategory);
                      
                      // Get the sequence for this option
                      const optionSequence = statusOrder[optionCategory] || 0;
                      
                      // Disable if:
                      // 1. Payment required (except for "Orders" status)
                      // 2. It's before the minimum status (can't go backwards)
                      // 3. Never disable Cancelled or Returned
                      // 4. If current statusFilter is "Dispatch", disable "Dispatch" option (can only go to Delivered, Returned, or Cancelled)
                      // Note: For bulk operations, we allow jumping forward to any status (not just next sequential)
                      const isDispatchTab = statusFilter === 'Dispatch';
                      const isDispatchOption = option.name === 'Dispatch';
                      const shouldDisableByDispatchTab = isDispatchTab && isDispatchOption;
                      
                      const shouldDisableBySequence = !isAlwaysAvailable && (
                        optionSequence < minStatusOrder || 
                        shouldDisableByDispatchTab
                      );
                      
                      const shouldDisable = shouldDisableByPayment || shouldDisableBySequence;
                      
                      return (
                      <MenuItem 
                          key={option.id || option.name} 
                          value={option.name}
                        disabled={shouldDisable}
                        >

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            {getIconForCategory(option.category)}
                            <StatusChip 
                              status={option.category?.toLowerCase()}
                              label={option.name}
                              size="small"
                            />
                            {shouldDisableByPayment && (
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontSize: '0.7rem' }}>
                                Payment required
                              </Typography>
                            )}
                            {shouldDisableBySequence && !shouldDisableByPayment && (
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontSize: '0.7rem' }}>
                                {shouldDisableByDispatchTab ? 'Not available' : 'Sequential only'}
                              </Typography>
                            )}
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl> */}
  {(() => {
    const selectedOrders = filteredData.filter(order => selectedProducts.includes(order.id));
    const hasUnpaidOrders = selectedOrders.some(order => {
      const paymentStatus = (order.payment_status?.name || order.payment_status || '').toLowerCase();
      return !isPaymentSatisfiedForOrderAdvance(paymentStatus);
    });

    return (
      <>
        {/* {hasUnpaidOrders && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={600}>
              Payment Required
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              Some selected orders must be paid before they can be moved to any status other than "Orders". Please update the payment status first.
            </Typography>
          </Alert>
        )} */}

        <FormControl fullWidth>
          <InputLabel>Update Status To</InputLabel>
          <Select
            value={
              (statusFilter === 'Dispatch' && bulkChallanModal.selectedStatus === 'Dispatch')
                ? ''
                : (bulkChallanModal.selectedStatus || '')
            }
            label="Update Status To"
            onChange={(e) => {
              const selectedValue = e.target.value;
              const selectedOption = statusOptions.find(opt => opt.name === selectedValue);

              // Prevent selecting Dispatch when Dispatch tab is active
              if (statusFilter === 'Dispatch' && selectedValue === 'Dispatch') return;

              handleBulkChallanInputChange('selectedStatus', selectedValue);
              handleBulkChallanInputChange('selectedStatusCategory', selectedOption?.category || '');
              if (selectedValue === 'Delivered') {
                const selectedOrders = filteredData.filter(order => selectedProducts.includes(order.id));
                const selectedOrderWithStartKm = selectedOrders.find((order) => {
                  const startKm = order.delivery_challan?.start_km;
                  return startKm !== null && startKm !== undefined && String(startKm).trim() !== '';
                });
                const deliveryChallan =
                  selectedOrderWithStartKm?.delivery_challan ||
                  getOrderChallan(selectedOrders.find((order) => getOrderChallan(order))) ||
                  getOrderChallan(selectedOrders[0]);
                if (deliveryChallan) {
                  setBulkChallanModal(prev => ({
                    ...prev,
                    driverName: deliveryChallan.driver_name || '',
                    vehicleNo:
                      deliveryChallan.vehicle_number ||
                      deliveryChallan.vehicle_no ||
                      deliveryChallan.vehicle_name ||
                      '',
                    start_km:
                      deliveryChallan.start_km !== null && deliveryChallan.start_km !== undefined
                        ? String(deliveryChallan.start_km)
                        : '',
                    ...getChallanLogisticsDefaults(deliveryChallan),
                  }));
                }
              }
            }}
          >
            {statusOptions.map((option) => {
              // Use the same sequential validation logic as individual modal
              const statusOrder = getStatusOrder();
              const isDeliveredTab = (statusFilter || '').toLowerCase() === 'delivered';

              // Payment check (Placed-tab bulk flow: allow Cancelled while unpaid)
              const isOrdersStatus = option.name === 'Orders';
              const isCancelledOption = (option.name || '').toLowerCase() === 'cancelled';
              const shouldDisableByPayment =
                hasUnpaidOrders &&
                !isOrdersStatus &&
                !(bulkChallanModal.allowUnpaidBulkCancelFromPlaced && isCancelledOption);

              // Sequential validation - check each selected order individually
              // For bulk operations, we need to check if ALL selected orders can move to this status
              // A status is only available if it's the next sequential status for ALL selected orders
              let isAvailableForAllOrders = true;
              const optionCategory = (option.category || '').toLowerCase();
              const optionName = (option.name || '').toLowerCase();
              
              // Check each selected order
              for (const order of selectedOrders) {
                const orderStatusCategory = (order.status?.category || '').toLowerCase();
                const orderStatusName = (order.status?.name || '').toLowerCase();
                
                // Get statuses to disable for this order (using same logic as individual modal)
                const statusesToDisable = getStatusesToDisable(orderStatusCategory, orderStatusName);
                
                // Check if this option should be disabled for this order
                const shouldDisableForThisOrder = statusesToDisable.includes(optionCategory);
                
                if (shouldDisableForThisOrder) {
                  isAvailableForAllOrders = false;
                  break; // If any order can't use this status, disable it
                }
              }

              // Additional checks for Dispatch tab
              const isDispatchTab = statusFilter === 'Dispatch';
              const isDispatchOption = option.name === 'Dispatch';
              const shouldDisableByDispatchTab = isDispatchTab && isDispatchOption;

              //* Combine all disable conditions
              const isReturnedOption = optionName === 'returned' || optionCategory === 'returned';
              const shouldDisableByDeliveredTab = isDeliveredTab && !isReturnedOption;
              const shouldDisableBySequence = !isAvailableForAllOrders || shouldDisableByDispatchTab || shouldDisableByDeliveredTab;
              const allowPlacedTabBulkCancelledPick =
                bulkChallanModal.allowUnpaidBulkCancelFromPlaced && isCancelledOption;
              const shouldDisable =
                (shouldDisableByPayment || shouldDisableBySequence) && !allowPlacedTabBulkCancelledPick;

              // Dispatch tab: hide Cancelled option
              if (isDispatchTab && isCancelledOption) return null;

              if (shouldDisable) return null;

              return (
                <MenuItem
                  key={option.id || option.name}
                  value={option.name}
                  disabled={shouldDisable}
                  sx={shouldDisable ? { opacity: 0.5, backgroundColor: theme.palette.grey[100] } : {}}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    {getIconForCategory(option.category)}
                    <StatusChip status={option.category?.toLowerCase()} label={option.name} size="small" />
                    {shouldDisableByPayment && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontSize: '0.7rem' }}>
                        Payment required
                      </Typography>
                    )}
                    {shouldDisableBySequence && !shouldDisableByPayment && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontSize: '0.7rem' }}>
                        {shouldDisableByDispatchTab || shouldDisableByDeliveredTab ? 'Not available' : 'Sequential only'}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </>
    );
  })() }

                </Grid>
                
                {/* Show delivery fields only for Dispatch status */}
                {bulkChallanModal.selectedStatus === 'Dispatch' && (
                  <>
                    <Grid item xs={12}>
                      <Box sx={{ 
                        p: 2, 
                        backgroundColor: alpha(theme.palette.warning.main, 0.1),
                        borderRadius: 2,
                        mb: 2
                      }}>
                        <Typography variant="subtitle2" fontWeight={600} color="warning.main">
                          Delivery Information Required for Dispatch Status
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Please provide delivery details to create delivery challans
                        </Typography>
                      </Box>
                    </Grid>
                    
                    {/* Order By - 1st in sequence (bulk modal) */}
                    <Grid item xs={12}>
                      <FormControl fullWidth required>
                        <InputLabel>Order By</InputLabel>
                        <Select
                          value={bulkChallanModal.orderBy}
                          onChange={(e) => handleBulkChallanInputChange('orderBy', e.target.value)}
                          disabled={bulkChallanModal.isCreating}
                          label="Order By"
                        >
                          <MenuItem value="">
                            <em>Select order by</em>
                          </MenuItem>
                          {orderByChoices.map((choice) => (
                            <MenuItem key={choice.key} value={choice.key}>
                              {choice.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Entity Name - Dynamic based on Order By selection - 2nd in sequence */}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Delivery Partner Name"
                        value={bulkChallanModal.entityName}
                        onChange={(e) => {
                          const lettersOnly = e.target.value.replace(/[^A-Za-z ]/g, '');
                          handleBulkChallanInputChange('entityName', lettersOnly);
                        }}
                        disabled={bulkChallanModal.isCreating}
                        required
                        placeholder="Enter Delivery Partner"
                      />
                    </Grid>
                    {/* Driver/Authorized Name - 3rd in sequence */}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Driver/Authorized Name"
                        value={bulkChallanModal.driverName || ''}
                        onChange={(e) => {
                          const lettersOnly = e.target.value.replace(/[^A-Za-z ]/g, '');
                          handleBulkChallanInputChange('driverName', lettersOnly);
                        }}
                        disabled={bulkChallanModal.isCreating}
                        required
                        placeholder="Enter driver's full name"
                        error={bulkChallanModal.error && bulkChallanModal.error.includes('Driver name')}
                      />
                    </Grid>
                    
                    {/* Vehicle Type - only when Order By is Truck */}
                    {String(bulkChallanModal.orderBy || '').trim().toLowerCase() === 'truck' && (
                    <Grid item xs={12}>
                      <FormControl fullWidth required>
                        <InputLabel>Vehicle Type</InputLabel>
                        <Select
                          value={bulkChallanModal.vehicleType || ''}
                          label="Vehicle Type"
                          onChange={(e) => handleBulkChallanInputChange('vehicleType', e.target.value)}
                          disabled={bulkChallanModal.isCreating}
                        >
                          <MenuItem value="">
                            <em>Select vehicle type</em>
                          </MenuItem>
                          {vehicleTypes.map((type) => (
                            <MenuItem key={type.id} value={type.value}>
                              {type.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    )}

                    {/* Vehicle No / Reference No - 4th in sequence */}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Vehicle No / Reference No"
                        value={bulkChallanModal.vehicleNo || ''}
                        onChange={(e) => {
                          handleBulkChallanInputChange(
                            'vehicleNo',
                            e.target.value.replace(/[^a-zA-Z0-9 ]/g, '')
                          );
                        }}
                        disabled={bulkChallanModal.isCreating}
                        required
                        placeholder="Enter vehicle registration number"
                        error={
                          !!(bulkChallanModal.error && bulkChallanModal.error.includes('vehicle number'))
                        }
                        helperText=""
                      />
                    </Grid>

                    {String(bulkChallanModal.orderBy || '').trim().toLowerCase() === 'truck' && (
                    <>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isTruthyFlag(bulkChallanModal.meter_not_working)}
                            onChange={(e) => {
                              handleBulkChallanInputChange('meter_not_working', e.target.checked);
                              if (e.target.checked) {
                                handleBulkChallanInputChange('start_km', '');
                              } else {
                                handleBulkChallanInputChange('google_maps_km', '');
                              }
                            }}
                            disabled={bulkChallanModal.isCreating}
                          />
                        }
                        label="Meter not working"
                      />
                    </Grid>

                    {/* Start KM - only show when meter working */}
                    {!isTruthyFlag(bulkChallanModal.meter_not_working) && (
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Start KM"
                          type="number"
                          value={bulkChallanModal.start_km || ''}
                          onChange={(e) => {
                            const value = e.target.value;

                            if (value === '' || /^[1-9]\d*$/.test(value)) {
                              handleBulkChallanInputChange('start_km', value);
                            }
                          }}
                          onKeyDown={(e) => {
                            // Block unwanted keys
                            if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          disabled={bulkChallanModal.isCreating}
                          placeholder="Enter starting kilometer reading"
                          inputProps={{
                            min: 1,
                            step: 1,
                            onWheel: (e) => e.currentTarget.blur(),
                          }}
                        />
                      </Grid>
                    )}

                    {isTruthyFlag(bulkChallanModal.meter_not_working) && (
                     <Grid item xs={12} md={6}>
                     <TextField
                       fullWidth
                       required
                       label="Google Maps KM (round trip)"
                       type="number"
                       value={bulkChallanModal.google_maps_km || ''}
                       onChange={(e) => handleBulkChallanInputChange('google_maps_km', e.target.value)}
                       disabled={bulkChallanModal.isCreating}
                       placeholder="Enter Google Maps KM"
                       inputProps={{
                         min: 0.01,
                         step: 'any',
                         onWheel: (e) => {
                           e.preventDefault();
                           e.currentTarget.blur();
                         },
                       }}
                     />
                   </Grid>
                    )}

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        required
                        label="Diesel Receipt Number"
                        value={bulkChallanModal.diesel_receipt_number || ''}
                        onChange={(e) => handleBulkChallanInputChange('diesel_receipt_number', e.target.value)}
                        disabled={bulkChallanModal.isCreating}
                        placeholder="Enter diesel receipt number"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="body2" fontWeight={500} sx={{ mb: 0.75 }}>
                        Receipt Upload
                        <Box component="span" sx={{ color: 'error.main', ml: 0.25 }}>
                          *
                        </Box>
                      </Typography>
                      <input
                        id="bulk-diesel-receipt-camera-input"
                        type="file"
                        accept="image/*"
                        capture="environment"
                        style={{ display: 'none' }}
                        disabled={bulkChallanModal.isCreating}
                        onChange={(e) => {
                          const file = e.target.files?.[0] || null;
                          e.target.value = '';
                          if (!file) return;
                          if (!file.type.startsWith('image/') && !/\.(jpe?g|png|gif|webp|bmp)$/i.test(file.name || '')) {
                            setBulkChallanModal((prev) => ({
                              ...prev,
                              diesel_receipt_upload: null,
                              diesel_receipt_upload_error: 'Only image files are allowed for camera capture.',
                            }));
                            return;
                          }
                          if (file.size > 5 * 1024 * 1024) {
                            setBulkChallanModal((prev) => ({
                              ...prev,
                              diesel_receipt_upload: null,
                              diesel_receipt_upload_error: 'File size must not exceed 5 MB.',
                            }));
                            return;
                          }
                          setBulkChallanModal((prev) => ({
                            ...prev,
                            diesel_receipt_upload: file,
                            diesel_receipt_upload_error: '',
                          }));
                        }}
                      />
                      <input
                        id="bulk-diesel-receipt-file-input"
                        type="file"
                        accept="image/*,.pdf,application/pdf"
                        style={{ display: 'none' }}
                        disabled={bulkChallanModal.isCreating}
                        onChange={(e) => {
                          const file = e.target.files?.[0] || null;
                          e.target.value = '';
                          if (!file) return;
                          const isImageOrPdf =
                            file.type.startsWith('image/') ||
                            file.type === 'application/pdf' ||
                            /\.(pdf|jpe?g|png|gif|webp|bmp)$/i.test(file.name || '');
                          if (!isImageOrPdf) {
                            setBulkChallanModal((prev) => ({
                              ...prev,
                              diesel_receipt_upload: null,
                              diesel_receipt_upload_error: 'Only image or PDF files are allowed.',
                            }));
                            return;
                          }
                          if (file.size > 5 * 1024 * 1024) {
                            setBulkChallanModal((prev) => ({
                              ...prev,
                              diesel_receipt_upload: null,
                              diesel_receipt_upload_error: 'File size must not exceed 5 MB.',
                            }));
                            return;
                          }
                          setBulkChallanModal((prev) => ({
                            ...prev,
                            diesel_receipt_upload: file,
                            diesel_receipt_upload_error: '',
                          }));
                        }}
                      />
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                        <Button
                          variant="outlined"
                          component="label"
                          htmlFor="bulk-diesel-receipt-camera-input"
                          startIcon={<CameraAltIcon />}
                          disabled={bulkChallanModal.isCreating}
                          sx={{ textTransform: 'none' }}
                        >
                          Take Photo
                        </Button>
                        <Button
                          variant="outlined"
                          component="label"
                          htmlFor="bulk-diesel-receipt-file-input"
                          startIcon={<UploadFileIcon />}
                          disabled={bulkChallanModal.isCreating}
                          sx={{ textTransform: 'none' }}
                        >
                          Upload from Device
                        </Button>
                        {bulkChallanModal.diesel_receipt_upload && (
                          <Button
                            variant="text"
                            color="inherit"
                            startIcon={<CloseIcon />}
                            disabled={bulkChallanModal.isCreating}
                            onClick={() => {
                              setBulkChallanModal((prev) => ({
                                ...prev,
                                diesel_receipt_upload: null,
                                diesel_receipt_upload_error: '',
                              }));
                            }}
                            sx={{ textTransform: 'none' }}
                          >
                            Clear
                          </Button>
                        )}
                      </Stack>
                      {bulkChallanModal.diesel_receipt_upload_error && (
                        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                          {bulkChallanModal.diesel_receipt_upload_error}
                        </Typography>
                      )}
                      {bulkChallanModal.diesel_receipt_upload && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Selected: {bulkChallanModal.diesel_receipt_upload.name}
                        </Typography>
                      )}
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        required
                        label="Diesel Amount"
                        type="number"
                        value={bulkChallanModal.diesel_amount || ''}
                        onChange={(e) => handleBulkChallanInputChange('diesel_amount', e.target.value)}
                        disabled={bulkChallanModal.isCreating}
                        placeholder="Enter diesel amount"
                        inputProps={{
                          min: 0,
                          step: 'any',
                          onWheel: (e) => e.currentTarget.blur(),
                        }}
                      />
                    </Grid>
                    </>
                    )}

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Crates Dispatched"
                        type="number"
                        value={bulkChallanModal.crates_dispatched ?? '0'}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || /^\d+$/.test(value)) {
                            handleBulkChallanInputChange('crates_dispatched', value);
                          }
                        }}
                        disabled={bulkChallanModal.isCreating}
                        placeholder="0"
                        inputProps={{
                          min: 0,
                          step: 1,
                          onWheel: (e) => e.currentTarget.blur(),
                        }}
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Box>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={bulkChallanModal.useCustomDispatchPerson}
                              onChange={(e) => handleBulkChallanInputChange('useCustomDispatchPerson', e.target.checked)}
                              disabled={bulkChallanModal.isCreating}
                            />
                          }
                          label="Add custom dispatch person"
                        />
                        {bulkChallanModal.useCustomDispatchPerson ? (
                          <TextField
                            fullWidth
                            label="Custom Dispatch Person Name"
                            value={bulkChallanModal.customDispatchPerson}
                            onChange={(e) => handleBulkChallanInputChange('customDispatchPerson', e.target.value)}
                            disabled={bulkChallanModal.isCreating}
                            placeholder="Enter custom dispatch person name"
                            sx={{ mt: 1 }}
                          />
                        ) : (
                          <FormControl fullWidth sx={{ mt: 1 }}>
                            <InputLabel>Dispatch Person Name</InputLabel>
                            <Select
                              value={bulkChallanModal.dispatchPersonName}
                              label="Dispatch Person Name"
                              onChange={(e) => handleBulkChallanInputChange('dispatchPersonName', e.target.value)}
                              disabled={bulkChallanModal.isCreating}
                            >
                              <MenuItem value="">
                                <em>Select dispatch person</em>
                              </MenuItem>
                              {dispatchPersons.map((person) => (
                                <MenuItem key={person.id} value={person.name}>
                                  {person.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Delivery Date"
                        type="date"
                        value={bulkChallanModal.deliveryDate || new Date().toISOString().slice(0,10)}
                        onChange={(e) => handleBulkChallanInputChange('deliveryDate', e.target.value)}
                        disabled={bulkChallanModal.isCreating}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Email Addresses (Optional)
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                          <TextField
                            fullWidth
                            size="small"
                            value={bulkChallanModal.emailInput || ''}
                            onChange={(e) => handleBulkChallanInputChange('emailInput', e.target.value)}
                            disabled={bulkChallanModal.isCreating}
                            placeholder="Enter email address"
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                addEmailToBulkModal();
                              }
                            }}
                          />
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={addEmailToBulkModal}
                            disabled={!bulkChallanModal.emailInput?.trim() || bulkChallanModal.isCreating}
                          >
                            Add
                          </Button>
                        </Box>
                        {bulkChallanModal.emails && bulkChallanModal.emails.length > 0 && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {bulkChallanModal.emails.map((email, index) => (
                              <Chip
                                key={index}
                                label={email}
                                onDelete={() => removeEmailFromBulkModal(email)}
                                size="small"
                                disabled={bulkChallanModal.isCreating}
                              />
                            ))}
                          </Box>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          Delivery challans will be sent to these email addresses
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={bulkChallanModal.generateInvoice || false}
                            onChange={(e) => handleBulkChallanInputChange('generateInvoice', e.target.checked)}
                            disabled={
                              bulkChallanModal.isCreating ||
                              (['processing'].includes((statusFilter || '').toLowerCase()) &&
                                !shouldEnableButton?.('generate_invoice_along_with_delivery_challan'))
                            }
                          />
                        }
                        label="Generate Invoice along with Delivery Challan"
                      />
                    </Grid>
                  </>
                )}

                  {/* Delivered logistics + End KM when applicable */}
                  {bulkChallanModal.selectedStatus === 'Delivered' && (() => {
                    const selectedOrders = filteredData.filter(order => selectedProducts.includes(order.id));
                    const isDispatchTabForBulk = ['dispatch', 'shipped'].includes((statusFilter || '').toLowerCase());
                    const selectedOrderWithStartKm = selectedOrders.find(order => {
                      const startKm = order.delivery_challan?.start_km;
                      const hasStartKm = startKm !== null && startKm !== undefined && String(startKm).trim() !== '';
                      if (isDispatchTabForBulk && !isOrderByTruck(order)) return false;
                      return hasStartKm;
                    });
                    const deliveryChallan =
                      selectedOrderWithStartKm?.delivery_challan ||
                      getOrderChallan(selectedOrders.find((order) => getOrderChallan(order))) ||
                      getOrderChallan(selectedOrders[0]);

                    const meterNotWorking = isTruthyFlag(
                      bulkChallanModal.meter_not_working ?? deliveryChallan?.meter_not_working
                    );
                    const googleMapsKm =
                      bulkChallanModal.google_maps_km ||
                      (deliveryChallan?.google_maps_km !== null &&
                      deliveryChallan?.google_maps_km !== undefined
                        ? String(deliveryChallan.google_maps_km)
                        : '');
                    const cratesDispatched = Number(
                      bulkChallanModal.crates_dispatched !== undefined &&
                        String(bulkChallanModal.crates_dispatched).trim() !== ''
                        ? bulkChallanModal.crates_dispatched
                        : (deliveryChallan?.crates_dispatched ?? 0)
                    );
                    const cratesReturned = Number(bulkChallanModal.crates_returned);
                    const plantsReturnedRaw = String(bulkChallanModal.plants_returned ?? '').trim();
                    const plantsReturned = plantsReturnedRaw === '' ? 0 : Number(plantsReturnedRaw);
                    const finalKm = Number(bulkChallanModal.final_km);
                    const mapsKmNumber = Number(googleMapsKm);
                    const showKmExcessReason =
                      meterNotWorking &&
                      String(bulkChallanModal.final_km || '').trim() !== '' &&
                      !Number.isNaN(finalKm) &&
                      !Number.isNaN(mapsKmNumber) &&
                      finalKm > mapsKmNumber;
                    const showCratesShortReason =
                      String(bulkChallanModal.crates_returned || '').trim() !== '';
                    const showPlantsReason = plantsReturned > 0;

                    const driverName = bulkChallanModal.driverName || deliveryChallan?.driver_name || '';
                    const vehicleNo =
                      bulkChallanModal.vehicleNo ||
                      deliveryChallan?.vehicle_number ||
                      deliveryChallan?.vehicle_no ||
                      deliveryChallan?.vehicle_name ||
                      '';
                    const startKm =
                      bulkChallanModal.start_km ||
                      (
                        deliveryChallan?.start_km !== null && deliveryChallan?.start_km !== undefined
                          ? String(deliveryChallan.start_km)
                          : ''
                      );
                    const showEndKmSection = !meterNotWorking && !!deliveryChallan && String(startKm).trim() !== '';

                    return (
                      <>
                        {showEndKmSection && (
                          <>
                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                label="Driver Name"
                                value={driverName}
                                disabled
                                InputProps={{ readOnly: true }}
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                label="Vehicle No / Reference No"
                                value={vehicleNo}
                                disabled
                                InputProps={{ readOnly: true }}
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                label="Start KM"
                                value={startKm}
                                disabled
                                InputProps={{ readOnly: true }}
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="End KM"
                              required
                              type="number"
                              value={bulkChallanModal.end_km || ''}
                              onChange={(e) => {
                                const endKmValue = e.target.value;
                                handleBulkChallanInputChange('end_km', endKmValue);
                              }}
                              disabled={bulkChallanModal.isCreating}
                              placeholder="Enter ending kilometer reading"
                              inputProps={{
                                onWheel: (e) => {
                                  e.preventDefault();
                                  e.currentTarget.blur();
                                },
                              }}
                              error={(() => {
                                const parsedStartKm = parseFloat(startKm);
                                const endKm = parseFloat(bulkChallanModal.end_km);
                                const hasEndKm = String(bulkChallanModal.end_km || '').trim() !== '';
                                return !hasEndKm || (!isNaN(parsedStartKm) && !isNaN(endKm) && endKm <= parsedStartKm);
                              })()}
                              helperText={(() => {
                                const parsedStartKm = parseFloat(startKm);
                                const endKm = parseFloat(bulkChallanModal.end_km);
                                const hasEndKm = String(bulkChallanModal.end_km || '').trim() !== '';
                                if (!hasEndKm) {
                                  return 'End KM is required';
                                }
                                if (!isNaN(parsedStartKm) && !isNaN(endKm) && endKm <= parsedStartKm) {
                                  return 'End KM must be greater than Start KM';
                                }
                                return '';
                              })()}
                            />
                          </Grid>
                          </>
                        )}

                        <Grid item xs={12}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            Delivery Logistics
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Meter not Working"
                            value={meterNotWorking ? 'Yes' : 'No'}
                            disabled
                            InputProps={{ readOnly: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Google Maps KM"
                            value={googleMapsKm || '—'}
                            disabled
                            InputProps={{ readOnly: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Crates Dispatched"
                            value={String(cratesDispatched)}
                            disabled
                            InputProps={{ readOnly: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Diesel Receipt Number"
                            value={
                              bulkChallanModal.diesel_receipt_number ||
                              deliveryChallan?.diesel_receipt_number ||
                              '—'
                            }
                            disabled
                            InputProps={{ readOnly: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Diesel Amount"
                            value={
                              bulkChallanModal.diesel_amount ||
                              (deliveryChallan?.diesel_amount !== null &&
                              deliveryChallan?.diesel_amount !== undefined
                                ? String(deliveryChallan.diesel_amount)
                                : '—')
                            }
                            disabled
                            InputProps={{ readOnly: true }}
                          />
                        </Grid>

                        {meterNotWorking && (
                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                required
                                label="Final KM"
                                type="number"
                                value={bulkChallanModal.final_km || ''}
                                onChange={(e) => handleBulkChallanInputChange('final_km', e.target.value)}
                                disabled={bulkChallanModal.isCreating}
                                placeholder="Enter actual trip KM"
                                inputProps={{
                                  min: 0,
                                  step: 'any',
                                  onWheel: (e) => {
                                    e.preventDefault();
                                    e.currentTarget.blur();
                                  },
                                }}
                              />
                            </Grid>
                          )}

                        {showKmExcessReason && (
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              required
                              label="KM Excess Reason"
                              value={bulkChallanModal.km_excess_reason || ''}
                              onChange={(e) => handleBulkChallanInputChange('km_excess_reason', e.target.value)}
                              disabled={bulkChallanModal.isCreating}
                              placeholder="Why KM exceeded Maps estimate"
                            />
                          </Grid>
                        )}

                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            required
                            label="Crates Returned"
                            type="number"
                            value={bulkChallanModal.crates_returned || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || /^\d+$/.test(value)) {
                                handleBulkChallanInputChange('crates_returned', value);
                              }
                            }}
                            disabled={bulkChallanModal.isCreating}
                            placeholder="Enter crates returned"
                            inputProps={{
                              min: 0,
                              step: 1,
                              onWheel: (e) => {
                                e.preventDefault();
                                e.currentTarget.blur();
                              },
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            required
                            label="Crates Return Person"
                            value={bulkChallanModal.crates_return_person || ''}
                            onChange={(e) => {
                              const value = e.target.value.replace(/[^a-zA-Z\s]/g, '');
                              handleBulkChallanInputChange('crates_return_person', value);
                            }}
                            disabled={bulkChallanModal.isCreating}
                            placeholder="Enter crates return person name"
                            InputLabelProps={{ shrink: true }}
                            sx={{
                              '& .MuiFormLabel-asterisk': { color: 'error.main' },
                            }}
                          />
                        </Grid>

                        {showCratesShortReason && (
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              required
                              label="Crates Short Reason"
                              value={bulkChallanModal.crates_short_reason || ''}
                              onChange={(e) => handleBulkChallanInputChange('crates_short_reason', e.target.value)}
                              disabled={bulkChallanModal.isCreating}
                              placeholder="Why returned crates are less than dispatched"
                            />
                          </Grid>
                        )}

                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Plants Returned"
                            type="number"
                            value={bulkChallanModal.plants_returned ?? '0'}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || /^\d+$/.test(value)) {
                                handleBulkChallanInputChange('plants_returned', value);
                              }
                            }}
                            disabled={bulkChallanModal.isCreating}
                            placeholder="0"
                            inputProps={{
                              min: 0,
                              step: 1,
                              onWheel: (e) => {
                                e.preventDefault();
                                e.currentTarget.blur();
                              },
                            }}
                          />
                        </Grid>

                        {showPlantsReason && (
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              required
                              label="Plants Returned Reason"
                              value={bulkChallanModal.plants_returned_reason || ''}
                              onChange={(e) => handleBulkChallanInputChange('plants_returned_reason', e.target.value)}
                              disabled={bulkChallanModal.isCreating}
                              placeholder="Why plants were returned"
                            />
                          </Grid>
                        )}
                      </>
                    );
                  })()}

                {bulkChallanModal.selectedStatus === 'Cancelled' && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Reason"
                      value={bulkChallanModal.cancellationReason || ''}
                      onChange={(e) => handleBulkChallanInputChange('cancellationReason', e.target.value)}
                      disabled={bulkChallanModal.isCreating}
                      required
                      placeholder="Enter reason for cancellation"
                      sx={{ mt: 2 }}
                      multiline
                      rows={3}
                    />
                  </Grid>
                )}

                {bulkChallanModal.selectedStatus === 'Returned' && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Reason"
                      value={bulkChallanModal.returnReason || ''}
                      onChange={(e) => handleBulkChallanInputChange('returnReason', e.target.value)}
                      disabled={bulkChallanModal.isCreating}
                      required
                      placeholder="Enter reason for returned"
                      sx={{ mt: 2 }}
                      multiline
                      rows={3}
                    />
                  </Grid>
                )}
              </Grid>

            
            </Box>
          </DialogContent>

          <DialogActions>
            <Button 
              onClick={handleCloseBulkChallanModal}
              disabled={bulkChallanModal.isCreating}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleBulkChallanSubmit}
              variant="contained"
              color="secondary"
              disabled={isBulkSubmitDisabled()}
              startIcon={
                bulkChallanModal.isCreating ? 
                <CircularProgress size={16} /> : 
                <DeliveryIcon />
              }
              sx={{
                '&:hover': {
                  backgroundColor: theme.palette.success.main,
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              {bulkChallanModal.isCreating ? 'Processing...' : 
                bulkChallanModal.selectedStatus === 'Dispatch' ? 
                  `Create ${getEligibleProductsCount()} Challans` : 
                  `Update ${getSelectedProductsCount()} Orders`
              }
            </Button>
          </DialogActions>
        </Dialog>

        {/* Email Dialogs */}
        <Dialog
          open={emailDialog.open}
          onClose={handleCloseEmailDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EmailIcon color="primary" />
              <Typography variant="h6" fontWeight={600}>Send Documents via Email</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Selected orders: {selectedProducts.length}
            </Typography>
          </DialogTitle>
          <DialogContent>
            {emailDialog.error && (
              <Alert severity="error" sx={{ mb: 2 }}>{emailDialog.error}</Alert>
            )}
            {emailDialog.success && (
              <Alert severity="success" sx={{ mb: 2 }}>{emailDialog.success}</Alert>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Add Email"
                    value={emailDialog.emailInput}
                    onChange={(e) =>
                      setEmailDialog((prev) => ({
                        ...prev,
                        emailInput: e.target.value,
                        error: '',
                        success: '',
                      }))
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        addEmailToBulkDialog()
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={addEmailToBulkDialog}
                    sx={{ minWidth: 92, textTransform: 'none' }}
                    startIcon={<AddIcon />}
                  >
                    Add
                  </Button>
                </Box>
                {emailDialog.emails.length > 0 && (
                  <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {emailDialog.emails.map((email) => (
                      <Chip
                        key={email}
                        label={email}
                        onDelete={() => removeEmailFromBulkDialog(email)}
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                    ))}
                  </Box>
                )}
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  {(statusFilter || '').toLowerCase() !== 'dispatch' && (
                    <FormControlLabel
                      control={<Checkbox checked={emailDialog.sendInvoice} onChange={(e) => handleEmailDialogChange('sendInvoice', e.target.checked)} />}
                      label={`Include Invoices (${getAvailableCounts().invoices} available)`}
                    />
                  )}
                  <FormControlLabel
                    control={<Checkbox checked={emailDialog.sendDelivery} onChange={(e) => handleEmailDialogChange('sendDelivery', e.target.checked)} />}
                    label={`Include Delivery Challans (${getAvailableCounts().challans} available)`}
                  />
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEmailDialog} disabled={emailDialog.isSending}>Cancel</Button>
            <Button
              variant="contained"
              startIcon={emailDialog.isSending ? <CircularProgress size={16} /> : <SendIcon />}
              disabled={
                emailDialog.isSending ||
                (emailDialog.emails.length === 0 && !isValidBulkEmailInput(emailDialog.emailInput))
              }
              onClick={handleEmailSendSubmit}
              sx={{ textTransform: 'none' }}
            >
              {emailDialog.isSending ? 'Sending...' : 'Send Email'}
            </Button>
          </DialogActions>
        </Dialog>

        <AppOrderPopup open={singleEmailDialog.open} onClose={closeSingleOrderEmail} maxWidth="sm" fullWidth>
          <DialogTitle>Send Documents via Email</DialogTitle>
          <DialogContent>
            <Stack spacing={2.25} sx={{ mt: 1 }}>
              {singleEmailDialog.order && (
                <Typography variant="body2" color="text.secondary">
                  Order ID: {singleEmailDialog.order.order_id}
                </Typography>
              )}

              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Add Email"
                  value={singleEmailDialog.emailInput}
                  onChange={(e) => setSingleEmailDialog(prev => ({ ...prev, emailInput: e.target.value, error: '', success: '' }))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') addEmailToSingle()
                  }}
                />
                <Button
                  variant="contained"
                  onClick={addEmailToSingle}
                  sx={{ minWidth: 92, textTransform: 'none' }}
                  startIcon={<AddIcon />}
                >
                  Add
                </Button>
              </Box>

              {singleEmailDialog.emails.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {singleEmailDialog.emails.map((email) => (
                    <Chip
                      key={email}
                      label={email}
                      onDelete={() => removeEmailFromSingle(email)}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  ))}
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 3 }}>
                {(statusFilter || '').toLowerCase() !== 'dispatch' && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={singleEmailDialog.sendInvoice}
                        onChange={(e) => setSingleEmailDialog(prev => ({ ...prev, sendInvoice: e.target.checked, error: '', success: '' }))}
                        disabled={!extractInvoice(singleEmailDialog.order)?.path}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">include Invoice</Typography>
                        {!extractInvoice(singleEmailDialog.order)?.path && (
                          <Typography variant="caption" color="text.secondary">
                            (not available)
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                )}

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={singleEmailDialog.sendDelivery}
                      onChange={(e) => setSingleEmailDialog(prev => ({ ...prev, sendDelivery: e.target.checked, error: '', success: '' }))}
                      disabled={!extractDeliveryChallan(singleEmailDialog.order)?.path}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">Include Delivery Challan</Typography>
                      {!extractDeliveryChallan(singleEmailDialog.order)?.path && (
                        <Typography variant="caption" color="text.secondary">
                          (not available)
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </Box>

              {singleEmailDialog.error && (
                <Alert severity="error" onClose={() => setSingleEmailDialog(prev => ({ ...prev, error: '' }))}>
                  {singleEmailDialog.error}
                </Alert>
              )}

              {singleEmailDialog.success && (
                <Alert severity="success" onClose={() => setSingleEmailDialog(prev => ({ ...prev, success: '' }))}>
                  {singleEmailDialog.success}
                </Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeSingleOrderEmail} disabled={singleEmailDialog.isSending}>Cancel</Button>
            <Button
              variant="contained"
              startIcon={singleEmailDialog.isSending ? <CircularProgress size={16} /> : <SendIcon />}
              disabled={
                singleEmailDialog.isSending ||
                (!extractInvoice(singleEmailDialog.order)?.path && !extractDeliveryChallan(singleEmailDialog.order)?.path) ||
                (singleEmailDialog.emails.length === 0 && !isValidSingleEmailInput(singleEmailDialog.emailInput))
              }
              onClick={submitSingleOrderEmail}
            >
              {singleEmailDialog.isSending ? 'Sending...' : 'Send Email'}
            </Button>
          </DialogActions>
        </AppOrderPopup>

        {/* Bulk Payment Status Update Modal */}
        <Dialog
          open={bulkPaymentStatusModal.open}
          onClose={handleCloseBulkPaymentStatusModal}
          maxWidth={bulkPaymentStatusModal.step === 'payment_details' ? false : 'sm'}
          fullWidth={bulkPaymentStatusModal.step !== 'payment_details'}
          scroll={bulkPaymentStatusModal.step === 'payment_details' ? 'paper' : 'body'}
          PaperProps={
            bulkPaymentStatusModal.step === 'payment_details'
              ? PAYMENT_DETAILS_DIALOG_PAPER_PROPS
              : undefined
          }
        >
          <DialogTitle>
            {bulkPaymentStatusModal.step === 'payment_option'
              ? 'Payment Option'
              : bulkPaymentStatusModal.step === 'payment_details'
                ? 'Payment Details'
                : 'Update Payment Status'}
            <Typography variant="body2" color="text.secondary">
              {getUnpaidOrdersFromList(getSelectedOrdersForBulkPayment()).length} unpaid of{' '}
              {getSelectedProductsCount()} selected order{getSelectedProductsCount() > 1 ? 's' : ''}
            </Typography>
          </DialogTitle>
          <DialogContent
            sx={
              bulkPaymentStatusModal.step === 'payment_details'
                ? PAYMENT_DETAILS_DIALOG_CONTENT_SX
                : undefined
            }
          >
            {bulkPaymentStatusModal.error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {bulkPaymentStatusModal.error}
              </Alert>
            )}

            {bulkPaymentStatusModal.paymentError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {bulkPaymentStatusModal.paymentError}
              </Alert>
            )}

            {bulkPaymentStatusModal.isUpdating && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  Updating payment status
                </Box>
              </Alert>
            )}

            {bulkPaymentStatusModal.step === 'payment_option' && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Choose your payment method:
                </Typography>
                <RadioGroup
                  value={bulkPaymentStatusModal.paymentOption}
                  onChange={(e) =>
                    setBulkPaymentStatusModal((prev) => ({
                      ...prev,
                      paymentOption: e.target.value,
                      paymentError: '',
                      ...(e.target.value === 'full'
                        ? {
                            advanceAmount: String(
                              prev.summaryOrder?.total_amount ?? prev.summaryOrder?.total ?? ''
                            ),
                          }
                        : { advanceAmount: '' }),
                    }))
                  }
                >
                  <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, borderRadius: 2 }}>
                    <FormControlLabel
                      value="full"
                      control={<Radio />}
                      label="Order with full Payment"
                    />
                  </Paper>
                  {/* <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <FormControlLabel
                      value="advance"
                      control={<Radio />}
                      label="Order with Advanced Payment"
                    />
                  </Paper> */}
                </RadioGroup>
              </Box>
            )}

            {bulkPaymentStatusModal.step === 'payment_details' && (() => {
              const unpaidCount = getUnpaidOrdersFromList(getSelectedOrdersForBulkPayment()).length;

              return (
                <PaymentDetailsStepContent
                  idPrefix="bulk-payment"
                  order={bulkPaymentStatusModal.summaryOrder}
                  orderPaymentDetails={getBulkPaidPaymentModalState().orderPaymentDetails}
                  summaryPaymentDetails={getBulkSelectedPaymentSummary()}
                  paymentOption={bulkPaymentStatusModal.paymentOption}
                  advanceAmount={bulkPaymentStatusModal.advanceAmount}
                  onAdvanceAmountChange={(e) =>
                    setBulkPaymentStatusModal((prev) => ({
                      ...prev,
                      advanceAmount: e.target.value,
                      paymentError: '',
                    }))
                  }
                  creditNote={bulkPaymentStatusModal.creditNote || ''}
                  onCreditNoteChange={(e) =>
                    setBulkPaymentStatusModal((prev) => ({
                      ...prev,
                      creditNote: e.target.value,
                      paymentError: '',
                    }))
                  }
                  paymentMethod={bulkPaymentStatusModal.paymentMethod}
                  paymentMethodConfig={bulkPaymentStatusModal.paymentMethodConfig}
                  advancePaymentMethods={advancePaymentMethods}
                  loadingAdvancePaymentMethods={loadingAdvancePaymentMethods}
                  onPaymentMethodChange={(e) => {
                    const methodId = e.target.value;
                    setBulkPaymentStatusModal((prev) => ({
                      ...prev,
                      ...buildPaymentMethodChangePayload(
                        methodId,
                        prev.orderPaymentDetails
                      ),
                    }));
                  }}
                  paymentFields={bulkPaymentStatusModal.paymentFields}
                  paymentFiles={bulkPaymentStatusModal.paymentFiles}
                  onFieldChange={(key, value) => {
                    if (key === 'cheque_amount' && bulkPaymentStatusModal.orderPaymentDetails) {
                      return;
                    }
                    setBulkPaymentStatusModal((prev) => ({
                      ...prev,
                      paymentFields: { ...prev.paymentFields, [key]: value },
                      paymentError: '',
                    }));
                  }}
                  onFileChange={(key, file) =>
                    setBulkPaymentStatusModal((prev) => ({
                      ...prev,
                      paymentFiles: { ...prev.paymentFiles, [key]: file },
                      paymentError: '',
                    }))
                  }
                  itemsLimit={3}
                  totalLabel="Reference total"
                  summaryBanner={
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Applying payment to <strong>{unpaidCount}</strong> unpaid order
                      {unpaidCount > 1 ? 's' : ''}.
                    </Typography>
                  }
                />
              );
            })()}

            {bulkPaymentStatusModal.step === 'status' && (
            <>
            {bulkPaymentStatusModal.summaryOrder &&
              isPaymentUpdateDisabled({
                ...bulkPaymentStatusModal.summaryOrder,
                payment_status: {
                  name: bulkPaymentStatusModal.initialPaymentStatus || '',
                },
              }) && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {getPaymentUpdateDisabledReason(bulkPaymentStatusModal.summaryOrder, statusFilter)}
              </Alert>
            )}
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Payment Status</InputLabel>
                <Select
                  value={bulkPaymentStatusModal.selectedPaymentStatus || ''}
                  label="Payment Status"
                  onChange={(e) => {
                    const newStatus = e.target.value;
                    if (newStatus === 'paid') {
                      loadAdvancePaymentMethods();
                    }
                    setBulkPaymentStatusModal((prev) => ({
                      ...prev,
                      selectedPaymentStatus: newStatus,
                      error: '',
                      paymentError: '',
                      refundAmount:
                        newStatus === 'refunded'
                          ? (prev.summaryOrder?.total_amount ?? prev.refundAmount ?? '')
                          : prev.refundAmount,
                      ...(newStatus === 'paid'
                        ? getInlinePaidPaymentStepState(prev.orderPaymentDetails)
                        : newStatus === 'credit'
                          ? getCreditPaymentStepState(prev.orderPaymentDetails)
                          : {
                              step: 'status',
                              paymentOption: '',
                              advanceAmount: '',
                              installmentAmount: '',
                              creditNote: '',
                              paymentMethod: '',
                              paymentMethodConfig: null,
                              paymentFields: {},
                              paymentFiles: {},
                            }),
                    }));
                  }}
                  disabled={
                    bulkPaymentStatusModal.isUpdating ||
                    loadingPaymentStatusChoices ||
                    isPaymentUpdateDisabled({
                      ...bulkPaymentStatusModal.summaryOrder,
                      payment_status: {
                        name: bulkPaymentStatusModal.initialPaymentStatus || '',
                      },
                    })
                  }
                  MenuProps={{
                    disableScrollLock: true,
                    PaperProps: {
                      sx: { maxHeight: 300 },
                    },
                    sx: {
                      zIndex: (theme) => theme.zIndex.modal + 1,
                    },
                  }}
                >
                  {renderPaymentStatusMenuItems()}
                </Select>
              </FormControl>
            </>
            )}
            {bulkPaymentStatusModal.step === 'status' &&
              (bulkPaymentStatusModal.paymentOption === 'credit' ||
                bulkPaymentStatusModal.paymentOption === 'remaining') && (() => {
                const unpaidCount = getUnpaidOrdersFromList(getSelectedOrdersForBulkPayment()).length;
                return (
                  <Box sx={{ mt: 2 }}>
                    <PaymentDetailsStepContent
                      idPrefix="bulk-payment-credit"
                      order={bulkPaymentStatusModal.summaryOrder}
                      orderPaymentDetails={getBulkPaidPaymentModalState().orderPaymentDetails}
                      summaryPaymentDetails={getBulkSelectedPaymentSummary()}
                      paymentOption={bulkPaymentStatusModal.paymentOption}
                      advanceAmount={bulkPaymentStatusModal.advanceAmount}
                      onAdvanceAmountChange={(e) =>
                        setBulkPaymentStatusModal((prev) => ({
                          ...prev,
                          advanceAmount: e.target.value,
                          paymentError: '',
                        }))
                      }
                      installmentAmount={bulkPaymentStatusModal.installmentAmount || ''}
                      onInstallmentAmountChange={(e) => {
                        const value = e.target.value;
                        const remaining = Number(
                          bulkPaymentStatusModal.orderPaymentDetails?.remainingAmount ?? 0
                        );
                        if (value !== '' && Number(value) > remaining) {
                          setBulkPaymentStatusModal((prev) => ({
                            ...prev,
                            paymentError: 'Installment amount cannot exceed the remaining amount',
                          }));
                          return;
                        }
                        setBulkPaymentStatusModal((prev) => ({
                          ...prev,
                          installmentAmount: value,
                          paymentError: '',
                          ...(prev.paymentOption === 'credit'
                            ? {
                                paymentFields: getCreditChequePaymentFields(
                                  prev.paymentFields,
                                  value,
                                  prev.paymentMethodConfig
                                ),
                              }
                            : {}),
                        }));
                      }}
                      creditNote={bulkPaymentStatusModal.creditNote || ''}
                      onCreditNoteChange={(e) =>
                        setBulkPaymentStatusModal((prev) => ({
                          ...prev,
                          creditNote: e.target.value,
                          paymentError: '',
                        }))
                      }
                      paymentMethod={bulkPaymentStatusModal.paymentMethod}
                      paymentMethodConfig={bulkPaymentStatusModal.paymentMethodConfig}
                      advancePaymentMethods={advancePaymentMethods}
                      loadingAdvancePaymentMethods={loadingAdvancePaymentMethods}
                      onPaymentMethodChange={(e) => {
                        const methodId = e.target.value;
                        setBulkPaymentStatusModal((prev) => {
                          const payload = buildPaymentMethodChangePayload(
                            methodId,
                            prev.orderPaymentDetails
                          );
                          if (prev.paymentOption === 'credit' && payload.paymentMethodConfig?.type === 'cheque') {
                            payload.paymentFields = getCreditChequePaymentFields(
                              payload.paymentFields,
                              prev.installmentAmount ?? '',
                              payload.paymentMethodConfig
                            );
                          }
                          return { ...prev, ...payload };
                        });
                      }}
                      paymentFields={bulkPaymentStatusModal.paymentFields}
                      paymentFiles={bulkPaymentStatusModal.paymentFiles}
                      onFieldChange={(key, value) => {
                        if (key === 'cheque_amount' && bulkPaymentStatusModal.orderPaymentDetails) {
                          return;
                        }
                        setBulkPaymentStatusModal((prev) => ({
                          ...prev,
                          paymentFields: { ...prev.paymentFields, [key]: value },
                          paymentError: '',
                        }));
                      }}
                      onFileChange={(key, file) =>
                        setBulkPaymentStatusModal((prev) => ({
                          ...prev,
                          paymentFiles: { ...prev.paymentFiles, [key]: file },
                          paymentError: '',
                        }))
                      }
                      itemsLimit={3}
                      totalLabel="Reference total"
                      summaryBanner={
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Applying{' '}
                          {bulkPaymentStatusModal.paymentOption === 'credit'
                            ? 'credit payment'
                            : 'payment'}{' '}
                          to <strong>{unpaidCount}</strong> unpaid order
                          {unpaidCount > 1 ? 's' : ''}.
                        </Typography>
                      }
                    />
                  </Box>
                );
              })()}
            {bulkPaymentStatusModal.step === 'status' &&
              ['failed', 'pending', 'outstanding'].includes(
                bulkPaymentStatusModal.selectedPaymentStatus
              ) && (
                <Box sx={{ mt: 2 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Note"
                    value={bulkPaymentStatusModal.creditNote || ''}
                    onChange={(e) =>
                      setBulkPaymentStatusModal((prev) => ({
                        ...prev,
                        creditNote: e.target.value,
                        paymentError: '',
                      }))
                    }
                    placeholder="Enter note"
                    multiline
                    minRows={3}
                    required
                  />
                </Box>
              )}
            {bulkPaymentStatusModal.step === 'status' &&
              bulkPaymentStatusModal.selectedPaymentStatus === 'refunded' &&
              (() => {
                const apiDetails = bulkPaymentStatusModal.orderPaymentDetails;
                const totalAmount = Number(
                  apiDetails?.totalAmount ??
                    bulkPaymentStatusModal.summaryOrder?.total_amount ??
                    bulkPaymentStatusModal.summaryOrder?.total ??
                    0
                );
                const advancePaidTotal = getAdvancePaymentEntries(
                  bulkPaymentStatusModal.summaryOrder || bulkPaymentStatusModal
                ).reduce((sum, entry) => sum + Number(entry.advance_amount || 0), 0);
                const paidAmountFallback =
                  advancePaidTotal > 0
                    ? advancePaidTotal
                    : (bulkPaymentStatusModal.summaryOrder?.amount_paid ??
                        bulkPaymentStatusModal.summaryOrder?.paid ??
                        0);
                const paidAmount = Number(apiDetails?.paidAmount ?? paidAmountFallback);
                const pendingAmount = Number(
                  apiDetails?.remainingAmount ?? Math.max(0, totalAmount - paidAmount)
                );
                const maxAmount = paidAmount;
                const refundAmount = Number(bulkPaymentStatusModal.refundAmount || 0);
                const isExceeding =
                  bulkPaymentStatusModal.refundAmount !== '' &&
                  (!Number.isFinite(refundAmount) || refundAmount <= 0 || refundAmount > maxAmount);
                const formatAmount = (n) =>
                  Number.isNaN(n)
                    ? '0.00'
                    : n.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      });

                return (
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ mb: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <TextField
                        fullWidth
                        label="Refund Amount"
                        type="number"
                        value={bulkPaymentStatusModal.refundAmount ?? ''}
                        placeholder="Enter refund amount"
                        inputProps={{
                          min: 0,
                          max: maxAmount,
                          step: 0.01,
                        }}
                        error={isExceeding}
                        helperText={
                          isExceeding
                            ? 'Refund amount cannot exceed the available paid amount'
                            : ''
                        }
                        onChange={(e) =>
                          setBulkPaymentStatusModal((prev) => ({
                            ...prev,
                            refundAmount: e.target.value,
                          }))
                        }
                      />
                      <Box display="flex" gap={2} mt={2}>
                        <TextField
                          label="Total"
                          value={formatAmount(totalAmount)}
                          size="small"
                          fullWidth
                          disabled
                        />
                        <TextField
                          label="Paid"
                          value={formatAmount(paidAmount)}
                          size="small"
                          fullWidth
                          disabled
                        />
                        <TextField
                          label="Pending"
                          value={formatAmount(pendingAmount)}
                          size="small"
                          fullWidth
                          disabled
                        />
                      </Box>
                      <TextField
                        fullWidth
                        size="small"
                        label="Note"
                        value={bulkPaymentStatusModal.creditNote || ''}
                        onChange={(e) =>
                          setBulkPaymentStatusModal((prev) => ({
                            ...prev,
                            creditNote: e.target.value,
                            paymentError: '',
                          }))
                        }
                        placeholder="Enter note"
                        multiline
                        minRows={3}
                        required
                        sx={{ mt: 2 }}
                      />
                    </Box>
                  </Box>
                );
              })()}
          </DialogContent>
          <DialogActions
            sx={
              bulkPaymentStatusModal.step === 'payment_details'
                ? PAYMENT_DETAILS_DIALOG_ACTIONS_SX
                : undefined
            }
          >
            <Button
              onClick={() => {
                if (bulkPaymentStatusModal.step === 'payment_details') {
                  setBulkPaymentStatusModal((prev) => ({
                    ...prev,
                    step: 'status',
                    selectedPaymentStatus: '',
                    paymentOption: '',
                    advanceAmount: '',
                    paymentMethod: '',
                    paymentFields: {},
                    paymentFiles: {},
                    paymentError: '',
                  }));
                  return;
                }
                handleCloseBulkPaymentStatusModal();
              }}
              disabled={bulkPaymentStatusModal.isUpdating}
            >
              {bulkPaymentStatusModal.step === 'status' ? 'Cancel' : 'Back'}
            </Button>

            {bulkPaymentStatusModal.step === 'payment_option' && (
              <Button
                variant="contained"
                disabled={!bulkPaymentStatusModal.paymentOption || bulkPaymentStatusModal.isUpdating}
                onClick={() =>
                  setBulkPaymentStatusModal((prev) => ({
                    ...prev,
                    step: 'payment_details',
                    paymentError: '',
                    ...(prev.paymentOption === 'full'
                      ? {
                          advanceAmount: String(
                            prev.summaryOrder?.total_amount ?? prev.summaryOrder?.total ?? ''
                          ),
                        }
                      : {}),
                  }))
                }
              >
                Confirm
              </Button>
            )}

            {bulkPaymentStatusModal.step === 'payment_details' && (
              <Button
                variant="contained"
                disabled={
                  bulkPaymentStatusModal.isUpdating ||
                  !canSubmitPaidPayment(getBulkPaidPaymentModalState()) ||
                  getUnpaidOrdersFromList(getSelectedOrdersForBulkPayment()).length === 0
                }
                onClick={handleConfirmBulkPaidPaymentDetails}
                startIcon={
                  bulkPaymentStatusModal.isUpdating ? <CircularProgress size={16} /> : <UpdateIcon />
                }
              >
                {bulkPaymentStatusModal.isUpdating ? 'Updating...' : 'Submit Payment'}
              </Button>
            )}

            {bulkPaymentStatusModal.paymentOption === 'remaining' && (
              <Button
                variant="contained"
                disabled={
                  bulkPaymentStatusModal.isUpdating ||
                  !canSubmitPaidPayment(getBulkPaidPaymentModalState()) ||
                  getUnpaidOrdersFromList(getSelectedOrdersForBulkPayment()).length === 0
                }
                onClick={handleConfirmBulkPaidPaymentDetails}
                startIcon={
                  bulkPaymentStatusModal.isUpdating ? <CircularProgress size={16} /> : <UpdateIcon />
                }
              >
                {bulkPaymentStatusModal.isUpdating ? 'Updating...' : 'Submit Payment'}
              </Button>
            )}

            {bulkPaymentStatusModal.paymentOption === 'credit' &&
              shouldShowCreditSubmitButton({
                ...bulkPaymentStatusModal,
                order: bulkPaymentStatusModal.summaryOrder,
              }) && (
              <Button
                variant="contained"
                disabled={
                  bulkPaymentStatusModal.isUpdating ||
                  !canSubmitCreditPayment({
                    ...bulkPaymentStatusModal,
                    order: bulkPaymentStatusModal.summaryOrder,
                  }) ||
                  getUnpaidOrdersFromList(getSelectedOrdersForBulkPayment()).length === 0
                }
                onClick={handleConfirmBulkCreditPaymentDetails}
                startIcon={
                  bulkPaymentStatusModal.isUpdating ? <CircularProgress size={16} /> : <UpdateIcon />
                }
              >
                {bulkPaymentStatusModal.isUpdating ? 'Updating...' : 'Submit Payment'}
              </Button>
            )}

            {bulkPaymentStatusModal.step === 'status' &&
              bulkPaymentStatusModal.selectedPaymentStatus !== 'paid' &&
              bulkPaymentStatusModal.selectedPaymentStatus !== 'credit' && (
                <Button
                  onClick={handleBulkPaymentStatusUpdateFromModal}
                  variant="contained"
                  disabled={
                    !bulkPaymentStatusModal.selectedPaymentStatus ||
                    bulkPaymentStatusModal.isUpdating ||
                    isPaymentUpdateDisabled({
                      ...bulkPaymentStatusModal.summaryOrder,
                      payment_status: {
                        name: bulkPaymentStatusModal.initialPaymentStatus || '',
                      },
                    }) ||
                    (() => {
                      const ps = bulkPaymentStatusModal.selectedPaymentStatus;
                      if (
                        ['failed', 'pending', 'outstanding'].includes(ps) &&
                        !String(bulkPaymentStatusModal.creditNote || '').trim()
                      ) {
                        return true;
                      }
                      if (ps !== 'refunded') return false;
                      if (!String(bulkPaymentStatusModal.creditNote || '').trim()) return true;
                      const refundNum = Number(bulkPaymentStatusModal.refundAmount);
                      const apiDetails = bulkPaymentStatusModal.orderPaymentDetails;
                      const advancePaidTotal = getAdvancePaymentEntries(
                        bulkPaymentStatusModal.summaryOrder || bulkPaymentStatusModal
                      ).reduce((sum, entry) => sum + Number(entry.advance_amount || 0), 0);
                      const paidFallback =
                        advancePaidTotal > 0
                          ? advancePaidTotal
                          : (bulkPaymentStatusModal.summaryOrder?.amount_paid ??
                              bulkPaymentStatusModal.summaryOrder?.paid ??
                              0);
                      const maxRefund = Number(apiDetails?.paidAmount ?? paidFallback);
                      return (
                        !Number.isFinite(refundNum) ||
                        refundNum <= 0 ||
                        refundNum > maxRefund
                      );
                    })()
                  }
                  startIcon={
                    bulkPaymentStatusModal.isUpdating ? <CircularProgress size={16} /> : <UpdateIcon />
                  }
                >
                  {bulkPaymentStatusModal.isUpdating ? 'Updating...' : 'Update Payment'}
                </Button>
              )}
          </DialogActions>
        </Dialog>
      </Box>
    )
  } 

  export {
    AppOrderDetailsView,
    PaymentDetailsStepContent,
    PAYMENT_DETAILS_DIALOG_CONTENT_SX,
    PAYMENT_DETAILS_DIALOG_ACTIONS_SX,
    PAYMENT_DETAILS_DIALOG_PAPER_PROPS,
    consumeSavedAppOrderStatusTab,
    consumeSavedAppOrderListState,
    rememberLastOpenedAppOrder,
  };

  export default ProductList; 
